import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, NgZone, OnDestroy, QueryList, ViewChild, ViewChildren, inject } from "@angular/core";
import { DelegationsService, POWER } from "src/app/service/delegations.service";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  Validators,
} from "@angular/forms";
import { NgClass, NgStyle } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexTooltip,
  ApexStroke,
} from "ng-apexcharts";
import { NgApexchartsModule } from "ng-apexcharts";
import {
  ApexNonAxisChartSeries,
  ApexResponsive,
  ApexPlotOptions,
  ApexFill,
} from "ng-apexcharts";
import { DatePipe } from "@angular/common";
import { MatSelectModule } from "@angular/material/select";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { UserService } from "src/app/service/user.service";
import { CrmService } from "src/app/service/crm.service";
import { NgxDaterangepickerMd } from "ngx-daterangepicker-material";
import moment from "moment";
import { ChatService } from "src/app/service/chat.service";
import { ReactiveFormsModule } from '@angular/forms';
import { environment } from "src/environments/environment";
import { WhatsappMessagesCardComponent } from "src/app/component/whatsapp-messages-card/whatsapp-messages-card.component";
import { ActivatedRoute } from '@angular/router';
import { alertModalDelete, alertModalWithoutConfirm, alertModalConfirm, alertModalNextTaskReminder } from "../../helpers/alert";
import * as d3 from 'd3';
import { HierarchyRectangularNode } from 'd3-hierarchy';
import { ThemeService } from "../../../service/theme.service";
import { Subject, Subscription, forkJoin } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { DISPOSITION_EVENT } from 'src/app/constants/disposition.constants';


interface TerritoryNode {
  name: string;
  assignedLeads: number;
  convertedLeads: number;
}

export interface Disposition {
  _id: string;
  leadId: string;
  note?: string;
  userName?: string;
  profileImg?: string;
  updatedAt?: string;
  status?: string;
  prevStatus?: string;
  type?: 'User' | 'System';
  event?: string;
  hasClosingPotential?: any;
  createdAt?: string;
}


export type RadialChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  labels: string[];
  plotOptions: ApexPlotOptions;
  fill: ApexFill;
  stroke: ApexStroke;
};

export type DonutChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels?: any;
  colors?: any;
  stroke?: ApexStroke;
  plotOptions?: ApexPlotOptions;
  dataLabels?: ApexDataLabels;
  legend?: ApexLegend;
  fill?: ApexFill;
};

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis?: ApexYAxis;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  dataLabels: ApexDataLabels;
  colors?: string[];
  fill?: any;
  legend?: any;
  grid?: any;
  mode?: any;
};

type FileBlock = {
  id: string;
  type: 'text' | 'file';
  value?: string;
  fileName: string;
  fileSize: number;
  rawFile: File;
  uploading: boolean;
  progress: number;
  uploadId?: string;
  mediaId?: string;
  url?: string;
  uploadedAt?: Date | string;
  extension?: string;
  thumbnailUrl?: string;
  parts: { PartNumber: number; ETag: string }[];
  leadId?: string;
};
type TextBlock = {
  id: string;
  type: 'text';
  value: string;
};

type EditorBlock = TextBlock | FileBlock;

@Component({
  selector: "app-admin-dashboard",
  imports: [
    MatFormFieldModule,
    MatDatepickerModule,
    MatInputModule,
    MatButtonModule,
    FormsModule,
    DatePipe,
    NgClass,
    ReactiveFormsModule,
    InfiniteScrollModule,
    NgApexchartsModule,
    MatSelectModule,
    NgStyle,
    NgxDaterangepickerMd,
    MatAutocompleteModule,
    MatTooltipModule,
    WhatsappMessagesCardComponent,
  ],
  templateUrl: "./admin-dashboard.component.html",
  styleUrls: ["../styles/leads.css", "./admin-dashboard.component.css"]
})
export class AdminDashboardComponent implements OnDestroy {
  // Delegated power (14 Oct 2026): someone outside Sales/Admin may be allowed to assign leads.
  private readonly delegations = inject(DelegationsService);
  /** On the All-leads page the page's project is "ALL"; a lead that carries no project of its own is then sent without one and the server finds it by id (22 Oct 2026). */
  isRealProject(project: string | undefined): boolean {
    return ['IIC-LAB', 'V-RETAIL', 'V-ESTATE'].includes(project || '');
  }
  /** The server's own words when it refuses, instead of a generic line. */
  private serverMessage(err: any, fallback: string): string {
    const m = err?.error?.message;
    const text = Array.isArray(m) ? m.map((x: any) => x?.message || x).join('; ') : typeof m === 'object' ? m?.message : m;
    return typeof text === 'string' && text.trim() ? text : fallback;
  }
  get canAssignLeads(): boolean {
    return this.delegations.has(POWER.LEADS_ASSIGN);
  }
  get canDeleteLeads(): boolean {
    const dept = (this.userDepartment || localStorage.getItem("department") || "").toUpperCase();
    return dept === "ADMIN" || dept === "MARKETING" || this.userRole === "ADMIN";
  }
  get canViewDeletedLeads(): boolean {
    const dept = (this.userDepartment || localStorage.getItem("department") || "").toUpperCase();
    return dept === "ADMIN" || dept === "MARKETING" || this.userRole === "ADMIN";
  }
  private themeSub!: Subscription;
  private readonly destroy$ = new Subject<void>();
  private territoryRenderFrame?: number;
  private resizeRenderFrame?: number;


  @Input() project: string = "";
  // @ViewChild("chart", { static: true }) chart: ChartComponent;
  @ViewChild("donutchart", { static: false }) donutchart: ChartComponent;
  @ViewChild("radialchart", { static: false }) radialchart: ChartComponent;
  @ViewChild("saleschart") saleschart: ChartComponent;
  @ViewChild('chart', { static: false }) chart!: ElementRef;
  @ViewChildren('editorTextarea') editorTextareas!: QueryList<ElementRef<HTMLTextAreaElement>>;

  public radialchartOptions: Partial<RadialChartOptions>;
  public chartOptions: Partial<ChartOptions>;
  public donutchartOptions: Partial<DonutChartOptions>;
  radialChartData: any[] = [];
  leadForm: FormGroup;
  projectTitleMap: Record<string, string> = {
    'ALL': 'All Projects',
    'IIC-LAB': 'IIC-LAB',
    'V-RETAIL': 'V-Retail',
    'V-ESTATE': 'V-Estate',
    'OUTBOUND': 'Outbound'
  };

  get bannerHeight(): string {
    return this.userDepartment === 'MARKETING' ? '78vh' : '70vh';
  }

  get modalTitle(): string {
    return this.projectTitleMap[this.project] || this.project;
  }

  get overviewSubhead(): string {
    switch (this.project) {
      case 'ALL':
        return 'ALL';
      case 'IIC-LAB':
        return 'INK IN CAPS';
      case 'V-RETAIL':
        return 'V-RETAIL';
      case 'V-ESTATE':
        return 'V-ESTATE';
      case 'OUTBOUND':
        return 'OUTBOUND';
      default:
        return 'INK IN CAPS';
    }
  }
  audioSpeeds: number[] = [1, 1.5, 2, 3];
  selectedAudioSpeed: number = 1;
  isPlayingAudio: boolean = false;
  currentAudioTime: number = 0;
  audioDuration: number = 0;
  isAudioMuted: boolean = false;
  isDraggingAudio: boolean = false;

  toggleAudioSpeed(audioEl?: HTMLAudioElement): void {
    const currentIndex = this.audioSpeeds.indexOf(this.selectedAudioSpeed);
    const nextIndex = (currentIndex + 1) % this.audioSpeeds.length;
    this.selectedAudioSpeed = this.audioSpeeds[nextIndex];

    const player = audioEl || (document.getElementById('ivrRecording') as HTMLAudioElement);
    if (player) {
      player.playbackRate = this.selectedAudioSpeed;
    }
  }

  togglePlayPause(audioEl: HTMLAudioElement): void {
    if (!audioEl) return;
    if (audioEl.paused) {
      const playPromise = audioEl.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          this.isPlayingAudio = true;
        }).catch((err: any) => {
          console.error('Audio playback failed:', err);
          this.isPlayingAudio = false;
        });
      } else {
        this.isPlayingAudio = true;
      }
    } else {
      audioEl.pause();
      this.isPlayingAudio = false;
    }
  }

  onAudioTimeUpdate(audioEl: HTMLAudioElement): void {
    if (audioEl && !this.isDraggingAudio) {
      this.currentAudioTime = audioEl.currentTime;
      if (audioEl.duration && isFinite(audioEl.duration) && !isNaN(audioEl.duration) && audioEl.duration > 0) {
        this.audioDuration = audioEl.duration;
      }
    }
  }

  onAudioSeekInput(event: Event): void {
    this.isDraggingAudio = true;
    const input = event.target as HTMLInputElement;
    this.currentAudioTime = Number(input.value);
  }

  onAudioSeekChange(event: Event, audioEl: HTMLAudioElement): void {
    const input = event.target as HTMLInputElement;
    const seekTime = Number(input.value);

    if (!audioEl || !Number.isFinite(seekTime)) {
      this.isDraggingAudio = false;
      return;
    }

    this.currentAudioTime = seekTime;

    try {
      if (audioEl.readyState > 0) {
        audioEl.currentTime = seekTime;
      }
    } catch (err) {
      console.error('Seeking failed:', err);
    }

    setTimeout(() => {
      this.isDraggingAudio = false;
    }, 150);
  }

  onAudioSeek(event: any, audioEl: HTMLAudioElement): void {
    const seekTime = parseFloat(event.target.value);
    if (audioEl) {
      audioEl.currentTime = seekTime;
      this.currentAudioTime = seekTime;
    }
  }

  toggleMute(audioEl: HTMLAudioElement): void {
    if (audioEl) {
      audioEl.muted = !audioEl.muted;
      this.isAudioMuted = audioEl.muted;
    }
  }

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  }

  setAudioSpeed(speed: number, audioEl?: HTMLAudioElement): void {
    this.selectedAudioSpeed = speed;
    const player = audioEl || (document.getElementById('ivrRecording') as HTMLAudioElement);
    if (player) {
      player.playbackRate = speed;
    }
  }

  onAudioPlay(event: Event): void {
    this.isPlayingAudio = true;
    const player = event.target as HTMLAudioElement;
    if (player) {
      player.playbackRate = this.selectedAudioSpeed;
    }
  }

  onAudioLoaded(event: Event): void {
    const player = event.target as HTMLAudioElement;
    if (player && player.duration && isFinite(player.duration) && !isNaN(player.duration) && player.duration > 0) {
      this.audioDuration = player.duration;
    }
  }

  showCopyTooltip = false;
  copyLeadDetails() {
    const lead = this.singleLead || this.selectedLeadForDetails || this.selectedLead;
    if (!lead) return;

    const badgeLabel =
      this.badgeOptions?.find((b: any) => b.value === (lead.customerValue || lead.badge))?.label || '-';

    const datePipe = new DatePipe('en-US');
    const formattedDate = datePipe.transform(lead.createdAt, 'dd-MM-yyyy') || '-';
    const projectName = this.projectTitleMap[this.project] || this.project || 'Lead Details';
    let details = `${projectName}
-------------------------------------------

Lead Information
Name: ${lead.name || '-'}
Email: ${this.getLeadEmail(lead)}
Phone: ${lead.phoneNo || '-'}
Date: ${formattedDate}
Badge: ${badgeLabel}
Source: ${lead.source || '-'}
`;

    if ((this.project === 'IIC-LAB' && lead.source === 'Contact Us') || (this.project === 'V-ESTATE' && (lead.source === 'Contact Us' || lead.source === 'Book Demo' || lead.source === 'whatsapp' || lead.source === 'Whatsapp' || lead.source === 'WhatsApp'))) {
      details += `UTM Source: ${lead.utmSource || '-'}\n`;
      details += `UTM Medium: ${lead.utmMedium || '-'}\n`;
      details += `UTM Content: ${lead.utmContent || '-'}\n`;
    }

    const assignedUser = lead.assignedTo?.userName || lead.followUpAssignedTo?.userName || lead.assignedToString || '-';
    const notesVal = lead.adminNotes || lead.description || this.statusnote || '';

    details += `
Assign To
Name: ${assignedUser}

Message: ${this.getLeadMessage(lead)}

Your Note:
${notesVal}`;

    const onSuccess = () => {
      this.showCopyTooltip = true;
      this.changeDetector.markForCheck();
      setTimeout(() => {
        this.showCopyTooltip = false;
        this.changeDetector.markForCheck();
      }, 2000);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(details)
        .then(onSuccess)
        .catch(err => {
          console.warn('navigator.clipboard.writeText failed, using fallback:', err);
          this.fallbackCopyTextToClipboard(details, onSuccess);
        });
    } else {
      this.fallbackCopyTextToClipboard(details, onSuccess);
    }
  }

  fallbackCopyTextToClipboard(text: string, cb?: () => void) {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (cb) cb();
    } catch (err) {
      console.error('Fallback copy failed:', err);
      if (cb) cb();
    }
    document.body.removeChild(textArea);
  }

  @ViewChild('waCard') waCard?: WhatsappMessagesCardComponent;

  viewWhatsAppDetail(lead: any): void {
    if (!lead?.phoneNo) return;
    if (this.waCard) {
      this.waCard.openDetailModal();
    }
  }

  hasIvrDetails(lead: any): boolean {
    return !!(
      this.selectedType === 'IVR' ||
      lead?.source === 'IVR' ||
      lead?.callerNo ||
      lead?.calledNo ||
      lead?.callerId ||
      (this.project === 'IIC-LAB' && this.selectedSection === 'IVR')
    );
  }

  private cleanupModalBackdrop(): void {
    document.querySelectorAll('.modal-backdrop, .modal-backdrop.show, .upload-modal-backdrop').forEach(el => el.remove());
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
  }

  isIvrLead(lead: any): boolean {
    if (!lead) return false;
    const src = String(lead.source || lead.projectTab || lead.sourceLead || '').toLowerCase();
    return src === 'ivr';
  }

  private getLeadMessage(lead: any): string {
    if (!lead) return 'No message';
    const isIvr = this.isIvrLead(lead);
    const rawMsg = lead.message || lead.note || lead.notes;
    if (isIvr && !lead.extractedRecordingUrl && (!rawMsg || rawMsg === 'No message')) {
      return 'No recording available for this call';
    }
    return rawMsg || 'No message';
  }

  getMessageWithDemoDate(lead: any): string {
    if (!lead) return 'No message';

    const message = this.getLeadMessage(lead);

    if ((lead.project === 'V-ESTATE' || lead.project === 'V-RETAIL') &&
      (lead.source === 'Book Demo' || lead.source === 'Book A Demo') &&
      lead.demoDate) {
      const datePipe = new DatePipe('en-US');
      const demoDate = new Date(lead.demoDate);
      const formattedDemoDate = datePipe.transform(demoDate, 'dd-MM-yyyy') || lead.demoDate;
      const formattedDemoTime = datePipe.transform(demoDate, 'hh:mm a') || '';
      return formattedDemoTime
        ? `${message}\nDemo Date: ${formattedDemoDate}\nDemo Time: ${formattedDemoTime}`
        : `${message}\nDemo Date: ${formattedDemoDate}`;
    }

    return message;
  }

  isEmailLead(lead: any): boolean {
    if (!lead) return false;
    const source = String(lead.source || lead.projectTab || lead.sourceLead || '').toLowerCase();
    return source === 'email';
  }

  getLeadDisplayMessage(lead: any): string {
    if (!lead) return '-';
    const msg = this.getMessageWithDemoDate(lead);
    if (this.isEmailLead(lead) && lead?.subject) {
      return `Subject: ${lead.subject}\n\n${msg}`;
    }
    return msg;
  }

  capitalizeName(name: string): string {
    if (!name) return '';
    return name.replace(/\b\w/g, char => char.toUpperCase());
  }

  getAgentName(lead: any): string {
    if (!lead) return '';
    let name = '';
    if (lead.agentName) name = lead.agentName;
    else if (lead.agentFirstName) {
      name = lead.agentFirstName + (lead.agentLastName ? ' ' + lead.agentLastName : '');
    }
    return this.capitalizeName(name);
  }

  formatDuration(value: number | string | undefined): string {
    if (value == null || value === '') return '0:00';
    if (typeof value === 'string') {
      if (value.includes(':')) return value;
      const parsed = Number(value);
      if (!isFinite(parsed)) return '0:00';
      value = parsed;
    }
    const seconds = typeof value === 'number' ? value : Number(value);
    if (!isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  }


  constructor(
    private userService: UserService,
    private crmService: CrmService,
    private route: ActivatedRoute,
    private chatService: ChatService,
    private router: Router,
    private fb: FormBuilder,
    private themeService: ThemeService,
    private changeDetector: ChangeDetectorRef,
    private zone: NgZone,
    private http: HttpClient
  ) {
    void this.delegations.loadMe().catch(() => {});
    const year = new Date().getFullYear();
    this.donutchartOptions = {
      ...this.donutchartOptions,

      chart: {
        type: "donut",
        height: 155,
        sparkline: {
          enabled: true
        },
        animations: {
          enabled: true,
          speed: 1200,
          animateGradually: {
            enabled: true,
            delay: 300,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 500,
          },
        },
      },

      colors: ["#3b82f6", "#eab308", "#22c55e", "#ef4444"],

      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ["#ffffff"] },

      plotOptions: {
        pie: {
          expandOnClick: false,
          donut: {
            size: "72%",
            labels: {
              show: false
            }
          },
        },
      },
    };

    this.radialchartOptions = {
      series: [75],

      chart: {
        height: 350,
        type: "radialBar",
        toolbar: { show: true },
        animations: {
          enabled: true,
          speed: 1200,
          animateGradually: {
            enabled: true,
            delay: 300,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },

      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 225,

          hollow: {
            margin: 0,
            size: "70%",
            background: "#fff",
            dropShadow: {
              enabled: true,
              top: 3,
              left: 0,
              blur: 4,
              opacity: 0.24,
            },
          },

          track: {
            background: "#fff",
            strokeWidth: "67%",
            margin: 0,
            dropShadow: {
              enabled: true,
              top: -3,
              left: 0,
              blur: 4,
              opacity: 0.35,
            },
          },

          dataLabels: {
            show: true,
            name: {
              offsetY: -10,
              show: true,
              color: "#888",
              fontSize: "17px",
            },
            value: {
              formatter: function (val) {
                return parseInt(val.toString(), 10).toString();
              },
              color: "#111",
              fontSize: "36px",
              show: true,
            },
          },
        },
      },

      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "horizontal",
          shadeIntensity: 0.5,
          gradientToColors: ["#ABE5A1"],
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },

      stroke: { lineCap: "round" },
      labels: ["Percent"],
    };
    this.initializeForm();
  }

  initializeForm() {
    this.leadForm = this.fb.group({
      leadId: ['', Validators.required],
      email: [''],
      companyName: [''],
      leadName: [''],
      name: [''],
      contactName: [''],
      phoneNo: [''],
      designation: [''],
      followUpDate: [''],
      followUpAssignee: [''],
      status: [''],
      userMessage: [''],
      adminNotes: [''],
      customerValue: [''],
      lossReason: ['']
    });

    this.leadForm.get('leadName')?.valueChanges.subscribe(val => {
      if (this.leadForm.get('contactName')?.value !== val) {
        this.leadForm.get('contactName')?.setValue(val, { emitEvent: false });
      }
      if (this.leadForm.get('name')?.value !== val) {
        this.leadForm.get('name')?.setValue(val, { emitEvent: false });
      }
    });

    this.leadForm.get('contactName')?.valueChanges.subscribe(val => {
      if (this.leadForm.get('leadName')?.value !== val) {
        this.leadForm.get('leadName')?.setValue(val, { emitEvent: false });
      }
      if (this.leadForm.get('name')?.value !== val) {
        this.leadForm.get('name')?.setValue(val, { emitEvent: false });
      }
    });

    this.leadForm.get('name')?.valueChanges.subscribe(val => {
      if (this.leadForm.get('leadName')?.value !== val) {
        this.leadForm.get('leadName')?.setValue(val, { emitEvent: false });
      }
      if (this.leadForm.get('contactName')?.value !== val) {
        this.leadForm.get('contactName')?.setValue(val, { emitEvent: false });
      }
    });
  }

  userDepartment: any;
  loggedInUser: any;
  userRole: string = "";
  followUpForm!: FormGroup;
  userId: any;
  loggedInUserName: any;

  formatAssignedTo(assignedTo: any, isHtml: boolean = false): string {
    if (!assignedTo) return "";

    const loggedInId = this.userId || localStorage.getItem("guestID") || "";
    const loggedInName = this.loggedInUserName || localStorage.getItem("username") || localStorage.getItem("userName") || "";

    const formatItem = (item: any) => {
      if (!item) return "";
      if (typeof item === "string") return item;
      const userName = item.userName || item.assigneeName || item.username || "";
      if (!userName) return "";

      const assignedByName = item.assignedByName;
      const assignedById = item.assignedById;

      let byText = "";
      if (assignedById && loggedInId && String(assignedById) === String(loggedInId)) {
        byText = "by Me";
      } else if (assignedByName && loggedInName && String(assignedByName).trim().toLowerCase() === String(loggedInName).trim().toLowerCase()) {
        byText = "by Me";
      } else if (assignedByName) {
        byText = `by ${assignedByName}`;
      }

      if (!byText) return userName;

      return isHtml
        ? `${userName}<span class="assigned-by d-block" style="font-size: 13px; color: grey; font-weight: 400; line-height: 1.2;">(${byText})</span>`
        : `${userName}\n(${byText})`;
    };

    if (Array.isArray(assignedTo)) {
      return assignedTo.map((a) => formatItem(a)).filter(Boolean).join(", ");
    }

    if (typeof assignedTo === "object") {
      return formatItem(assignedTo);
    }

    return String(assignedTo);
  }

  getAssignedToDisplay(lead: any, isHtml: boolean = false): string {
    if (!lead) return "-";
    if (lead.assignedTo) {
      const formatted = this.formatAssignedTo(lead.assignedTo, isHtml);
      if (formatted) return formatted;
    }
    if (lead.assignedToString) {
      return lead.assignedToString;
    }
    return "-";
  }

  getAssignedByName(lead: any): string {
    if (!lead) return "-";
    const assignedObj = lead?.assignedTo || lead?.followUpAssignedTo;
    let name = "";
    let id = "";
    if (assignedObj) {
      if (Array.isArray(assignedObj)) {
        name = assignedObj[0]?.assignedByName || "";
        id = assignedObj[0]?.assignedById || "";
      } else {
        name = assignedObj.assignedByName || "";
        id = assignedObj.assignedById || "";
      }
    } else {
      name = lead?.assignedByName || "";
      id = lead?.assignedById || "";
    }

    if (!name && !id) return "-";

    const loggedInId = this.userId || localStorage.getItem("guestID") || "";
    const loggedInName = this.loggedInUserName || localStorage.getItem("username") || localStorage.getItem("userName") || "";

    if (id && loggedInId && String(id) === String(loggedInId)) {
      return "Me";
    }
    if (name && loggedInName && String(name).trim().toLowerCase() === String(loggedInName).trim().toLowerCase()) {
      return "Me";
    }

    return name || "-";
  }

  getAssignedByImg(lead: any): string {
    if (!lead) return '/assets/leads/user-icon.png';
    const assignedObj = lead?.assignedTo || lead?.followUpAssignedTo;
    let img = '';
    if (assignedObj) {
      if (Array.isArray(assignedObj)) {
        img = assignedObj[0]?.userProfileImg || assignedObj[0]?.assignedByImg || assignedObj[0]?.userImg || '';
      } else {
        img = assignedObj.userProfileImg || assignedObj.assignedByImg || assignedObj.userImg || '';
      }
    }
    if (!img) {
      img = lead?.userProfileImg || lead?.assignedByImg || lead?.userImg || '';
    }
    return img && typeof img === 'string' && img.trim() ? img.trim() : '/assets/leads/user-icon.png';
  }

  onImgError(event: Event, fallbackSrc: string = '/assets/leads/user-icon.png') {
    const target = event.target as HTMLImageElement;
    if (target && target.src !== fallbackSrc) {
      target.src = fallbackSrc;
    }
  }

  isStatusDropdownDisabled(lead?: any): boolean {
    return false;
  }

  ngOnInit() {
    // All Leads spans every project, so load a smaller page and let infinite
    // scroll fetch subsequent pages as needed.
    this.currentLimit = this.project === 'ALL' ? 20 : 100;
    this.userId = localStorage.getItem("guestID");
    this.loggedInUserName = localStorage.getItem("username") || localStorage.getItem("userName") || "";
    this.userDepartment = localStorage.getItem("department") || "";

    if (this.userDepartment === "ADMIN") {
      this.userRole = "ADMIN";
    } else if (this.userDepartment === "SALES" || this.userDepartment === "ASSISTANT-SALES") {
      this.userRole = "SALES";
    }

    // These requests are independent. Start them together, but let each widget
    // update only when its own response is ready.
    this.getAllLeadsBreakdown();
    this.fetchFollowUpReminders();
    this.fetchAllReminderCounts();

    this.themeSub = this.themeService.isDarkMode$.subscribe(() => {
      if (this.chart?.nativeElement) {
        if (this.territoryData && this.territoryData.length) {
          this.scheduleTerritoryRender();
        } else {
          this.scheduleTerritoryRender();
        }
        this.changeDetector.markForCheck();
      }
    });
    this.followUpForm = this.fb.group({
      date: [''],
      time: [''],
      type: ['']
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['view'] === 'kanban') {
        this.openKanbanFromRoute();
        if (params['column'] === 'closing-pool') {
          setTimeout(() => {
            const el = document.getElementById('drop-list-6');
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
          }, 600);
        }
      } else {
        if (this.showCanvasUI) {
          this.showCanvasUI = false;
          this.isCanvasLoading = false;
          this.selectedLeadForDetails = null;
          this.showDetailsPanel = false;
          this.startDate = null;
          this.endDate = null;
          this.selectedDateRange = null;
          this.getAdminOverView();
          this.getAllLeadsBreakdown();
          this.getLeadsListingDetail();
        }
      }
    });
    this.listenOverviewUpdates();
    this.getSalesUserList();
    this.getAdminOverView();
    this.getLeadsListingDetail();
  }

  ngOnDestroy() {
    this.cleanupModalBackdrop();
    if (this.themeSub) {
      this.themeSub.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
    if (this.territoryRenderFrame) cancelAnimationFrame(this.territoryRenderFrame);
    if (this.resizeRenderFrame) cancelAnimationFrame(this.resizeRenderFrame);
  }


  territoryData: TerritoryNode[] = [];
  isTerritoryLoading = true;
  getAllLeadsBreakdown() {
    if (this.userDepartment === 'MARKETING') {
      this.isTerritoryLoading = false;
      return;
    }
    const payload: any = {
      project: this.project
    };

    if (this.startDate && this.endDate) {
      payload.startDate = this.startDate;
      payload.endDate = this.endDate;
    }

    this.isTerritoryLoading = true;
    this.crmService.leadsBreakdown(payload).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isTerritoryLoading = false;
        this.changeDetector.markForCheck();
      })
    ).subscribe(
      (res: any) => {
        const users = res?.data?.userData || [];


        this.territoryData = users.map((u: any) => ({
          name: u.userName,
          assignedLeads: Number(u.assignedLeads) || 0,
          convertedLeads: Number(u.convertedLeads) || 0
        }));

        this.scheduleTerritoryRender();
      },
      err => console.error(err)
    );
  }



  @HostListener('window:resize')
  onWindowResize() {
    // Resize fires continuously while the user resizes the window. Render once
    // in the next frame instead of rebuilding the SVG for every event.
    if (this.resizeRenderFrame) cancelAnimationFrame(this.resizeRenderFrame);
    this.resizeRenderFrame = requestAnimationFrame(() => this.scheduleTerritoryRender());
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target && !target.closest('.dropdown.customervalue-dropdown') && !target.closest('.dropdown.daterange-dropdown')) {
      this.isCustomerValueDropdownOpen = false;
      this.isDateDropdownOpen = false;
    }
    if (!target || !target.closest('.follow-up-assignee-dd')) {
      this.followUpTaskAssigneeDropdownOpen = false;
    }
    if (!target || (!target.closest('.badge-dd') && !target.closest('.badge-dropdown') && !target.closest('.badge-dd-container'))) {
      this.badgeDropdownOpen = false;
    }
    if (!target || !target.closest('.reminder-user-filter-dd')) {
      this.reminderUserDropdownOpen = false;
    }
  }

  onDetailsPanelClick(event: MouseEvent) {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (!target) return;
    if (!target.closest('.follow-up-assignee-dd')) {
      this.followUpTaskAssigneeDropdownOpen = false;
    }
    if (!target.closest('.badge-dd') && !target.closest('.badge-dropdown') && !target.closest('.badge-dd-container')) {
      this.badgeDropdownOpen = false;
    }
  }

  private scheduleTerritoryRender(): void {
    if (this.territoryRenderFrame) cancelAnimationFrame(this.territoryRenderFrame);

    this.zone.runOutsideAngular(() => {
      this.territoryRenderFrame = requestAnimationFrame(() => {
        if (!this.chart?.nativeElement) return;
        if (this.territoryData.length) {
          this.drawTerritoryMap();
        } else {
          this.drawEmptyTerritoryState();
        }
      });
    });
  }

  calculateNormalizedWidths(values: number[], availableWidth: number, minW: number, maxW: number): number[] {
    const n = values.length;
    if (n === 0) return [];
    if (n === 1) return [availableWidth];

    const totalVal = values.reduce((sum, v) => sum + v, 0) || 1;
    let rawWidths = values.map(v => (v / totalVal) * availableWidth);

    let clamped = rawWidths.map(w => Math.max(minW, Math.min(maxW, w)));
    let clampedTotal = clamped.reduce((sum, w) => sum + w, 0);

    if (clampedTotal > 0) {
      let scaled = clamped.map(w => Math.floor(availableWidth * (w / clampedTotal)));
      let currentSum = scaled.reduce((sum, w) => sum + w, 0);
      let diff = availableWidth - currentSum;

      for (let i = 0; i < diff; i++) {
        scaled[i % n] += 1;
      }
      return scaled;
    }

    const eq = Math.floor(availableWidth / n);
    let result = new Array(n).fill(eq);
    result[n - 1] += availableWidth - (eq * n);
    return result;
  }

  private userColorMap = new Map<string, { start: string; end: string }>();

  private curatedGradients = [
    { start: '#C58BF2', end: '#A068ED' }, // 0: Suman-style Purple
    { start: '#F988C7', end: '#EE5CA6' }, // 1: Apurva-style Pink
    { start: '#FFAF9B', end: '#F87E7B' }, // 2: Vinisha-style Coral
    { start: '#C7ED73', end: '#99D546' }, // 3: Lime Green
    { start: '#A5C5FE', end: '#779FF6' }, // 4: Periwinkle Blue
    { start: '#4EECD5', end: '#14B8A6' }, // 5: Mint Teal
    { start: '#FDE047', end: '#F59E0B' }, // 6: Lighter Sunny Yellow
    { start: '#818CF8', end: '#4F46E5' }, // 7: Indigo Blue
    { start: '#FB7185', end: '#E11D48' }, // 8: Rose Crimson
    { start: '#34D399', end: '#059669' }, // 9: Emerald Green
    { start: '#38BDF8', end: '#0284C7' }, // 10: Cyan Sky
    { start: '#FB923C', end: '#EA580C' }, // 11: Vibrant Orange
    { start: '#E879F9', end: '#C084FC' }, // 12: Orchid Violet
    { start: '#A7F3D0', end: '#10B981' }, // 13: Light Spring Green
    { start: '#F472B6', end: '#BE185D' }, // 14: Deep Magenta
    { start: '#60A5FA', end: '#2563EB' }, // 15: Royal Azure
    { start: '#FCA5A5', end: '#DC2626' }, // 16: Coral Red
    { start: '#FDE047', end: '#CA8A04' }, // 17: Golden Sunshine
    { start: '#C084FC', end: '#7E22CE' }, // 18: Deep Purple
    { start: '#2DD4BF', end: '#0D9488' }  // 19: Ocean Teal
  ];

  private slugify(str: string): string {
    return (str || '').toLowerCase().replace(/[^a-z0-9]/g, '-');
  }

  private getProceduralGradient(index: number): { start: string; end: string } {
    if (index < this.curatedGradients.length) {
      return this.curatedGradients[index];
    }
    const goldenAngle = 137.508;
    const hue = Math.round((index * goldenAngle) % 360);
    const endHue = (hue + 15) % 360;
    return {
      start: `hsl(${hue}, 82%, 68%)`,
      end: `hsl(${endHue}, 85%, 54%)`
    };
  }

  private getUserColor(userKey: string, allUserKeys: string[]): { start: string; end: string } {
    const normalizedKey = (userKey || '').toLowerCase().trim();
    if (!normalizedKey) {
      return this.curatedGradients[0];
    }

    if (this.userColorMap.has(normalizedKey)) {
      return this.userColorMap.get(normalizedKey)!;
    }

    const sortedKeys = Array.from(new Set((allUserKeys || []).map(k => (k || '').toLowerCase().trim()).filter(Boolean))).sort();

    sortedKeys.forEach((key) => {
      if (!this.userColorMap.has(key)) {
        let assignedIdx = 0;
        const usedStarts = new Set(Array.from(this.userColorMap.values()).map(g => g.start));
        while (usedStarts.has(this.getProceduralGradient(assignedIdx).start)) {
          assignedIdx++;
        }
        this.userColorMap.set(key, this.getProceduralGradient(assignedIdx));
      }
    });

    if (this.userColorMap.has(normalizedKey)) {
      return this.userColorMap.get(normalizedKey)!;
    }

    let fallbackIdx = 0;
    const usedStarts = new Set(Array.from(this.userColorMap.values()).map(g => g.start));
    while (usedStarts.has(this.getProceduralGradient(fallbackIdx).start)) {
      fallbackIdx++;
    }
    const color = this.getProceduralGradient(fallbackIdx);
    this.userColorMap.set(normalizedKey, color);
    return color;
  }

  private calculateProportionalLayout(
    sortedUsers: any[],
    width: number,
    height: number,
    gap: number = 12
  ): Array<{ x0: number; x1: number; y0: number; y1: number; data: any }> {
    const n = sortedUsers.length;
    if (n === 0) return [];

    const minCardWidth = 110;
    const minCardHeight = 78;

    const totalLeads = d3.sum(sortedUsers, d => d.assignedLeads || 0) || 1;
    const avgLeads = totalLeads / Math.max(1, n);
    const baseWeight = Math.max(0.5, Math.round(avgLeads * 0.12));

    const weightedUsers = sortedUsers.map(d => ({
      ...d,
      _origAssigned: d.assignedLeads || 0,
      _origConverted: d.convertedLeads || 0,
      _weight: (d.assignedLeads || 0) + baseWeight
    }));

    if (n <= 4) {
      const totalGapsX = (n - 1) * gap;
      const availableW = Math.max(10, width - totalGapsX);
      const colWidths = this.allocateProportionalDimensions(
        weightedUsers.map(u => u._weight),
        availableW,
        minCardWidth
      );

      let currX = 0;
      return weightedUsers.map((u, idx) => {
        const cw = colWidths[idx];
        const res = {
          x0: currX,
          x1: (idx === n - 1) ? width : currX + cw,
          y0: 0,
          y1: height,
          data: u
        };
        currX += cw + gap;
        return res;
      });
    }

    const columns: Array<{ users: any[]; totalWeight: number }> = [];
    let userIdx = 0;

    // Check top users for full height columns
    while (userIdx < Math.min(2, n - 3)) {
      const u = weightedUsers[userIdx];
      const leadRatio = u._origAssigned / totalLeads;
      if (leadRatio >= 0.22 && (n - userIdx - 1) >= 4) {
        columns.push({ users: [u], totalWeight: u._weight });
        userIdx++;
      } else {
        break;
      }
    }

    // Group remaining users into columns of 2 or 3 cards
    let remUsers = weightedUsers.slice(userIdx);

    while (remUsers.length > 0) {
      let cardsInCol = 2;
      if (remUsers.length >= 3 && remUsers.length % 2 !== 0 && (height - 2 * gap) / 3 >= minCardHeight) {
        cardsInCol = 3;
      }
      const colUsers = remUsers.slice(0, Math.min(cardsInCol, remUsers.length));
      remUsers = remUsers.slice(colUsers.length);
      const colWeight = d3.sum(colUsers, u => u._weight);
      columns.push({ users: colUsers, totalWeight: colWeight });
    }

    const numCols = columns.length;
    const totalGapsX = (numCols - 1) * gap;
    const availableW = Math.max(10, width - totalGapsX);
    const colWeights = columns.map(c => c.totalWeight);
    const colWidths = this.allocateProportionalDimensions(colWeights, availableW, minCardWidth);

    const layoutNodes: Array<{ x0: number; x1: number; y0: number; y1: number; data: any }> = [];
    let currX = 0;

    columns.forEach((col, cIdx) => {
      const cw = colWidths[cIdx];
      const x0 = currX;
      const x1 = (cIdx === numCols - 1) ? width : currX + cw;

      const numInCol = col.users.length;
      if (numInCol === 1) {
        layoutNodes.push({
          x0,
          x1,
          y0: 0,
          y1: height,
          data: col.users[0]
        });
      } else {
        const totalGapsY = (numInCol - 1) * gap;
        const availableH = Math.max(10, height - totalGapsY);
        const userWeightsInCol = col.users.map(u => u._weight);
        const cardHeights = this.allocateProportionalDimensions(userWeightsInCol, availableH, minCardHeight);

        let currY = 0;
        col.users.forEach((u, rIdx) => {
          const ch = cardHeights[rIdx];
          const y0 = currY;
          const y1 = (rIdx === numInCol - 1) ? height : currY + ch;
          layoutNodes.push({
            x0,
            x1,
            y0,
            y1,
            data: u
          });
          currY += ch + gap;
        });
      }

      currX += cw + gap;
    });

    return layoutNodes;
  }

  private allocateProportionalDimensions(
    weights: number[],
    availableSpace: number,
    minDimension: number
  ): number[] {
    const n = weights.length;
    if (n === 0) return [];
    if (n === 1) return [availableSpace];

    const minTotalNeeded = n * minDimension;
    if (availableSpace <= minTotalNeeded) {
      const equalSize = Math.floor(availableSpace / n);
      const res = new Array(n).fill(equalSize);
      res[n - 1] += availableSpace - equalSize * n;
      return res;
    }

    let spaceRemaining = availableSpace - minTotalNeeded;
    const totalWeight = d3.sum(weights) || 1;

    let alloc = weights.map(w => minDimension + Math.floor(spaceRemaining * (w / totalWeight)));
    let currentSum = d3.sum(alloc);
    let remainder = availableSpace - currentSum;

    for (let i = 0; i < remainder; i++) {
      alloc[i % n] += 1;
    }

    return alloc;
  }

  drawTerritoryMap() {
    if (!this.chart?.nativeElement) return;

    const containerWidth = this.chart.nativeElement.clientWidth || 1000;
    const isMobile = window.innerWidth <= 768;
    const width = isMobile ? Math.max(containerWidth, 640) : containerWidth;
    const height = 310;

    d3.select(this.chart.nativeElement).selectAll('*').remove();

    // EMPTY STATE
    if (!this.territoryData.length) {
      this.drawEmptyTerritoryState();
      return;
    }
    const isDark = this.themeService.isDarkMode;
    const bg = isDark ? 'transparent' : '#ffffff';

    const svg = d3.select(this.chart.nativeElement)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('width', isMobile ? `${width}px` : '100%')
      .style('min-width', isMobile ? `${width}px` : '0')
      .style('height', `${height}px`)
      .style('background', bg)
      .style('font-family', "'Poppins', sans-serif")
      .style('overflow', 'hidden');


    // DEFS: Gradients, Filters, Patterns
    const defs = svg.append('defs');

    // Soft Card Drop Shadow
    const filter = defs.append('filter')
      .attr('id', 'card-drop-shadow')
      .attr('x', '-10%')
      .attr('y', '-10%')
      .attr('width', '130%')
      .attr('height', '140%');

    filter.append('feDropShadow')
      .attr('dx', 0)
      .attr('dy', 6)
      .attr('stdDeviation', 6)
      .attr('flood-color', isDark ? '#000000' : '#64748b')
      .attr('flood-opacity', isDark ? '0.3' : '0.12');

    // Dot matrix background texture pattern (matching Image 2)
    const dotPattern = defs.append('pattern')
      .attr('id', 'card-dot-matrix')
      .attr('width', 14)
      .attr('height', 14)
      .attr('patternUnits', 'userSpaceOnUse');

    dotPattern.append('circle')
      .attr('cx', 7)
      .attr('cy', 7)
      .attr('r', 1.1)
      .attr('fill', 'rgba(255, 255, 255, 0.18)');

    const allUserKeys = (this.territoryData || [])
      .map(u => (u.name || (u as any).userName || (u as any).id || '').toLowerCase().trim())
      .filter(Boolean);

    // Sort users descending by assigned leads (and converted leads as secondary tie-breaker)
    const sortedData = [...this.territoryData].sort((a, b) => {
      const valA = a.assignedLeads || 0;
      const valB = b.assignedLeads || 0;
      if (valB !== valA) return valB - valA;
      return (b.convertedLeads || 0) - (a.convertedLeads || 0);
    });

    const gap = 12;
    const leaves = this.calculateProportionalLayout(sortedData, width, height, gap);

    // Render each leaf card
    leaves.forEach((d, i) => {
      const nw = Math.max(0, d.x1 - d.x0);
      const nh = Math.max(0, d.y1 - d.y0);
      if (nw <= 10 || nh <= 10) return;

      const userKey = (d.data.name || d.data.userName || d.data.id || `user-${i}`).toLowerCase().trim();
      const colorObj = this.getUserColor(userKey, allUserKeys);
      const gradId = `card-grad-${this.slugify(userKey)}-${i}`;
      const clipId = `card-clip-${i}`;

      const lg = defs.append('linearGradient')
        .attr('id', gradId)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%');

      lg.append('stop').attr('offset', '0%').attr('stop-color', colorObj.start);
      lg.append('stop').attr('offset', '100%').attr('stop-color', colorObj.end);

      defs.append('clipPath')
        .attr('id', clipId)
        .append('rect')
        .attr('width', nw)
        .attr('height', nh)
        .attr('rx', 18)
        .attr('ry', 18);

      const nodeGroup = svg.append('g')
        .attr('transform', `translate(${d.x0}, ${d.y0})`)
        .style('cursor', 'pointer');

      nodeGroup.append('rect')
        .attr('width', nw)
        .attr('height', nh)
        .attr('rx', 18)
        .attr('ry', 18)
        .attr('fill', `url(#${gradId})`)
        .attr('filter', 'url(#card-drop-shadow)')
        .style('transition', 'transform 0.25s ease, opacity 0.25s ease');

      const innerGroup = nodeGroup.append('g')
        .attr('clip-path', `url(#${clipId})`);

      innerGroup.append('rect')
        .attr('width', nw)
        .attr('height', nh)
        .attr('fill', 'url(#card-dot-matrix)')
        .attr('pointer-events', 'none');

      const centerX = nw / 2;

      if (nh >= 120 && nw >= 100) {
        // --- TALL / LARGE CARD LAYOUT ---
        const nameY = nh / 2 - 28;
        const nameFontSize = Math.min(24, Math.max(15, Math.floor(nw * 0.11)));

        // User Name
        innerGroup.append('text')
          .attr('x', centerX)
          .attr('y', nameY)
          .attr('text-anchor', 'middle')
          .attr('fill', '#ffffff')
          .style('font-size', `${nameFontSize}px`)
          .style('font-family', "'Poppins', sans-serif")
          .style('font-weight', '600')
          .style('letter-spacing', '0.3px')
          .style('text-transform', 'capitalize')
          .style('text-shadow', '0px 2px 4px rgba(0, 0, 0, 0.4), 0px 0px 4px rgba(255, 255, 255, 0.4)')
          .text(this.capitalizeName(d.data.name));

        // Assigned Leads Count
        const assignedY = nameY + Math.min(26, Math.max(20, nameFontSize + 6));
        innerGroup.append('text')
          .attr('x', centerX)
          .attr('y', assignedY)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.95)')
          .style('font-size', `${Math.min(16, Math.max(13, nameFontSize - 3))}px`)
          .style('font-family', "'Poppins', sans-serif")
          .style('font-weight', '500')
          .style('text-shadow', '0px 1px 3px rgba(0, 0, 0, 0.35), 0px 0px 3px rgba(255, 255, 255, 0.35)')
          .text(`${d.data._origAssigned} leads`);

        // Separator Line
        const lineY = assignedY + 8;
        const lineHalfWidth = Math.min(35, nw * 0.22);
        innerGroup.append('line')
          .attr('x1', centerX - lineHalfWidth)
          .attr('y1', lineY)
          .attr('x2', centerX + lineHalfWidth)
          .attr('y2', lineY)
          .attr('stroke', 'rgba(255, 255, 255, 0.4)')
          .attr('stroke-width', 1);

        // Converted Leads Count
        const convertY = lineY + 18;
        innerGroup.append('text')
          .attr('x', centerX)
          .attr('y', convertY)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.95)')
          .style('font-size', `${Math.min(15, Math.max(12, nameFontSize - 4))}px`)
          .style('font-family', "'Poppins', sans-serif")
          .style('font-weight', '500')
          .style('text-shadow', '0px 1px 3px rgba(0, 0, 0, 0.35), 0px 0px 3px rgba(255, 255, 255, 0.35)')
          .text(`${d.data._origConverted} convert`);

      } else if (nh >= 75) {
        // --- MEDIUM CARD LAYOUT ---
        const nameFontSize = Math.min(18, Math.max(13, Math.floor(nw * 0.10)));
        const startY = nh / 2 - 16;

        // User Name
        innerGroup.append('text')
          .attr('x', centerX)
          .attr('y', startY)
          .attr('text-anchor', 'middle')
          .attr('fill', '#ffffff')
          .style('font-size', `${nameFontSize}px`)
          .style('font-family', "'Poppins', sans-serif")
          .style('font-weight', '600')
          .style('text-transform', 'capitalize')
          .style('text-shadow', '0px 2px 4px rgba(0, 0, 0, 0.4)')
          .text(this.capitalizeName(d.data.name));

        // Assigned Leads Count
        innerGroup.append('text')
          .attr('x', centerX)
          .attr('y', startY + 18)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.95)')
          .style('font-size', `${Math.min(14, Math.max(11, nameFontSize - 2))}px`)
          .style('font-family', "'Poppins', sans-serif")
          .style('font-weight', '500')
          .style('text-shadow', '0px 1px 3px rgba(0, 0, 0, 0.35)')
          .text(`${d.data._origAssigned} leads`);

        // Converted Leads Count
        innerGroup.append('text')
          .attr('x', centerX)
          .attr('y', startY + 34)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.95)')
          .style('font-size', `${Math.min(13, Math.max(11, nameFontSize - 3))}px`)
          .style('font-family', "'Poppins', sans-serif")
          .style('font-weight', '500')
          .style('text-shadow', '0px 1px 3px rgba(0, 0, 0, 0.35)')
          .text(`${d.data._origConverted} convert`);

      } else {
        // --- COMPACT CARD LAYOUT ---
        const nameFontSize = Math.min(14, Math.max(11, Math.floor(nw * 0.09)));
        const startY = nh / 2 - 10;

        innerGroup.append('text')
          .attr('x', centerX)
          .attr('y', startY)
          .attr('text-anchor', 'middle')
          .attr('fill', '#ffffff')
          .style('font-size', `${nameFontSize}px`)
          .style('font-family', "'Poppins', sans-serif")
          .style('font-weight', '600')
          .style('text-transform', 'capitalize')
          .style('text-shadow', '0px 1px 3px rgba(0, 0, 0, 0.4)')
          .text(this.capitalizeName(d.data.name));

        innerGroup.append('text')
          .attr('x', centerX)
          .attr('y', startY + 16)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255, 255, 255, 0.95)')
          .style('font-size', `${Math.max(10, nameFontSize - 2)}px`)
          .style('font-family', "'Poppins', sans-serif")
          .style('font-weight', '500')
          .style('text-shadow', '0px 1px 2px rgba(0, 0, 0, 0.35)')
          .text(`${d.data._origAssigned}L • ${d.data._origConverted}C`);
      }

      // Smooth Hover Effect
      nodeGroup.on('mouseenter', function () {
        d3.select(this).select('rect')
          .transition()
          .duration(200)
          .attr('transform', 'scale(1.015)')
          .style('opacity', '0.96');
      }).on('mouseleave', function () {
        d3.select(this).select('rect')
          .transition()
          .duration(200)
          .attr('transform', 'scale(1)')
          .style('opacity', '1');
      });
    });
  }

  getNameFontSize(d: any): number {
    const w = d.x1 - d.x0;
    const h = d.y1 - d.y0;
    const minDim = Math.min(w, h);
    if (minDim > 160) return 20;
    if (minDim > 120) return 17;
    if (minDim > 80) return 15;
    if (minDim > 50) return 13;
    return 11;
  }

  getValueFontSize(d: any): number {
    const w = d.x1 - d.x0;
    const h = d.y1 - d.y0;
    const minDim = Math.min(w, h);
    if (minDim > 160) return 13;
    if (minDim > 120) return 12;
    return 11;
  }

  canShowThreeLines(d: any): boolean {
    return (d.y1 - d.y0) >= 80;
  }

  drawEmptyTerritoryState() {
    const containerWidth = this.chart?.nativeElement?.clientWidth || 1200;
    const width = Math.max(containerWidth, 600);
    const height = 310;

    const isDark = this.themeService.isDarkMode;
    const bg = isDark ? '#1e293b' : '#ffffff';
    const strokeColor = isDark ? '#334155' : '#e2e8f0';
    const textPrimary = isDark ? '#f8fafc' : '#374151';
    const textSecondary = isDark ? '#94a3b8' : '#64748b';

    const svg = d3.select(this.chart.nativeElement)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('width', '100%')
      .style('height', `${height}px`)
      .style('background', isDark ? 'transparent' : '#ffffff')
      .style('font-family', "'Poppins', sans-serif");

    // Soft background card
    svg.append('rect')
      .attr('x', 16)
      .attr('y', 16)
      .attr('width', width - 32)
      .attr('height', height - 32)
      .attr('rx', 18)
      .attr('fill', bg)
      .attr('stroke', strokeColor)
      .attr('stroke-width', 1);

    const centerX = width / 2;
    const centerY = height / 2;

    svg.append('text')
      .attr('x', centerX)
      .attr('y', centerY - 10)
      .attr('text-anchor', 'middle')
      .attr('fill', textPrimary)
      .style('font-size', '20px')
      .style('font-family', "'Poppins', sans-serif")
      .style('font-weight', '600')
      .style('letter-spacing', '0.3px')
      .text('No Data Available');

    svg.append('text')
      .attr('x', centerX)
      .attr('y', centerY + 24)
      .attr('text-anchor', 'middle')
      .attr('fill', textSecondary)
      .style('font-size', '14px')
      .style('font-family', "'Poppins', sans-serif")
      .style('font-weight', '400')
      .style('letter-spacing', '0.2px')
      .text('Try selecting a different date range or filter');
  }




  openKanbanFromRoute() {
    this.showCanvasUI = true;
    this.isCanvasLoading = true;
    this.loadAllColumns();
  }
  openCanvasUI(clickedLead: any) {
    this.showCanvasUI = true;
    this.isCanvasLoading = true;
    this.selectedLeadForDetails = clickedLead;

    const moduleRoute = this.getModuleRouteByProject(clickedLead?.project || this.project);
    this.router.navigate([`/lead-track/${moduleRoute}`], { queryParams: { view: 'kanban' } });
  }

  totalStatusCount: Record<string, number> = {};
  sortOrder: number = -1;
  dateRangeOption: '30' | '60' | '90' | 'all' = 'all';
  loadAllColumns() {
    this.isCanvasLoading = true;
    this.columnsLoaded = 0;

    const basePayload: any = {
      pageNum: 1,
      limit: 100,
      sort: this.sortOrder,
      startDate: this.startDate,
      endDate: this.endDate
    };

    if (this.selectedCustomerValue) {
      basePayload.customerValue = this.selectedCustomerValue.toLowerCase();
    }

    if (this.kanbanSearchQuery && this.kanbanSearchQuery.trim()) {
      basePayload.searchQuery = this.kanbanSearchQuery.trim();
    }

    if (this.project !== 'ALL') {
      basePayload.project = this.project;
    }

    const statuses = [
      { status: "Follow-up Task", target: "followUpLeadsCanvas", isFollowUpTask: true },
      { status: "Not started", target: "assignedLeadsCanvas" },
      { status: "Contacted", target: 0 },
      { status: "Email introduction", target: 1 },
      { status: "Meetings in Video call", target: 2 },
      { status: "Meetings in person", target: 3 },
      { status: "Presentation", target: 4 },
      { status: "Cost sheet", target: 5 },
      { status: "Closing Pool", target: 6 },
      { status: "Closed", target: 7 },
      { status: "Lost", target: 8 },
      { status: "Unqualified", target: 9 }
    ];

    statuses.forEach((s: any) => {
      const payload: any = { ...basePayload };

      if (s.isFollowUpTask) {
        this.crmService.getFollowUpLeads({ ...payload, project: this.project }).subscribe({
          next: (res: any) => {
            const items = res?.data?.leads || res?.data?.data || [];
            const count = res?.data?.leadsCount ?? items.length;
            this.totalStatusCount[s.status] = count;
            this.followUpLeadsCanvas = items;
            this.markColumnLoaded();
          },
          error: (err: any) => {
            console.error('Error fetching follow-up leads:', err);
            this.markColumnLoaded();
          }
        });
      } else {

        // Closing Pool queries by hasClosingPotential flag across statuses
        if (s.status === 'Closing Pool') {
          payload.hasClosingPotential = true;
        } else {
          payload.status = s.status;
        }

        this.crmService.getAdminAssignedLead({ ...payload, project: this.project }).subscribe((res: any) => {
          const items = res?.data?.data || [];
          const count = res?.data?.leadsCount || items.length;

          this.totalStatusCount[s.status] = count;

          if (s.target === "assignedLeadsCanvas") {
            this.assignedLeadsCanvas = items;
          } else {
            this.emptyColumns[s.target].items = items;
          }

          this.markColumnLoaded();
        });
      }
    });
  }





  toggleSortOrder() {
    this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    this.loadAllColumns();
  }



  isCustomerValueDropdownOpen = false;
  selectedCustomerValue: '' | 'Premium' | 'Emerging' | 'Basic' | 'Regular' = '';
  customerValueFilterLabel = 'Badge';

  kanbanSearchQuery: string = '';
  private kanbanSearchTimer: any = null;

  onKanbanSearchChange() {
    if (this.kanbanSearchTimer) {
      clearTimeout(this.kanbanSearchTimer);
    }
    this.kanbanSearchTimer = setTimeout(() => {
      this.currentPageNume = 1;
      this.loadAllColumns();
    }, 350);
  }

  clearKanbanSearch() {
    this.kanbanSearchQuery = '';
    this.currentPageNume = 1;
    this.loadAllColumns();
  }

  toggleCustomerValueDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isCustomerValueDropdownOpen = !this.isCustomerValueDropdownOpen;
    if (this.isCustomerValueDropdownOpen) {
      this.isDateDropdownOpen = false;
    }
  }

  applyCustomerValueFilter(value: '' | 'Premium' | 'Emerging' | 'Basic' | 'Regular') {
    this.selectedCustomerValue = value;
    this.isCustomerValueDropdownOpen = false;
    this.customerValueFilterLabel = value ? value : 'Badge';

    this.currentPageNume = 1;
    this.loadAllColumns();
  }

  isDateDropdownOpen = false;
  dateFilterKey: 'all' | '30' | '60' | '90' = 'all';
  dateFilterLabel = 'All Time';
  startDate = '';
  endDate = '';

  toggleDateDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isDateDropdownOpen = !this.isDateDropdownOpen;
    if (this.isDateDropdownOpen) {
      this.isCustomerValueDropdownOpen = false;
    }
  }

  closeAllDropdowns() {
    this.isCustomerValueDropdownOpen = false;
    this.isDateDropdownOpen = false;
    this.badgeDropdownOpen = false;
    this.followUpTaskAssigneeDropdownOpen = false;
  }

  applyDateFilter(value: 'all' | '30' | '60' | '90') {
    this.dateFilterKey = value;
    this.isDateDropdownOpen = false;

    if (value === 'all') {
      this.startDate = '';
      this.endDate = '';
      this.dateFilterLabel = 'All Time';
    } else {
      const days = Number(value);
      const end = new Date();
      const start = new Date();

      start.setDate(end.getDate() - days);

      this.startDate = this.formatDate(start);
      this.endDate = this.formatDate(end);
      this.dateFilterLabel = `Last ${value} Days`;
    }

    this.currentPageNume = 1;
    this.leads = [];
    this.loadAllColumns();

  }

  formatDate(date: Date | any): string {
    if (!date) return '';
    if (typeof date === 'string') return date.split('T')[0];
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }



  markColumnLoaded() {
    this.columnsLoaded++;
    if (this.columnsLoaded === this.totalColumnsToLoad) {
      this.isCanvasLoading = false;
    }
  }

  generateMonthlyData(data: any[]) {
    const groupByMonth = {};

    data.forEach((item) => {
      const date = new Date(item.date || item.createdAt || item.updatedAt);
      if (isNaN(date.getTime())) return;

      const key = `${date.getFullYear()}-${date.getMonth()}`;

      groupByMonth[key] = (groupByMonth[key] || 0) + 1;
    });

    const monthlyData = [];

    Object.keys(groupByMonth).forEach((key) => {
      const [year, month] = key.split("-").map(Number);
      const timestamp = new Date(year, month, 1).getTime();
      monthlyData.push([timestamp, groupByMonth[key]]);
    });

    return monthlyData.sort((a, b) => a[0] - b[0]);
  }

  public generateData(baseval, count, yrange) {
    var i = 0;
    var series = [];
    while (i < count) {
      var x = Math.floor(Math.random() * (750 - 1 + 1)) + 1;
      var y =
        Math.floor(Math.random() * (yrange.max - yrange.min + 1)) + yrange.min;
      var z = Math.floor(Math.random() * (75 - 15 + 1)) + 15;

      series.push([x, y, z]);
      baseval += 86400000;
      i++;
    }
    return series;
  }
  getTotalLeads(): string {
    return (this.donutchartOptions.series as number[])
      .reduce((a, b) => a + b, 0)
      .toString();
  }
  getOverallPercentage(): string {
    const total = Number(this.totalsLeadsCounts) || 0;
    const converted = Number(this.totalConvertedLeadsCount) || 0;
    const lost = Number(this.totalLostLeadsCount) || 0;

    const completed = converted + lost;

    if (total === 0) return "0%";

    const percent = (completed / total) * 100;

    return percent.toFixed(0) + "%";
  }

  createRadialChartData() {
    const donutSeries = [
      this.totalsLeadsCounts || 0,
      this.totalInProgressCount || 0,
      this.totalConvertedLeadsCount || 0,
      this.totalLostLeadsCount || 0,
    ];

    const colors = ["#5E65FF", "#FAE04E", "#98BE55", "#DD8179"];
    const labels = [
      "Total",
      "In Progress",
      "Converted",
      "Lost",
    ];

    const icons = [
      "../../assets/leads/newleads-radial.png",
      "../../assets/leads/inProgress-radial.png",
      "../../assets/leads/converted-radial.png",
      "../../assets/leads/lostlead-radial.png",
    ];

    const bgColors = [
      "rgba(226, 230, 255, 0.2)",
      "rgba(250, 224, 78, 0.2)",
      "rgba(158, 198, 0, 0.2)",
      "rgba(198, 129, 0, 0.2)",
    ];

    this.radialChartData = donutSeries.map((value, index) => ({
      value,
      label: labels[index],
      icon: icons[index],
      color: colors[index],
      hollowBg: bgColors[index],

      chartOptions: {
        height: 120,
        type: "radialBar",
        toolbar: { show: false },
      },

      plotOptions: {
        radialBar: {
          hollow: {
            size: "55%",
            background: bgColors[index],
            margin: 0,
          },
          dataLabels: {
            name: { show: false },
            value: { show: false },
          },
        },
      },

      stroke: { lineCap: "round" },
      fill: { colors: [colors[index]] },
    }));
  }

  updateDonutChart() {
    const colors = ["#3b82f6", "#eab308", "#22c55e", "#ef4444", "#a855f7"];

    this.donutchartOptions = {
      ...this.donutchartOptions,

      series: [
        this.totalsLeadsCounts || 0,
        this.totalInProgressCount || 0,
        this.totalConvertedLeadsCount || 0,
        this.totalLostLeadsCount || 0,
        this.totalClosingPoolCount || 0,
      ],

      labels: ["Total Leads", "In Progress", "Converted Leads", "Lost Leads", "Closing Pool"],

      colors: colors,
    };
  }

  getSelectedMonthRange(startDate: string, endDate: string) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    return {
      startMonth: start.getMonth() + 1,
      endMonth: end.getMonth() + 1,
      year: start.getFullYear()
    };
  }





  formatPrettyDate(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  }

  clearDateFilter() {
    this.startDate = "";
    this.endDate = "";
    this.selectedDateRange = null;
    this.displayDateRange = null;
    this.isFilteredView = false;

    this.currentPageNume = 1;
    this.leads = [];
    this.newsLetter = [];
    this.careersList = [];

    console.log("Date filter cleared → loading default year graph");
    this.getAdminOverView();
    this.getAllLeadsBreakdown();

    if (this.selectedSection === 'Newsletter') {
      this.getNewsLetter();
    } else if (this.selectedSection === 'Career') {
      this.getCareer();
    } else {
      this.getLeadsListingDetail();
    }
  }
  deleteLead(leadId: string, lead?: any) {
    alertModalDelete('Are you sure you want to delete this lead?', '')
      .then((confirmed) => {
        if (!confirmed) return;

        const foundLead = lead || this.leads.find(l => l._id === leadId);
        const projectTab = this.getLeadProjectTab(foundLead);
        // In ALL mode use the lead's own project; otherwise use this.project
        const projectForDelete = (this.project === 'ALL' && foundLead?.project)
          ? foundLead.project
          : this.project;

        const isIicLab = projectForDelete === 'IIC-LAB' || projectForDelete === 'IIC' || foundLead?.project === 'IIC-LAB' || foundLead?.project === 'IIC';
        const isCareer = (projectTab && projectTab.toLowerCase() === 'career') || (this.selectedSection && this.selectedSection.toLowerCase() === 'career') || (foundLead?.source && String(foundLead.source).toLowerCase() === 'career');

        if (isIicLab && isCareer) {
          this.crmService.deleteIICLabLead('Career', leadId).subscribe(
            () => {
              console.log('Career Lead deleted successfully.');
              if (this.selectedSection === 'Career') {
                this.getCareer();
              } else {
                this.getLeadsListingDetail();
              }
              this.getAdminOverView();
            },
            (err) => {
              console.error('Delete failed', err);
            }
          );
        } else {
          this.crmService.deleteLead(projectForDelete, projectTab, leadId).subscribe(
            () => {
              console.log('Lead deleted successfully.');

              if (this.selectedSection === 'Newsletter') {
                this.getNewsLetter();
              } else if (this.selectedSection === 'Career') {
                this.getCareer();
              } else {
                this.getLeadsListingDetail();
              }
              this.getAdminOverView();
            },
            (err) => {
              console.error('Delete failed', err);
            }
          );
        }
      });
  }

  shiftToIICLeads(leadId: string) {
    if (!leadId) return;

    this.crmService.transferLead(leadId).subscribe({
      next: (res: any) => {
        alertModalWithoutConfirm('success', 'Lead successfully shifted to IIC Leads!');
        this.getLeadsListingDetail();
        this.getAdminOverView();
      },
      error: (err: any) => {
        console.error('Error shifting lead to IIC:', err);
        const msg = err?.error?.message || err?.message || 'Failed to shift lead to IIC Leads.';
        alertModalWithoutConfirm('error', msg);
      }
    });
  }

  deleteIICLead(leadId: string) {
    let source: 'Career' | 'Newsletter';
    if (this.selectedSection === 'Career') {
      source = 'Career';
    } else if (this.selectedSection === 'Newsletter') {
      source = 'Newsletter';
    } else {
      return;
    }
    alertModalDelete('Are you sure you want to delete this entry?', '')
      .then((confirmed) => {
        if (!confirmed) return;

        this.crmService.deleteIICLabLead(source, leadId).subscribe(
          () => {
            console.log('Deleted successfully.');
            if (source === 'Career') {
              this.getCareer();
            } else {
              this.getNewsLetter();
            }
          },
          (err) => {
            console.error('Delete failed', err);
          }
        );
      });
  }


  expandedLeadId: string | null = null;
  toggleAccordion(lead: any, event: Event) {
    event.stopPropagation();
    this.expandedLeadId =
      this.expandedLeadId === lead._id ? null : lead._id;
  }

  getStatusIcon(status?: string): string {
    if (!status) return '';
    const normalized = status.trim().toLowerCase();
    const found = this.canvasTabs.find(tab =>
      tab.label.toLowerCase() === normalized ||
      tab.status.toLowerCase() === normalized
    );
    if (found?.icon) return found.icon;

    if (normalized.includes('video')) return '/assets/leads/video-meeting.png';
    if (normalized.includes('person')) return '/assets/leads/meeting-icon.png';
    if (normalized.includes('contacted')) return '/assets/leads/circle-phone.png';
    if (normalized.includes('email')) return '/assets/leads/email-tab.png';
    if (normalized.includes('presentation')) return '/assets/leads/presentation-icon.png';
    if (normalized.includes('cost')) return '/assets/leads/cost-sheet-icon.png';
    if (normalized.includes('closed')) return '/assets/leads/closed.png';
    if (normalized.includes('lost')) return '/assets/leads/lost-icon.png';
    if (normalized.includes('unqualified')) return '/assets/leads/unqualified.png';
    if (normalized.includes('not started') || normalized.includes('assigned')) return '/assets/leads/assigned-lead-icon.png';

    return '';
  }

  getStatusClass(status: string | undefined): string {
    if (!status) return 'status-blue';
    const s = String(status).trim().toLowerCase();
    switch (s) {
      case 'not started':
        return 'status-grey';
      case 'lost':
        return 'status-red';
      case 'closed':
        return 'status-green';
      case 'unassigned':
        return 'status-gold';
      default:
        return 'status-blue';
    }
  }


  // DATE RANGE PICKER
  selectedDateRange: any = null;
  locale = {
    format: "MM/DD/YYYY",
    applyLabel: "OK",
    cancelLabel: "Cancel",
    customRangeLabel: "Custom Range",
    daysOfWeek: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    monthNames: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    firstDay: 1,
  };

  ranges: any = {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
    "Last 7 Days": [moment().subtract(6, "days"), moment()],
    "Last 30 Days": [moment().subtract(29, "days"), moment()],
    "This Month": [moment().startOf("month"), moment().endOf("month")],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  };

  // Filter variables
  isMediaLoading = true;
  folderId: string = "";
  isFilteredView = false;
  displayDateRange: string | null = null;
  onDateSelected(event: any) {
    if (event.startDate && event.endDate) {
      const start = event.startDate;
      const end = event.endDate;
      this.startDate = this.toISODate(start, false);
      this.endDate = this.toISODate(end, true);
      this.displayDateRange = `${this.formatDisplayDate(
        start
      )} - ${this.formatDisplayDate(end)}`;
      this.isFilteredView = true;
      console.log("Selected Date Range:", this.startDate, this.endDate);
      this.currentPageNume = 1;
      this.leads = [];
      this.newsLetter = [];
      this.careersList = [];
      this.getAllLeadsBreakdown();
      this.getAdminOverView();
      if (this.selectedSection === 'Newsletter') {
        this.getNewsLetter();
      } else if (this.selectedSection === 'Career') {
        this.getCareer();
      } else {
        this.getLeadsListingDetail();
      }
    }
  }

  toISODate(momentDate: any, isEndOfDay: boolean = false): string {
    if (!momentDate) return '';
    const year = momentDate.year ? momentDate.year() : new Date(momentDate).getFullYear();
    const month = momentDate.month ? momentDate.month() : new Date(momentDate).getMonth();
    const date = momentDate.date ? momentDate.date() : new Date(momentDate).getDate();

    if (isEndOfDay) {
      return new Date(Date.UTC(year, month, date, 23, 59, 59, 999)).toISOString();
    }
    return new Date(Date.UTC(year, month, date, 0, 0, 0, 0)).toISOString();
  }

  onDatepickerHide() {
    console.log("Datepicker hidden");
  }
  formatDisplayDate(momentDate: any): string {
    const day = String(momentDate.date()).padStart(2, "0");
    const month = String(momentDate.month() + 1).padStart(2, "0");
    const year = momentDate.year();
    return `${day}/${month}/${year}`;
  }

  totalsLeadsCounts: any;
  totalAssignedLeadsCount: any;
  totalInProgressCount: any;
  totalConvertedLeadsCount: any;
  totalLostLeadsCount: any;
  totalClosingPoolCount: any;
  progressLeadPercentage: any;
  convertedLeadPercentage: any;
  lostLeadsPercentage: any;
  salesUserName: any;
  role: any;
  progressLeadTrend: any = { type: 'neutral', value: 0, text: 'No change from last week' };
  convertedLeadTrend: any = { type: 'neutral', value: 0, text: 'No change from last week' };
  lostLeadTrend: any = { type: 'neutral', value: 0, text: 'No change from last week' };
  totalLeadsTrend: any = { type: 'neutral', value: 0, text: 'No change from last week' };
  isStatsLoading: boolean = true;
  isChartLoading = true;
  isDonutChartLoading = true;
  getAdminOverView() {
    if (this.userDepartment === 'MARKETING') {
      this.isStatsLoading = false;
      this.isDonutChartLoading = false;
      return;
    }
    this.isStatsLoading = true;
    this.isDonutChartLoading = true;
    this.crmService.getLeadsOverview(this.project, this.startDate, this.endDate).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.changeDetector.markForCheck())
    ).subscribe((res: any) => {
      this.isStatsLoading = false;
      this.isDonutChartLoading = false;
      this.totalsLeadsCounts = res.data.totalLeadsCount;
      this.totalAssignedLeadsCount = res.data.totalAssignedLeads;
      this.salesUserName = res.data.userName;
      this.role = res.data.role;
      this.totalInProgressCount = res.data.inProgressLeadsCount;
      this.totalConvertedLeadsCount = res.data.convertedLeadsCount;
      this.totalLostLeadsCount = res.data.lostLeadsCount;
      this.totalClosingPoolCount = res.data.closingPoolCount;

      this.progressLeadPercentage = res.data.inProgressLeadsPercentage;
      this.convertedLeadPercentage = res.data.convertedLeadsPercentage;
      this.lostLeadsPercentage = res.data.lostLeadsPercentage;
      this.progressLeadTrend = this.getTrendText(res.data.inProgressLeadsChange);
      this.convertedLeadTrend = this.getTrendText(res.data.convertedLeadsChange);
      this.lostLeadTrend = this.getTrendText(res.data.lostLeadsChange);
      this.totalLeadsTrend = this.getTrendText(res.data.totalLeadsChange);

      this.updateDonutChart();
      this.createRadialChartData();
    }, () => {
      this.isStatsLoading = false;
      this.isDonutChartLoading = false;
    });
  }

  getTrendText(change: number) {
    if (change > 0) {
      return {
        value: change,
        text: 'more than last week',
        type: 'positive'
      };
    }

    if (change < 0) {
      return {
        value: Math.abs(change),
        text: 'less than last week',
        type: 'negative'
      };
    }

    return {
      value: 0,
      text: 'No change from last week',
      type: 'neutral'
    };
  }


  currentPageNume = 1;
  currentLimit = 100;
  selectedType: string = "ALL";
  isSpinnerLoader: boolean = false;
  searchQuery: string;
  totalLeads: number;
  leads: any = [];
  showNoDataMessage: boolean = false;
  searchDebounce: any;
  selectedLeadName: string = "";
  isUnassignedFilter: boolean = false;
  isDeletedFilter: boolean = false;
  get isUnQualifiedFilter(): boolean {
    return this.isDeletedFilter;
  }
  set isUnQualifiedFilter(val: boolean) {
    this.isDeletedFilter = val;
  }

  toggleDeletedFilter() {
    this.currentPageNume = 1;
    this.leads = [];
    this.newsLetter = [];
    this.careersList = [];

    this.isDeletedFilter = !this.isDeletedFilter;

    if (this.isDeletedFilter) {
      this.isUnassignedFilter = false;
      this.selectedAssignedUser = "allUsers";
      if (this.selectedOptionStatus === 'Unqualified' || this.selectedOptionStatus === 'deleted') {
        this.selectedOptionStatus = '';
      }
    }

    if (this.selectedSection === 'Newsletter') {
      this.getNewsLetter();
      return;
    }
    if (this.selectedSection === 'Career') {
      this.getCareer();
      return;
    }
    this.getLeadsListingDetail();
  }

  resetDeletedFilter(event: Event) {
    event.stopPropagation();
    this.isDeletedFilter = false;
    this.currentPageNume = 1;
    this.leads = [];
    this.newsLetter = [];
    this.careersList = [];

    if (this.selectedSection === 'Newsletter') {
      this.getNewsLetter();
      return;
    }
    if (this.selectedSection === 'Career') {
      this.getCareer();
      return;
    }
    this.getLeadsListingDetail();
  }

  private leadsRequest?: Subscription;
  getLeadsListingDetail(): void {
    if (this.selectedType === "Career") {
      this.selectedSection = "Career";
      this.careerTable = true;
      this.newsLetterTable = false;
      this.getCareer();
      return;
    }
    if (this.selectedType === "Newsletter") {
      this.selectedSection = "Newsletter";
      this.newsLetterTable = true;
      this.careerTable = false;
      this.getNewsLetter();
      return;
    }

    this.careerTable = false;
    this.newsLetterTable = false;

    const payload: any = {
      searchQuery: (this.selectedLeadName || this.searchQuery || "").trim(),
      pageNum: this.currentPageNume,
      limit: this.currentLimit,
    };

    if (this.project === 'ALL') {
      payload.isAll = true;
    } else {
      payload.project = this.project;
    }

    if (this.startDate) {
      payload.startDate = this.startDate;
    }
    if (this.endDate) {
      payload.endDate = this.endDate;
    }

    if (this.selectedType && this.selectedType !== "ALL") {
      if (this.selectedType === "Outbound" || this.selectedType === "OUTBOUND") {
        payload.leadType = "Outbound";
      } else {
        payload.projectTab = this.selectedType;
      }
    }


    if (this.selectedOptionStatus) {
      if (this.selectedOptionStatus === 'unassigned') {
        payload.unAssigned = true;
      } else if (this.selectedOptionStatus === 'deleted') {
        payload.isDeleted = true;
      } else if (this.selectedOptionStatus === 'Closing Pool' || this.selectedOptionStatus === 'closing-pool') {
        payload.hasClosingPotential = true;
      } else {
        payload.status = this.selectedOptionStatus;
      }
    }

    if (!payload.unAssigned && this.isUnassignedFilter) {
      payload.unAssigned = true;
    } else if (!payload.unAssigned && !payload.isDeleted && this.selectedAssignedUser && this.selectedAssignedUser !== "allUsers") {
      payload.assignedTo = this.selectedAssignedUser;
    }

    if (this.isDeletedFilter) {
      payload.isDeleted = true;
      delete payload.status;
    }

    // Cancel an outdated search/filter request. Its response must not replace
    // newer rows or keep the table loader visible.
    this.leadsRequest?.unsubscribe();
    this.isSpinnerLoader = true;

    this.leadsRequest = this.crmService.searchLeadList(payload).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isSpinnerLoader = false;
        this.changeDetector.markForCheck();
      })
    ).subscribe(
      (response: any) => {
        const newLeads = response.data.leads || [];
        newLeads.sort((a: any, b: any) => {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });

        this.leads =
          this.currentPageNume === 1
            ? newLeads
            : [...this.leads, ...newLeads];

        this.totalLeads = response.data.totalLeads;

        this.filterLeads();
        this.showNoDataMessage = this.filteredLeads.length === 0;
      }
    );
  }



  selectedOptionStatus: string = '';
  onStatusChange() {
    this.currentPageNume = 1;
    this.leads = [];

    if (this.selectedOptionStatus === 'unassigned') {
      this.isUnassignedFilter = true;
      this.isDeletedFilter = false;
      this.selectedAssignedUser = "allUsers";
    } else if (this.selectedOptionStatus === 'deleted') {
      this.isDeletedFilter = true;
      this.isUnassignedFilter = false;
      this.selectedAssignedUser = "allUsers";
    } else {
      this.isUnassignedFilter = false;
      this.isDeletedFilter = false;
    }

    if (this.selectedSection === 'Newsletter') {
      this.getNewsLetter();
      return;
    }

    if (this.selectedSection === 'Career') {
      this.getCareer();
      return;
    }

    this.getLeadsListingDetail();
  }






  onSearchChange() {
    clearTimeout(this.searchDebounce);

    this.searchDebounce = setTimeout(() => {
      this.currentPageNume = 1;
      this.leads = [];
      this.newsLetter = [];
      this.careersList = [];

      if (this.selectedSection === 'Newsletter') {
        this.getNewsLetter();
      } else if (this.selectedSection === 'Career') {
        this.getCareer();
      } else {
        this.getLeadsListingDetail();
      }
    }, 500);
  }

  clearSearch() {
    this.searchQuery = "";
    this.currentPageNume = 1;
    this.leads = [];
    this.newsLetter = [];
    this.careersList = [];

    if (this.selectedSection === 'Newsletter') {
      this.getNewsLetter();
      return;
    } else if (this.selectedSection === 'Career') {
      this.getCareer();
      return;
    }
    this.getLeadsListingDetail();
  }

  newsLetterLength: number = 0;
  getNewsLetter() {
    const field: any = {
      pageNum: this.currentPageNume,
      limit: this.currentLimit,
      searchQuery: (this.searchQuery || "").trim(),
      projectTab: 'Newsletter'
    };

    if (this.project === 'ALL') {
      field.isAll = true;
    } else {
      field.project = this.project;
    }

    if (this.startDate) {
      field.startDate = this.startDate;
    }
    if (this.endDate) {
      field.endDate = this.endDate;
    }

    if (this.selectedOptionStatus) {
      if (this.selectedOptionStatus === 'unassigned') {
        field.unAssigned = true;
      } else if (this.selectedOptionStatus === 'deleted') {
        field.isDeleted = true;
      } else if (this.selectedOptionStatus === 'Closing Pool' || this.selectedOptionStatus === 'closing-pool') {
        field.hasClosingPotential = true;
      } else {
        field.status = this.selectedOptionStatus;
      }
    }

    if (!field.unAssigned && this.isUnassignedFilter) {
      field.unAssigned = true;
    }
    if (this.isDeletedFilter) {
      field.isDeleted = true;
      delete field.status;
    }

    this.isSpinnerLoader = true;

    this.crmService.searchLeadList(field).subscribe((res: any) => {
      const newNewsLetter = (res.data.leads || []).map((lead: any) => {
        if (lead.assignedTo) {
          lead.assignedToString = this.formatAssignedTo(lead.assignedTo);
        }
        return lead;
      });

      this.newsLetter =
        this.currentPageNume === 1
          ? newNewsLetter
          : [...this.newsLetter, ...newNewsLetter];

      this.newsLetter.sort((a: any, b: any) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

      this.isSpinnerLoader = false;
      this.newsLetterTable = true;
      this.iicContact = false;
      this.newsLetterLength = res?.data?.totalLeads ?? res?.totalLeads ?? this.newsLetter.length;
      this.totalLeads = this.newsLetterLength;

      // Sync filteredLeads so the non-IIC-LAB table (project === 'ALL' etc.) also renders newsletter leads
      this.filteredLeads = this.newsLetter;
      this.showNoDataMessage = this.filteredLeads.length === 0;
      this.changeDetector.markForCheck();
    });
  }


  careersList: any = [];
  careerTable = false;
  careerLength: number = 0;
  searchCareerQuery: any;
  getCareer(): void {
    const field: any = {
      pageNum: this.currentPageNume,
      limit: this.currentLimit,
      name: this.searchCareerQuery || this.selectedLeadName || this.searchQuery || ""
    };

    if (this.isDeletedFilter || this.selectedOptionStatus === 'deleted') {
      field.isDeleted = true;
      delete field.status;
    } else if (this.selectedOptionStatus === 'Closing Pool' || this.selectedOptionStatus === 'closing-pool') {
      field.hasClosingPotential = true;
    } else if (this.selectedOptionStatus) {
      field.status = this.selectedOptionStatus;
    }

    if (this.selectedOptionStatus === 'Closing Pool' || this.selectedOptionStatus === 'closing-pool') {
      field.hasClosingPotential = true;
    }

    this.isSpinnerLoader = true;
    this.careerTable = true;
    this.newsLetterTable = false;

    this.crmService.getCareerList(field).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isSpinnerLoader = false;
        this.changeDetector.markForCheck();
      })
    ).subscribe((res: any) => {
      const newCareerList = res?.data?.careers || res?.careers || res?.data || [];

      this.careersList =
        this.currentPageNume === 1
          ? newCareerList
          : [...this.careersList, ...newCareerList];

      this.careersList.sort((a: any, b: any) => {
        return new Date(b.createdAt || b.date || 0).getTime() - new Date(a.createdAt || a.date || 0).getTime();
      });

      this.careerTable = true;
      this.iicContact = false;
      this.careerLength = res?.data?.totalApplications ?? res?.totalApplications ?? newCareerList.length;
      this.totalLeads = this.careerLength;

      // Sync filteredLeads so the non-IIC-LAB table (project === 'ALL' etc.) also renders career leads
      this.filteredLeads = this.careersList;
      this.showNoDataMessage = this.filteredLeads.length === 0;
    });
  }

  goToKanban() {
    this.showCanvasUI = true;
    this.selectedLeadForDetails = null;

    const moduleRoute = this.getModuleRouteByProject(this.project);
    this.router.navigate([`/lead-track/${moduleRoute}`], { queryParams: { view: 'kanban' } });
  }

  goToKanbanClosingPool() {
    this.showCanvasUI = true;
    this.selectedLeadForDetails = null;

    const moduleRoute = this.getModuleRouteByProject(this.project);
    this.router.navigate([`/lead-track/${moduleRoute}`], { queryParams: { view: 'kanban', column: 'closing-pool' } });

    setTimeout(() => {
      const el = document.getElementById('drop-list-6');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }, 500);
  }


  singleLead: any;
  selectedSection: 'Contact Us' | 'Newsletter' | 'Career' | 'IVR' | 'Whatsapp' | 'Email' | 'ALL' | 'Ads' = 'ALL';
  newsLetter: any = [];
  newsLetterTable = false;
  iicContact = true;
  singleLeadDetails(leadId: number): void {
    this.resetModalState();

    let sourceList: any[] = [];

    switch (this.selectedSection) {
      case 'Contact Us':
      case 'IVR':
      case 'Whatsapp':
      case 'Email':
      case 'Ads':
      case 'ALL':
        sourceList = this.filteredLeads;
        break;


      case 'Newsletter':
        sourceList = this.newsLetter;
        break;

      case 'Career':
        sourceList = this.careersList;
        break;
    }

    this.singleLead = sourceList.find(x => x._id === leadId);

    if (!this.singleLead) return;

    this.selectedAssignee = this.salesUsers.find(
      (user) => user.userName === this.singleLead?.assignedToString
    ) || null;

    const value = this.singleLead?.customerValue;

    this.selectedBadge = [
      this.badgeOptions.find(b => b.value === value)
    ];

    this.statusnote = this.singleLead.description || "";
    this.initialBadgeValue = value || "";
    this.initialNotesValue = this.singleLead.description || "";

    if (this.singleLead?.duration) {
      const dur = Number(this.singleLead.duration);
      if (!isNaN(dur) && dur > 0) {
        this.audioDuration = dur;
      }
    }

    console.log(this.singleLead, "single");
  }

  selectedStatus: any[] = [];
  statusSelected: any;
  statusnote: string = "";
  lossReason: string = "";
  initialBadgeValue: string = "";
  initialNotesValue: string = "";
  resetModalState() {
    this.selectedBadge = [];
    this.selectedUserForLead = "";
    this.statusnote = "";
    this.lossReason = "";
    this.initialBadgeValue = "";
    this.initialNotesValue = "";
    this.currentAudioTime = 0;
    this.audioDuration = 0;
    this.isPlayingAudio = false;
    this.isAudioMuted = false;
    this.selectedAudioSpeed = 1;
    this.isDraggingAudio = false;
    const player = document.getElementById('ivrRecording') as HTMLAudioElement;
    if (player) {
      player.pause();
      player.currentTime = 0;
    }
  }

  filteredLeads: any[] = [];
  showNoSourceDataMessage: boolean = false;
  selectedCallFilter: string = "ALL";
  filterLeads() {
    let results = this.leads;

    if (this.selectedType !== "ALL") {
      if (this.selectedType === "Outbound" || this.selectedType === "OUTBOUND") {
        results = results.filter(
          (lead) => lead.leadType === "Outbound" || lead.leadType === "OUTBOUND" || lead.source === "Outbound" || lead.project === "OUTBOUND"
        );
      } else {
        results = results.filter(
          (lead) => lead.source === this.selectedType ||
            lead.projectTab === this.selectedType ||
            (lead.source && lead.source.toLowerCase() === this.selectedType.toLowerCase()) ||
            (lead.projectTab && lead.projectTab.toLowerCase() === this.selectedType.toLowerCase())
        );
      }
    }

    if (this.selectedCallFilter !== "ALL") {
      if (this.selectedCallFilter === "Not answered") {
        results = results.filter(lead => lead.callType === "Notanswered");
      } else {
        results = results.filter(lead => lead.direction === this.selectedCallFilter && lead.callType !== "Notanswered");
      }
    }

    if (this.selectedAssignedUser && this.selectedAssignedUser !== "allUsers") {
      const selectedUserObj = (this.iicUsers || []).find(u => String(u.id) === String(this.selectedAssignedUser) || String(u.username).toLowerCase().trim() === String(this.selectedAssignedUser).toLowerCase().trim());
      const targetId = selectedUserObj ? selectedUserObj.id : this.selectedAssignedUser;
      const targetName = (selectedUserObj ? selectedUserObj.username : String(this.selectedAssignedUser)).toLowerCase().trim();

      results = results.filter(lead => {
        const assignedToId = lead.assignedTo?._id || lead.assignedTo?.userId || lead.assignedTo?.id;
        const assignedToName = (lead.assignedTo?.userName || lead.assignedTo?.username || lead.assignedToString || "").toLowerCase().trim();

        const followUpId = lead.followUpAssignedTo?._id || lead.followUpAssignedTo?.userId || lead.followUpAssignedTo?.id || lead.followUpAssignedTo?.userName;
        const followUpName = (lead.followUpAssignedTo?.userName || lead.followUpAssignedTo?.username || lead.followUpAssignedTo?.assigneeName || "").toLowerCase().trim();

        let arrayMatch = false;
        if (Array.isArray(lead.assignedTo)) {
          arrayMatch = lead.assignedTo.some((a: any) =>
            (a._id && String(a._id) === String(targetId)) ||
            (a.userId && String(a.userId) === String(targetId)) ||
            (a.userName && a.userName.toLowerCase().trim() === targetName) ||
            (a.assigneeName && a.assigneeName.toLowerCase().trim() === targetName)
          );
        }

        const idMatch = (assignedToId && String(assignedToId) === String(targetId)) ||
          (followUpId && String(followUpId) === String(targetId));

        const nameMatch = Boolean(targetName && (assignedToName.includes(targetName) || followUpName.includes(targetName)));

        return idMatch || nameMatch || arrayMatch;
      });
    }

    this.filteredLeads = results;

    if (this.filteredLeads.length === 0) {
      this.showNoSourceDataMessage = true;
    } else {
      this.showNoSourceDataMessage = false;
    }

    this.filteredLeads.forEach((lead) => {
      lead.assignedToString = Array.isArray(lead.assignedTo)
        ? lead.assignedTo.map((a) => a.userName).join(", ")
        : lead.assignedTo?.userName || "";
    });
  }

  onCallFilterChange() {
    this.filterLeads();
  }

  searchCareerDebounce: any;
  onSearchCareerChange() {
    clearTimeout(this.searchCareerDebounce);
    this.searchCareerDebounce = setTimeout(() => {
      this.currentPageNume = 1;
      this.getCareer();
    }, 500);
  }

  onClearCareerSearch() {
    this.searchCareerQuery = "";
    this.getCareer();
  }

  onSectionChange(section: 'ALL' | 'Contact Us' | 'Newsletter' | 'Career' | 'IVR' | 'Whatsapp' | 'Email' | 'Ads') {
    this.selectedSection = section as any;
    this.currentPageNume = 1;

    if (section === "ALL") {
      this.selectedType = "ALL";
      this.getLeadsListingDetail();

    } else if (section === "Contact Us") {

      this.selectedType = "Contact Us";
      this.getLeadsListingDetail();

    } else if (section === "Newsletter") {
      this.selectedType = "Newsletter";
      this.newsLetter = [];
      this.currentPageNume = 1;
      this.getNewsLetter();

    } else if (section === "Career") {
      this.getCareer();

    } else if (section === "IVR") {
      this.selectedType = "IVR";
      this.getLeadsListingDetail();

    } else if (section === "Whatsapp") {
      this.selectedType = "Whatsapp";
      this.getLeadsListingDetail();

    } else if (section === ("Email" as any)) {
      this.selectedType = "Email";
      this.getLeadsListingDetail();

    } else if (section === ("Ads" as any)) {
      this.selectedType = "Ads";
      this.getLeadsListingDetail();

    } else if (section === ("Outbound" as any)) {
      this.selectedType = "Outbound";
      this.getLeadsListingDetail();
    }
  }

  // getIVR() {
  //   this.isSpinnerLoader = true;
  //   this.crmService.getLeadsListingDetail().subscribe((res: any) => {
  //     this.ivr = res.data || [];
  //     this.isSpinnerLoader = false;
  //   });
  // }

  salesUsers: any[] = [];
  selectedLead: any = null;
  selectedAssigneeId: string = "";
  syncIicUsers() {
    this.iicUsers = (this.salesUsers || []).map((u: any) => ({
      id: u._id || u.id || u.userId,
      username: u.userName || u.username || u.firstName || "",
      profileImg: u.profileImg || "assets/default-user.svg",
    }));
  }

  getSalesUserList() {
    this.isAssignableUsersLoading = true;
    this.userService.getUsersByDepartment('SALES,ASSISTANT-SALES').pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isAssignableUsersLoading = false;
        this.changeDetector.markForCheck();
      })
    ).subscribe((res: any) => {
      const rawUsers = (res && (res.data || res)) || [];

      const map = new Map<string, any>();
      const pushToMap = (u: any) => {
        if (!u) return;
        const id = u._id || u.id || u.userId;
        const name = u.userName || u.username || u.firstName || "";
        if (!id && !name) return;

        const key = String(name || id).toLowerCase().trim();
        if (!map.has(key)) {
          map.set(key, {
            ...u,
            _id: id || name,
            userName: name || id
          });
        }
      };

      if (Array.isArray(rawUsers)) {
        rawUsers.forEach(pushToMap);
      }

      this.salesUsers = Array.from(map.values());
      this.syncIicUsers();
      this.changeDetector.markForCheck();
    }, err => {
      console.error("Error fetching department users:", err);
    });
  }
  selectedAssignee: any = null;
  onAssigneeSelect(user: any) {
    this.selectedAssignee = user;
  }

  get shouldShowUnassignButton(): boolean {
    if (!this.singleLead) return false;
    if (this.isLeadUnassigned(this.singleLead)) return false;

    const currentlyAssignedUser = (
      this.singleLead.assignedToString ||
      this.singleLead.followUpAssignedTo?.userName ||
      this.singleLead.assignedTo?.userName ||
      ""
    ).toLowerCase().trim();

    if (!this.selectedAssignee) {
      return !!currentlyAssignedUser;
    }

    const selectedUserName = (
      this.selectedAssignee.userName ||
      this.selectedAssignee.username ||
      ""
    ).toLowerCase().trim();

    return selectedUserName === currentlyAssignedUser;
  }

  get hasBadgeOrNotesChanged(): boolean {
    if (!this.singleLead) return false;

    const initialBadge = (this.initialBadgeValue || "").toLowerCase().trim();
    const currentBadgeObj = (this.selectedBadge && this.selectedBadge.length > 0)
      ? this.selectedBadge.find((b: any) => !!b)
      : null;
    const currentBadge = (currentBadgeObj?.value || "").toLowerCase().trim();

    const initialNote = (this.initialNotesValue || "").trim();
    const currentNote = (this.statusnote || "").trim();

    return initialBadge !== currentBadge || initialNote !== currentNote;
  }

  get shouldShowUpdateButton(): boolean {
    if (!this.singleLead) return false;
    if (this.isLeadUnassigned(this.singleLead)) return false;
    if (!this.shouldShowUnassignButton) return false;
    return this.hasBadgeOrNotesChanged;
  }

  updateLeadBadgeAndNotes() {
    if (!this.singleLead || !this.singleLead._id) return;

    const lead = this.singleLead;
    const id = lead._id;

    const initialBadge = (this.initialBadgeValue || "").toLowerCase().trim();
    const currentBadgeObj = (this.selectedBadge && this.selectedBadge.length > 0)
      ? this.selectedBadge.find((b: any) => !!b)
      : null;
    const currentBadge = (currentBadgeObj?.value || "").toLowerCase().trim();

    const initialNote = (this.initialNotesValue || "").trim();
    const currentNote = (this.statusnote || "").trim();

    const hasBadgeChanged = initialBadge !== currentBadge;
    const hasNotesChanged = initialNote !== currentNote;

    if (!hasBadgeChanged && !hasNotesChanged) {
      return;
    }

    const resolvedProjectTab = this.getLeadProjectTab(lead);
    const resolvedProject = (this.project === 'ALL' && lead?.project)
      ? lead.project
      : this.project;

    const isOutbound = this.project === 'OUTBOUND' || (lead && (lead.leadType === 'Outbound' || lead.leadType === 'OUTBOUND' || lead.project === 'OUTBOUND' || lead.source === 'Outbound' || lead.projectTab === 'Outbound')) || resolvedProject === 'OUTBOUND';

    const redirectUrl = this.getKanbanRedirectUrlByProject(resolvedProject);

    const body: any = {
      leadId: id,
      redirectUrl: redirectUrl
    };

    if (isOutbound) {
      body.leadType = 'Outbound';
    } else {
      if (this.isRealProject(resolvedProject)) {
        body.project = resolvedProject;
        body.projectTab = resolvedProjectTab;
      }
    }

    if (hasBadgeChanged && currentBadgeObj?.value) {
      body.customerValue = currentBadgeObj.value;
    }

    if (hasNotesChanged) {
      body.description = currentNote;
    }

    console.log("UPDATE BADGE/NOTES PAYLOAD:", body);
    this.isSpinnerLoader = true;

    this.crmService.updateLeadStatus(body).subscribe({
      next: (res: any) => {
        this.isSpinnerLoader = false;
        alertModalWithoutConfirm("success", "Successfully Updated");
        this.refreshLeadDetailsAfterUpdate(id, lead);
      },
      error: (err: any) => {
        console.error("Update Error", err);
        this.isSpinnerLoader = false;
        alertModalWithoutConfirm("error", this.serverMessage(err, "Failed to update details"));
      }
    });
  }

  refreshLeadDetailsAfterUpdate(id: any, lead: any) {
    try {
      const jquery = (window as any).$ || (typeof $ !== 'undefined' ? $ : null);
      if (jquery) {
        jquery('#contactLeadDetail').modal('hide');
        jquery('.modal').modal('hide');
        jquery('.modal-backdrop').remove();
        jquery('body').removeClass('modal-open');
      }
    } catch (e) {
      console.error("Error closing modal:", e);
    }

    const currentBadgeObj = (this.selectedBadge && this.selectedBadge.length > 0)
      ? this.selectedBadge.find((b: any) => !!b)
      : null;
    if (currentBadgeObj?.value) {
      this.initialBadgeValue = currentBadgeObj.value;
      if (this.singleLead) this.singleLead.customerValue = currentBadgeObj.value;
      this.syncLeadInAllColumns({ _id: id, customerValue: currentBadgeObj.value, badge: currentBadgeObj.value });
    } else {
      this.initialBadgeValue = "";
      if (this.singleLead) this.singleLead.customerValue = "";
    }

    this.initialNotesValue = this.statusnote || "";
    if (this.singleLead) this.singleLead.description = this.statusnote || "";

    this.getAdminOverView();
    if (this.selectedSection === 'Newsletter') {
      this.getNewsLetter();
    } else if (this.selectedSection === 'Career') {
      this.getCareer();
    } else {
      this.getLeadsListingDetail();
    }
    if (this.showCanvasUI) {
      this.loadAllColumns();
    }

    if (id) {
      this.getSingleLeadById(id, lead?.project || this.project, lead);
    }
    this.changeDetector.markForCheck();
  }

  selectedAssignedUser: string = "allUsers";

  onAssignedUserChange(value: string) {
    console.log("Assigned filter value:", value);

    this.currentPageNume = 1;
    this.leads = [];

    if (value === "allUsers") {
      this.selectedAssignedUser = "allUsers";
      this.isUnassignedFilter = false;
      this.isDeletedFilter = false;
      if (this.selectedOptionStatus === 'unassigned' || this.selectedOptionStatus === 'deleted') {
        this.selectedOptionStatus = '';
      }
    }
    else if (value === "unassigned") {
      this.selectedAssignedUser = "allUsers";
      this.isUnassignedFilter = true;
      this.isDeletedFilter = false;
      this.selectedOptionStatus = 'unassigned';
    }
    else if (value === "deleted" || value === "unqualified") {
      this.selectedAssignedUser = "allUsers";
      this.isDeletedFilter = true;
      this.isUnassignedFilter = false;
      this.selectedOptionStatus = '';
    }
    else {
      this.selectedAssignedUser = value;
      this.isUnassignedFilter = false;
      this.isDeletedFilter = false;
      if (this.selectedOptionStatus === 'unassigned' || this.selectedOptionStatus === 'deleted') {
        this.selectedOptionStatus = '';
      }
    }

    if (this.selectedSection === 'Newsletter') {
      this.getNewsLetter();
      return;
    }

    if (this.selectedSection === 'Career') {
      this.getCareer();
      return;
    }

    if (this.selectedSection === 'IVR') {
      this.getLeadsListingDetail();
      return;
    }

    this.getLeadsListingDetail();
  }



  resetUnqualified(event: Event) {
    this.resetDeletedFilter(event);
  }

  restoreLead(lead: any) {

    const resolvedProject = (this.project === 'ALL' && lead?.project)
      ? lead.project
      : this.project;
    const isOutbound = this.project === 'OUTBOUND' || (lead && (lead.leadType === 'Outbound' || lead.leadType === 'OUTBOUND' || lead.project === 'OUTBOUND' || lead.source === 'Outbound' || lead.projectTab === 'Outbound')) || resolvedProject === 'OUTBOUND';

    const payload: any = {
      leadId: lead._id
    };

    if (isOutbound) {
      payload.leadType = 'Outbound';
    } else {
      if (this.isRealProject(resolvedProject)) {
        payload.project = resolvedProject;
        // Only for IIC-LAB
        if (resolvedProject === 'IIC-LAB' && lead.source) {
          payload.projectTab = lead.source;
        }
      }
    }

    this.crmService.restoreLead(payload).subscribe(
      () => {
        console.log('Lead restored successfully');
        this.getLeadsListingDetail();
      },
      (err) => {
        console.error('Restore failed', err);
      }
    );
  }

  restoreIICLead(leadId: string) {
    let source: 'Career' | 'Newsletter';

    if (this.selectedSection === 'Career') {
      source = 'Career';
    } else if (this.selectedSection === 'Newsletter') {
      source = 'Newsletter';
    } else {
      return;
    }



    this.crmService.restoreIICLabLead(source, leadId).subscribe(
      () => {
        console.log('Entry restored successfully');
        if (source === 'Career') {
          this.getCareer();
        } else {
          this.getNewsLetter();
        }
      },
      (err) => {
        console.error('Restore failed', err);
      }
    );
  }

  dispositions: Disposition[] = [];
  get sortedDispositions() {
    return [...this.dispositions].sort((a, b) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }

  isClosingPoolAdded(item: any): boolean {
    return item?.hasClosingPotential == true || item?.hasClosingPotential == 'true' || item?.status === 'Closing Pool' || item?.stageStatus === 'Closing Pool';
  }

  get isLeadInClosingPool(): boolean {
    const lead = this.selectedLead || this.selectedLeadForDetails || this.singleLead;
    if (!lead) return false;
    return lead.hasClosingPotential === true ||
      lead.hasClosingPotential === 'true' ||
      lead.status === 'Closing Pool' ||
      lead.stageStatus === 'Closing Pool';
  }

  getLeadStageStatus(item: any): string {
    if (item?.status && item.status !== 'Closing Pool') return item.status;
    if (item?.prevStatus && item.prevStatus !== 'Closing Pool') return item.prevStatus;
    const lead = this.singleLead || this.selectedLeadForDetails || this.selectedLead;
    if (lead?.status && lead.status !== 'Closing Pool') return lead.status;
    if (lead?.prevStatus && lead.prevStatus !== 'Closing Pool') return lead.prevStatus;
    if (lead?.stageStatus && lead.stageStatus !== 'Closing Pool') return lead.stageStatus;
    return 'Not started';
  }

  getBadgeClass(value: string) {
    switch (value?.toLowerCase()) {
      case "premium":
        return "badge-premium";
      case "emerging":
        return "badge-emerging";
      case "regular":
        return "badge-regular";
      case "basic":
        return "badge-basic";
      default:
        return "";
    }
  }

  iicUsers: any[] = [];
  isAssignableUsersLoading = false;
  onAssigneeFilterOpened(opened: boolean): void {
    if (opened && (!this.iicUsers.length || !this.salesUsers.length) && !this.isAssignableUsersLoading) {
      this.getSalesUserList();
    }
  }

  getALLUsersList() {
    this.getSalesUserList();
  }

  getModuleRouteByProject(project?: string): string {
    const p = (project || this.project || 'ALL').toUpperCase().trim();
    if (p.includes('IIC')) return 'iic-leads';
    if (p.includes('ESTATE')) return 'vestate-leads';
    if (p.includes('RETAIL')) return 'vretail-leads';
    if (p.includes('OUTBOUND')) return 'outbound-leads';
    return 'all-leads';
  }

  getKanbanRedirectUrlByProject(project: string): string {
    const path = this.getModuleRouteByProject(project);

    return `${environment.redirectUrl}#/lead-track/${path}?view=kanban`;
  }

  followUpTasks: any[] = [];
  followUpTaskAssigneeDropdownOpen: boolean = false;
  selectedFollowUpTaskAssignee: any = null;
  followUpTaskDescription: string = '';
  isSubmittingFollowUpTask: boolean = false;

  toggleFollowUpTaskAssigneeDropdown(event: Event): void {
    event.stopPropagation();
    this.followUpTaskAssigneeDropdownOpen = !this.followUpTaskAssigneeDropdownOpen;
  }

  selectFollowUpTaskAssignee(user: any, event: Event): void {
    event.stopPropagation();
    this.selectedFollowUpTaskAssignee = user;
    this.followUpTaskAssigneeDropdownOpen = false;
  }

  clearFollowUpTaskAssignee(event: Event): void {
    event.stopPropagation();
    this.selectedFollowUpTaskAssignee = null;
    this.followUpTaskAssigneeDropdownOpen = false;
  }

  submitFollowUpTask(): void {
    if (this.hasActiveFollowUpTask) {
      alertModalWithoutConfirm('warning', 'An active follow-up task already exists for this lead.');
      return;
    }

    if (!this.selectedFollowUpTaskAssignee || !this.followUpTaskDescription?.trim()) {
      return;
    }

    const selectedLeadObj = (this as any).selectedLeadForDetails || (this as any).selectedLead || (this as any).singleLead;
    const leadId = selectedLeadObj?._id || selectedLeadObj?.id;

    if (!leadId) {
      alertModalWithoutConfirm('error', 'Lead ID not found.');
      return;
    }

    this.isSubmittingFollowUpTask = true;

    const leadObj = selectedLeadObj || {};
    const projectVal = selectedLeadObj?.project || this.getActualLeadProject(leadObj) || this.project || 'V-RETAIL';
    const leadTypeVal = selectedLeadObj?.leadType || (this.project === 'OUTBOUND' ? 'Outbound' : 'Inbound');

    const redirectUrl = this.getKanbanRedirectUrlByProject(projectVal);

    const payload: any = {
      leadId: leadId,
      project: projectVal,
      leadType: leadTypeVal,
      taskDescription: this.followUpTaskDescription.trim(),
      redirectUrl: redirectUrl,
      assignee: {
        userId: this.selectedFollowUpTaskAssignee?._id || this.selectedFollowUpTaskAssignee?.userId || this.selectedFollowUpTaskAssignee?.id || '',
        userName: this.selectedFollowUpTaskAssignee?.userName || this.selectedFollowUpTaskAssignee?.username || this.selectedFollowUpTaskAssignee?.name || ''
      }
    };

    const rawTab = selectedLeadObj?.source || selectedLeadObj?.sourceLead || selectedLeadObj?.projectTab;
    if (rawTab && rawTab !== 'ALL') {
      payload.projectTab = rawTab;
    }

    this.crmService.createFollowUpTask(payload).subscribe({
      next: (res: any) => {
        this.isSubmittingFollowUpTask = false;
        const newDescription = this.followUpTaskDescription.trim();
        const newAssignee = { ...this.selectedFollowUpTaskAssignee };
        this.followUpTaskDescription = '';
        this.selectedFollowUpTaskAssignee = null;

        const createdTask = res?.data || {
          _id: res?.taskId || ('task_' + Date.now()),
          leadId: leadId,
          taskDescription: newDescription,
          assignee: newAssignee,
          isCompleted: false,
          createdAt: new Date().toISOString()
        };
        if (!this.followUpTasks) {
          this.followUpTasks = [];
        }
        this.followUpTasks.push(createdTask);

        alertModalWithoutConfirm('success', 'Follow up task created successfully!');
        this.updateRemindersHasNextTaskScheduled(leadId);
        this.fetchFollowUpReminders();
        this.fetchAllReminderCounts();
        this.getSingleLeadById(leadId, rawTab || selectedLeadObj?.source, projectVal);
      },

      error: (err: any) => {
        this.isSubmittingFollowUpTask = false;
        console.error('Error creating follow up task:', err);
        alertModalWithoutConfirm('error', err?.error?.message || 'Failed to create follow up task.');
      }
    });
  }


  getLeadProjectTab(lead: any): string {
    const proj = (lead?.project || this.project || '').toUpperCase();
    const source = lead?.source || lead?.sourceLead || lead?.projectTab;
    if (source) {
      const srcStr = String(source).trim();
      if (srcStr.toLowerCase() === 'contact us' || srcStr.toLowerCase() === 'website') {
        return 'Contact Us';
      }
      if (srcStr.toLowerCase() === 'ads') {
        return 'Ads';
      }
      return srcStr;
    }
    if (proj === 'V-ESTATE' || proj === 'IIC-LAB' || proj === 'IIC') {
      return 'IVR';
    }
    return this.selectedSection || '';
  }

  getAssignedOnDate(dateStr: any): string {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "-";

    const months = [
      "Jan", "Feb", "Mar", "Apr", "May", "June",
      "July", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const monthName = months[date.getMonth()];
    const day = date.getDate();
    const formattedDate = `${monthName} ${day}`;

    const today = new Date();
    const d1 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const d2 = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffTime = d1.getTime() - d2.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return `${formattedDate} (${diffDays}days ago)`;
    }
    return formattedDate || "-";
  }

  updateStatus(id: string) {
    const lead = (this.singleLead && this.singleLead._id == id)
      ? this.singleLead
      : this.filteredLeads.find(l => l._id == id);
    const resolvedProjectTab = this.getLeadProjectTab(lead);
    // In ALL mode use the lead's own project; otherwise use this.project
    const resolvedProject = (this.project === 'ALL' && lead?.project)
      ? lead.project
      : this.project;

    const isOutbound = this.project === 'OUTBOUND' || (lead && (lead.leadType === 'Outbound' || lead.leadType === 'OUTBOUND' || lead.project === 'OUTBOUND' || lead.source === 'Outbound' || lead.projectTab === 'Outbound')) || resolvedProject === 'OUTBOUND';

    const redirectUrl = this.getKanbanRedirectUrlByProject(resolvedProject);

    const body: any = {
      leadId: id,
      status: this.selectedStatus[0],
      description: this.statusnote || "",
      redirectUrl: redirectUrl
    };

    if (isOutbound) {
      body.leadType = 'Outbound';
    } else {
      if (this.isRealProject(resolvedProject)) {
        body.project = resolvedProject;
        body.projectTab = resolvedProjectTab;
      }
    }

    if (this.selectedBadge[0]?.value) {
      body.customerValue = this.selectedBadge[0].value;
    }

    console.log("FINAL STATUS PAYLOAD:", body);

    this.isSpinnerLoader = true;

    this.crmService.updateLeadStatus(body).subscribe(
      (res) => {
        this.updateRemindersHasNextTaskScheduled(id);
        if (this.selectedAssignee) {
          const isOutbound = this.project === 'OUTBOUND' || (lead && (lead.leadType === 'Outbound' || lead.leadType === 'OUTBOUND' || lead.project === 'OUTBOUND' || lead.source === 'Outbound' || lead.projectTab === 'Outbound')) || resolvedProject === 'OUTBOUND';
          const assignBody: any = {
            userId: this.selectedAssignee._id,
            userName: this.selectedAssignee.userName,
            leadId: id,
            redirectUrl: this.getKanbanRedirectUrlByProject(resolvedProject)
          };

          if (isOutbound) {
            assignBody.leadType = 'Outbound';
          } else {
            if (this.isRealProject(resolvedProject)) {
              assignBody.project = resolvedProject;
              assignBody.projectTab = resolvedProjectTab;
            }
          }
          console.log("ASSIGN PAYLOAD:", assignBody);
          this.crmService.assignLead(assignBody).subscribe(
            (assignRes) => {
              console.log("Assign Success");
              alertModalWithoutConfirm("success", "Successfully Assigned");
              this.getAdminOverView();
              if (this.selectedSection === 'Newsletter') {
                this.getNewsLetter();
              } else if (this.selectedSection === 'Career') {
                this.getCareer();
              } else {
                this.getLeadsListingDetail();
              }
            },
            (assignErr) => {
              console.error("Assign Error", assignErr);
              this.isSpinnerLoader = false;
              alertModalWithoutConfirm("error", this.serverMessage(assignErr, "Failed to assign lead"));
            }
          );
        } else {
          this.getAdminOverView();
          if (this.selectedSection === 'Newsletter') {
            this.getNewsLetter();
          } else if (this.selectedSection === 'Career') {
            this.getCareer();
          } else {
            this.getLeadsListingDetail();
          }
        }
      },
      (err) => {
        console.error("Status Error", err);
        this.isSpinnerLoader = false;
        alertModalWithoutConfirm("error", this.serverMessage(err, "Failed to update status"));
      }
    );
  }

  async unassignLead(lead: any) {
    if (!lead || !lead._id) return;

    const confirmed = await alertModalConfirm("Are you sure you want to unassign this lead?");
    if (!confirmed) return;

    const resolvedProject = (this.project === 'ALL' && lead?.project) ? lead.project : this.project;
    const resolvedProjectTab = this.getLeadProjectTab(lead);
    const isOutbound = this.project === 'OUTBOUND' || (lead && (lead.leadType === 'Outbound' || lead.leadType === 'OUTBOUND' || lead.project === 'OUTBOUND' || lead.source === 'Outbound' || lead.projectTab === 'Outbound')) || resolvedProject === 'OUTBOUND';

    const payload: any = {
      leadId: lead._id
    };

    if (isOutbound) {
      payload.leadType = 'Outbound';
    } else {
      if (this.isRealProject(resolvedProject)) {
        payload.project = resolvedProject;
        payload.projectTab = resolvedProjectTab;
      }
    }

    this.isSpinnerLoader = true;
    this.crmService.unassignLead(payload).subscribe({
      next: (res: any) => {
        this.isSpinnerLoader = false;
        alertModalWithoutConfirm("success", "Successfully Unassigned");

        // Clear assignment from lead locally
        lead.assignedTo = null;
        lead.followUpAssignedTo = null;
        lead.assignedToString = "";
        this.selectedAssignee = null;

        // Refresh list
        this.getAdminOverView();
        if (this.selectedSection === 'Newsletter') {
          this.getNewsLetter();
        } else if (this.selectedSection === 'Career') {
          this.getCareer();
        } else {
          this.getLeadsListingDetail();
        }
      },
      error: (err: any) => {
        this.isSpinnerLoader = false;
        console.error("Unassign error", err);
        alertModalWithoutConfirm("error", this.serverMessage(err, "Failed to unassign lead"));
      }
    });
  }

  isLeadUnassigned(lead: any): boolean {
    if (!lead) return true;

    const assigned = lead.assignedTo || lead.followUpAssignedTo;
    if (assigned) {
      if (Array.isArray(assigned)) {
        if (assigned.length > 0 && (assigned[0].userId || assigned[0].userName)) {
          return false;
        }
      } else if (typeof assigned === 'object') {
        if (assigned.userId || assigned.userName) {
          return false;
        }
      } else if (typeof assigned === 'string' && assigned.trim() !== '' && assigned !== '-') {
        return false;
      }
    }

    if (lead.assignedToString && typeof lead.assignedToString === 'string') {
      const trimmed = lead.assignedToString.trim();
      if (trimmed !== '' && trimmed !== '-') {
        return false;
      }
    }

    return true;
  }

  canShiftToIIC(lead: any): boolean {
    if (!lead) return false;
    const src = String(lead?.source || lead?.sourceLead || '').toLowerCase().trim();
    const isAllowedSource = src === 'whatsapp' || src === 'ads';
    const currentProj = (this.project || '').toUpperCase();
    const leadProj = (lead?.project || '').toUpperCase();
    const isCurrentProjectIIC = currentProj === 'IIC-LAB' || currentProj === 'IIC';
    const isLeadProjectIIC = leadProj === 'IIC-LAB' || leadProj === 'IIC';
    return Boolean(isAllowedSource && !isCurrentProjectIIC && !isLeadProjectIIC);
  }

  hasQuickActions(lead: any): boolean {
    if (!lead) return false;
    const isShiftAvailable = this.canShiftToIIC(lead);

    const isQualificationAvailable =
      this.isLeadUnassigned(lead) ||
      lead?.isQualified === false ||
      lead?.status === 'Unqualified';

    return Boolean(isShiftAvailable || isQualificationAvailable);
  }

  toggleLeadQualification(idOrLead: any) {
    const lead = (typeof idOrLead === 'object' && idOrLead !== null)
      ? idOrLead
      : ((this.singleLead && this.singleLead._id == idOrLead)
        ? this.singleLead
        : this.filteredLeads.find(l => l._id == idOrLead));

    if (!lead || !lead._id) return;

    const isCurrentlyUnqualified = lead.isQualified === false || lead.status === 'Unqualified';
    const targetIsQualified = isCurrentlyUnqualified ? true : false;

    // Only unassigned leads can be updated to Unqualified
    if (!isCurrentlyUnqualified && !this.isLeadUnassigned(lead)) {
      alertModalWithoutConfirm("error", "Only unassigned leads can be marked as unqualified");
      return;
    }

    const resolvedProjectTab = this.getLeadProjectTab(lead);
    const resolvedProject = (this.project === 'ALL' && lead?.project)
      ? lead.project
      : this.project;
    const isOutbound = this.project === 'OUTBOUND' || (lead && (lead.leadType === 'Outbound' || lead.leadType === 'OUTBOUND' || lead.project === 'OUTBOUND' || lead.source === 'Outbound' || lead.projectTab === 'Outbound')) || resolvedProject === 'OUTBOUND';

    const redirectUrl = this.getKanbanRedirectUrlByProject(resolvedProject);

    const body: any = {
      leadId: lead._id,
      isQualified: targetIsQualified,
      redirectUrl: redirectUrl
    };

    if (isOutbound) {
      body.leadType = 'Outbound';
    } else {
      if (this.isRealProject(resolvedProject)) {
        body.project = resolvedProject;
        body.projectTab = resolvedProjectTab;
      }
    }

    if (this.selectedBadge[0]?.value || lead.customerValue) {
      body.customerValue = this.selectedBadge[0]?.value || lead.customerValue;
    }

    if (this.statusnote || lead.description) {
      body.description = this.statusnote || lead.description || "";
    }

    console.log("QUALIFICATION STATUS PAYLOAD:", body);
    this.isSpinnerLoader = true;

    this.crmService.updateLeadStatus(body).subscribe(
      (res: any) => {
        this.isSpinnerLoader = false;
        const msg = targetIsQualified ? "Lead marked as Qualified" : "Lead marked as Unqualified";
        alertModalWithoutConfirm("success", msg);

        lead.isQualified = targetIsQualified;
        if (this.singleLead && this.singleLead._id === lead._id) {
          this.singleLead.isQualified = targetIsQualified;
        }

        try {
          const jquery = (window as any).$ || (typeof $ !== 'undefined' ? $ : null);
          if (jquery) {
            jquery('#contactLeadDetail').modal('hide');
            jquery('.modal').modal('hide');
            jquery('.modal-backdrop').remove();
            jquery('body').removeClass('modal-open');
          }
        } catch (e) {
          console.error("Error closing modal:", e);
        }

        if (this.selectedSection === 'Newsletter') {
          this.getNewsLetter();
        } else if (this.selectedSection === 'Career') {
          this.getCareer();
        } else {
          this.getLeadsListingDetail();
        }
      },
      (err) => {
        this.isSpinnerLoader = false;
        console.error("Error updating lead qualification:", err);
        alertModalWithoutConfirm("error", this.serverMessage(err, "Failed to update lead qualification"));
      }
    );
  }

  listenOverviewUpdates() {
    this.chatService.leadOverviewUpdates$.pipe(takeUntil(this.destroy$)).subscribe((socketData) => {
      console.log("DATA", socketData)

      if (!socketData?.data || !Array.isArray(socketData.data)) return;

      let overview;

      if (this.userRole === "ADMIN") {
        overview = socketData.data.find(d => d.role !== "Sales Manager");
      } else {
        overview = socketData.data.find(d => d.role === "Sales Manager");
      }

      if (!overview) return;

      console.log("Selected Overview:", overview);

      this.applyOverviewSocketData(overview);
      this.updateDonutChart();
      this.createRadialChartData();
      this.changeDetector.markForCheck();
    });
  }

  applyOverviewSocketData(res: any) {
    this.totalsLeadsCounts = res.totalLeadsCount;
    this.totalAssignedLeadsCount = res.totalAssignedLeads;
    this.salesUserName = res.userName;
    this.role = res.role;

    this.totalInProgressCount = res.inProgressLeadsCount;
    this.totalConvertedLeadsCount = res.convertedLeadsCount;
    this.totalLostLeadsCount = res.lostLeadsCount;
    this.totalClosingPoolCount = res.closingPoolCount;

    this.progressLeadPercentage = res.inProgressLeadsPercentage;
    this.convertedLeadPercentage = res.convertedLeadsPercentage;
    this.lostLeadsPercentage = res.lostLeadsPercentage;


    this.progressLeadTrend = res.inProgressLeadsTrend;
    this.convertedLeadTrend = res.convertedLeadsTrend;
    this.lostLeadTrend = res.lostLeadsTrend;
    this.totalLeadsTrend = res.totalLeadsTrend;
  }



  selectedUser: any[] = [];
  selectedUserForLead: string = "";
  assignLeadToUser(user: any, lead: any) {
    this.selectedUserForLead = user.username;
    lead.assignedToString = user.username;

    const resolvedProject = (this.project === 'ALL' && lead?.project) ? lead.project : this.project;
    const isOutbound = this.project === 'OUTBOUND' || (lead && (lead.leadType === 'Outbound' || lead.leadType === 'OUTBOUND' || lead.project === 'OUTBOUND' || lead.source === 'Outbound' || lead.projectTab === 'Outbound')) || resolvedProject === 'OUTBOUND';

    const body: any = {
      userId: user.id,
      userName: user.username,
      leadId: lead._id,
    };

    if (isOutbound) {
      body.leadType = 'Outbound';
    } else {
      body.project = lead.project || resolvedProject;
      body.projectTab = this.getLeadProjectTab(lead);
    }

    this.crmService.assignLead(body).subscribe(
      (res) => {
        console.log("Lead assigned", res);
        this.getAdminOverView();
      },
      (err) => console.error(err)
    );
  }

  badgeOptions = [
    { label: "Premium", value: "premium", class: "badge-premium" },
    { label: "Emerging", value: "emerging", class: "badge-emerging" },
    { label: "Regular", value: "regular", class: "badge-regular" },
    { label: "Basic", value: "basic", class: "badge-basic" },
  ];

  selectedBadgeDetails: any = null;
  badgeDropdownOpen = false;

  get isAdmin(): boolean {
    const dept = (this.userDepartment || localStorage.getItem("department") || "").toLowerCase();
    return dept === "admin" || dept === "marketing" || this.userRole === "ADMIN" || this.loggedInUser;
  }

  toggleBadgeDropdown() {
    if (!this.isAdmin) return;
    this.badgeDropdownOpen = !this.badgeDropdownOpen;
    this.changeDetector.markForCheck();
  }

  selectBadgeDetails(badgeValue: string) {
    const badgeObj = this.badgeOptions.find((b) => b.value === badgeValue);
    this.selectedBadgeDetails = badgeObj;
    this.leadForm.patchValue({ customerValue: badgeValue });
    this.leadForm.get('customerValue')?.markAsDirty();
    this.leadForm.get('customerValue')?.markAsTouched();
    this.leadForm.markAsDirty();
    this.badgeDropdownOpen = false;
    this.changeDetector.markForCheck();
  }

  isUpdatingLead = false;
  initialLeadSnapshot: any = {};

  get hasUnsavedChanges(): boolean {
    if (!this.leadForm) return false;
    return (
      this.leadForm.dirty ||
      this.hasLeadValueChanged()
    );
  }

  hasLeadValueChanged(): boolean {
    if (!this.initialLeadSnapshot || Object.keys(this.initialLeadSnapshot).length === 0) {
      return false;
    }
    const current = this.leadForm.getRawValue();
    return Object.keys(current).some(key => {
      const initial = (this.initialLeadSnapshot[key] ?? '').toString().trim();
      const now = (current[key] ?? '').toString().trim();
      return initial !== now;
    });
  }

  getActualLeadProject(lead?: any): string {
    const targetLead = lead || this.singleLead || this.selectedLeadForDetails || this.selectedLead;
    const proj = targetLead?.project || this.singleLead?.project || this.selectedLeadForDetails?.project || this.selectedLead?.project;
    if (proj && proj.toUpperCase() !== 'ALL') {
      return proj.toUpperCase();
    }
    const source = targetLead?.source || targetLead?.sourceLead || targetLead?.projectTab || this.singleLead?.source || this.singleLead?.projectTab;
    if (source) {
      const normalizedSource = String(source).trim().toLowerCase();
      if (normalizedSource === 'outbound') return 'OUTBOUND';
      const vRetailSources = new Set([
        'book demo',
        'contact us',
        'user onboard',
        'lead magnet features',
        'lead magnet book demo',
        'lead magnet pricing',
        'email',
        'website'
      ]);
      const vEstateSources = new Set(['whatsapp', 'email']);
      const iicSources = new Set(['news letter', 'newsletter', 'career']);

      if (vRetailSources.has(normalizedSource)) return 'V-RETAIL';
      if (vEstateSources.has(normalizedSource)) return 'V-ESTATE';
      if (iicSources.has(normalizedSource)) return 'IIC-LAB';
    }
    if (this.project && this.project.toUpperCase() !== 'ALL') {
      return this.project.toUpperCase();
    }
    return 'V-RETAIL';
  }

  buildLeadPayload(): any {
    const leadObj = this.singleLead || this.selectedLeadForDetails || this.selectedLead;
    const isOutboundLead =
      this.project === 'OUTBOUND' ||
      (leadObj && (
        String(leadObj.leadType).toLowerCase() === 'outbound' ||
        String(leadObj.project).toLowerCase() === 'outbound' ||
        String(leadObj.source).toLowerCase() === 'outbound' ||
        String(leadObj.sourceLead).toLowerCase() === 'outbound' ||
        String(leadObj.projectTab).toLowerCase() === 'outbound'
      ));

    const targetLeadId = leadObj?._id || this.selectedLeadForDetails?._id || this.selectedLead?._id || this.singleLead?._id;

    const payload: any = {
      leadId: String(targetLeadId || ''),
    };

    if (isOutboundLead) {
      payload.leadType = 'Outbound';
    } else {
      const targetProject = this.getActualLeadProject(leadObj);
      const rawTab = leadObj?.source || leadObj?.sourceLead || leadObj?.projectTab;
      const projectTab = rawTab || (targetProject === 'IIC-LAB' || targetProject === 'V-ESTATE' ? 'IVR' : undefined);
      payload.project = targetProject;
      if (projectTab) {
        payload.projectTab = projectTab;
      }
    }

    if (this.leadForm) {
      const rawValues = this.leadForm.getRawValue();
      Object.keys(rawValues).forEach(key => {
        const val = rawValues[key];
        if (val !== undefined && val !== null && val !== '') {
          if (key === 'adminNotes') {
            payload.description = val;
          } else if (key === 'customerValue') {
            payload.customerValue = val;
          } else if (key !== 'leadId' && key !== 'userMessage' && key !== 'lossReason') {
            payload[key] = val;
          }
        }
      });
    }

    const badgeVal = this.leadForm.get('customerValue')?.value || this.selectedBadgeDetails?.value;
    if (badgeVal) {
      payload.customerValue = badgeVal;
    }

    const aNotes = this.leadForm.get('adminNotes')?.value;
    if (aNotes !== undefined && aNotes !== null) {
      payload.description = aNotes;
    }

    delete payload.adminNotes;
    delete payload.badge;

    return payload;
  }

  updateLead() {
    if (this.isUpdatingLead) return;
    this.isUpdatingLead = true;

    const payload = this.buildLeadPayload();
    this.crmService.updatefollowUpLead(payload).subscribe({
      next: (res: any) => {
        alertModalWithoutConfirm('success', 'Successfully Saved!');
        const targetLeadId = this.selectedLead?._id || this.selectedLeadForDetails?._id || this.singleLead?._id || payload.leadId;
        const rawForm = this.leadForm ? this.leadForm.getRawValue() : {};
        const badgeVal = rawForm.customerValue || this.selectedBadgeDetails?.value || payload.customerValue;
        const aNotes = rawForm.adminNotes !== undefined && rawForm.adminNotes !== null ? rawForm.adminNotes : payload.description;

        const updatedFields: any = {
          ...rawForm,
          _id: targetLeadId
        };
        if (badgeVal) {
          updatedFields.customerValue = badgeVal;
          updatedFields.badge = badgeVal;
          if (this.selectedLead) {
            this.selectedLead.customerValue = badgeVal;
            this.selectedLead.badge = badgeVal;
          }
          if (this.selectedLeadForDetails) {
            this.selectedLeadForDetails.customerValue = badgeVal;
            this.selectedLeadForDetails.badge = badgeVal;
          }
          if (this.singleLead) {
            this.singleLead.customerValue = badgeVal;
            this.singleLead.badge = badgeVal;
          }
        }
        if (aNotes !== undefined && aNotes !== null) {
          updatedFields.adminNotes = aNotes;
          updatedFields.description = aNotes;
          if (this.selectedLead) {
            this.selectedLead.adminNotes = aNotes;
            this.selectedLead.description = aNotes;
          }
          if (this.selectedLeadForDetails) {
            this.selectedLeadForDetails.adminNotes = aNotes;
            this.selectedLeadForDetails.description = aNotes;
          }
          if (this.singleLead) {
            this.singleLead.adminNotes = aNotes;
            this.singleLead.description = aNotes;
          }
        }
        this.syncLeadInAllColumns(updatedFields);
        this.initialLeadSnapshot = this.leadForm.getRawValue();
        this.leadForm.markAsPristine();
        this.leadForm.markAsUntouched();
        this.isUpdatingLead = false;
        this.changeDetector.markForCheck();
      },
      error: (err: any) => {
        console.error('Update failed', err);
        alertModalWithoutConfirm('error', 'Save failed');
        this.isUpdatingLead = false;
        this.changeDetector.markForCheck();
      }
    });
  }

  selectedBadge: any = [];
  leadId: any;
  selectBadge(badgeValue: string, i: number, id: string) {
    const badgeObj = this.badgeOptions.find((b) => b.value === badgeValue);

    const idx = (typeof i === 'number' && !isNaN(i)) ? i : 0;
    this.selectedBadge[idx] = badgeObj;
    this.selectedBadge[0] = badgeObj;
    this.leadId = id;

    console.log("Selected Badge:", badgeObj, "Lead:", id);
  }

  onScroll() {
    console.log("Scrolled! Current Leads Length:", this.leads.length);
    const totalLoaded = this.leads.length;
    if (totalLoaded >= this.totalLeads) return;
    this.currentPageNume++;
    if (this.selectedSection === 'Newsletter') {
      this.getNewsLetter();
    } else {
      this.getLeadsListingDetail();
    }
  }

  private getProjectForSource(source?: string): string | undefined {
    if (!source) {
      return undefined;
    }

    const normalizedSource = source.trim().toLowerCase();
    const vRetailSources = new Set([
      'book demo',
      'user onboard',
      'lead magnet features',
      'lead magnet book demo',
      'lead magnet pricing',
      'email'
    ]);
    const vEstateSources = new Set(['whatsapp', 'ivr', 'email', 'ads']);
    const iicLabSources = new Set(['news letter', 'newsletter', 'career']);

    if (vRetailSources.has(normalizedSource)) {
      return 'V-RETAIL';
    }

    if (vEstateSources.has(normalizedSource)) {
      return 'V-ESTATE';
    }

    if (iicLabSources.has(normalizedSource)) {
      return 'IIC-LAB';
    }

    if (normalizedSource === 'contact us') {
      return 'V-RETAIL';
    }

    return undefined;
  }

  isUtmApplicable(lead: any): boolean {
    const project = String(lead?.project || this.project || '').trim().toUpperCase();
    const source = String(lead?.source || lead?.projectTab || '').trim().toLowerCase();

    return (project === 'V-ESTATE' && (source === 'book demo' || source === 'contact us' || source === 'email' || source === 'ads')) ||
      ((project === 'IIC-LAB' || project === 'IIC') && (source === 'contact us' || source === 'email' || source === 'ads')) ||
      (project === 'V-RETAIL' && (source === 'ads' || source === 'contact us' || source === 'email'));
  }

  getUtmValue(lead: any, field: 'utmSource' | 'utmMedium' | 'utmContent'): string {
    return this.isUtmApplicable(lead) && lead?.[field] ? lead[field] : '-';
  }

  getUtmTooltip(lead: any, field: 'utmSource' | 'utmMedium' | 'utmContent'): string {
    const val = this.getUtmValue(lead, field);
    return (val && val !== '-') ? val : '';
  }

  onTabChange(newType: string) {
    this.selectedType = newType;
    this.currentPageNume = 1;
    this.leads = [];
    this.careersList = [];
    this.newsLetter = [];

    if (newType === "Career") {
      this.selectedSection = "Career";
      this.careerTable = true;
      this.newsLetterTable = false;
      this.getCareer();
    } else if (newType === "Newsletter") {
      this.selectedSection = "Newsletter";
      this.newsLetterTable = true;
      this.careerTable = false;
      this.getNewsLetter();
    } else {
      this.selectedSection = "ALL";
      this.careerTable = false;
      this.newsLetterTable = false;
      this.getLeadsListingDetail();
    }
  }

  isShimmerLoading: boolean = false;
  shimmerLoadingValue = ([] = [1, 2, 3, 4, 5, 6, 7, 8]);

  // canvas / details
  showCanvasUI = false;
  showDetailsPanel = false;
  assignedLeadsCanvas: any[] = [];
  selectedLeadForDetails: any = null;
  logoPreview: string | null = null;
  totalColumnsToLoad = 8;
  columnsLoaded = 0;
  autoScrollInterval: any = null;
  showLostReason: boolean = false;
  isCanvasLoading: boolean = true;
  fullscreenPreview: {
    url: string;
    type: 'image' | 'video' | 'document';
    fileName: string;
  } | null = null;
  editorBlocks: EditorBlock[] = [
    { id: crypto.randomUUID(), type: 'text', value: '' }
  ];
  emptyColumns: any[] = Array.from({ length: 10 }, (_, i) => ({
    index: i + 1,
    items: []
  }));

  assistantTaskNote: string = '';
  assistantTaskReminderDueAt: string = '';
  todayDate: string = new Date().toISOString().split('T')[0];
  isUpdatingTaskNote: boolean = false;
  isCompletingTask: boolean = false;
  updatedTaskIds: Set<string> = new Set<string>();

  // ================= FOLLOW-UP REMINDERS SECTION ================= //
  activeReminderTab: 'Today' | 'Upcoming' | 'Pending' | 'Completed' = 'Today';
  remindersTabs: Array<'Today' | 'Upcoming' | 'Pending' | 'Completed'> = ['Today', 'Upcoming', 'Pending', 'Completed'];
  selectedReminderUser: string = '';
  selectedReminderUserObj: any = null;
  reminderUserDropdownOpen: boolean = false;
  remindersData: { [key: string]: any[] } = {
    Today: [],
    Upcoming: [],
    Pending: [],
    Completed: []
  };
  reminderCounts: { [key: string]: number } = {
    Today: 0,
    Upcoming: 0,
    Pending: 0,
    Completed: 0
  };
  isRemindersLoading: boolean = false;
  remindersPage: { [key: string]: number } = {
    Today: 1,
    Upcoming: 1,
    Pending: 1,
    Completed: 1
  };
  remindersLimit: number = 20;
  remindersHasMore: { [key: string]: boolean } = {
    Today: true,
    Upcoming: true,
    Pending: true,
    Completed: true
  };
  remindersLoadingMore: boolean = false;

  toggleReminderUserDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.reminderUserDropdownOpen = !this.reminderUserDropdownOpen;
  }

  selectReminderUser(userName: string, event?: Event, userObj?: any) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedReminderUser = userName || '';
    this.selectedReminderUserObj = userObj || null;
    this.reminderUserDropdownOpen = false;
  }

  get displayedReminders(): any[] {
    const list = this.remindersData[this.activeReminderTab] || [];
    if (!this.selectedReminderUser) {
      return list;
    }
    const targetName = this.selectedReminderUser.trim().toLowerCase();
    const targetId = this.selectedReminderUserObj?._id || this.selectedReminderUserObj?.id || this.selectedReminderUserObj?.userId;

    return list.filter((item: any) => {
      if (!item) return false;
      const createdByName = (item.createdBy?.userName || item.createdBy?.username || item.createdBy?.name || item.createdByName || '').toString().trim().toLowerCase();
      const createdById = item.createdBy?._id || item.createdBy?.id || item.createdBy?.userId || item.createdById;
      if (targetName && createdByName === targetName) return true;
      if (targetId && String(createdById) === String(targetId)) return true;

      const assigneeName = (item.assignee?.userName || item.assignee?.username || item.assignee?.name || item.assignedTo?.userName || item.assignedTo?.username || item.assignedTo?.name || item.assigneeName || item.assignedToName || '').toString().trim().toLowerCase();
      const assigneeId = item.assignee?._id || item.assignee?.id || item.assignee?.userId || item.assignedTo?._id || item.assignedTo?.id || item.assignedTo?.userId;
      if (targetName && assigneeName === targetName) return true;
      if (targetId && String(assigneeId) === String(targetId)) return true;

      const assignedByName = (item.assignedBy?.userName || item.assignedBy?.username || item.assignedBy?.name || item.assigner?.userName || item.assigner?.name || item.assignedByName || item.assignerName || item.meta?.assignedBy?.userName || item.meta?.assignedBy?.name || item.meta?.assignedByName || item.meta?.assigner?.userName || item.meta?.assigner?.name || item.meta?.assignerName || '').toString().trim().toLowerCase();
      const assignedById = item.assignedBy?._id || item.assignedBy?.id || item.assignedBy?.userId || item.assigner?._id || item.assigner?.id || item.assigner?.userId || item.assignedById || item.assignerId || item.meta?.assignedBy?._id || item.meta?.assignedBy?.id || item.meta?.assignedById || item.meta?.assigner?._id || item.meta?.assigner?.id || item.meta?.assignerId;
      if (targetName && assignedByName === targetName) return true;
      if (targetId && String(assignedById) === String(targetId)) return true;

      const itemUserName = (item.userName || item.username || '').toString().trim().toLowerCase();
      if (targetName && itemUserName === targetName) return true;

      return false;
    });
  }

  getReminderCount(tab: string): number {
    const list = this.remindersData[tab] || [];
    if (!this.selectedReminderUser) {
      return this.reminderCounts[tab] ?? list.length;
    }
    const targetName = this.selectedReminderUser.trim().toLowerCase();
    const targetId = this.selectedReminderUserObj?._id || this.selectedReminderUserObj?.id || this.selectedReminderUserObj?.userId;

    return list.filter((item: any) => {
      if (!item) return false;
      const createdByName = (item.createdBy?.userName || item.createdBy?.username || item.createdBy?.name || item.createdByName || '').toString().trim().toLowerCase();
      const createdById = item.createdBy?._id || item.createdBy?.id || item.createdBy?.userId || item.createdById;
      if (targetName && createdByName === targetName) return true;
      if (targetId && String(createdById) === String(targetId)) return true;

      const assigneeName = (item.assignee?.userName || item.assignee?.username || item.assignee?.name || item.assignedTo?.userName || item.assignedTo?.username || item.assignedTo?.name || item.assigneeName || item.assignedToName || '').toString().trim().toLowerCase();
      const assigneeId = item.assignee?._id || item.assignee?.id || item.assignee?.userId || item.assignedTo?._id || item.assignedTo?.id || item.assignedTo?.userId;
      if (targetName && assigneeName === targetName) return true;
      if (targetId && String(assigneeId) === String(targetId)) return true;

      const assignedByName = (item.assignedBy?.userName || item.assignedBy?.username || item.assignedBy?.name || item.assigner?.userName || item.assigner?.name || item.assignedByName || item.assignerName || item.meta?.assignedBy?.userName || item.meta?.assignedBy?.name || item.meta?.assignedByName || item.meta?.assigner?.userName || item.meta?.assigner?.name || item.meta?.assignerName || '').toString().trim().toLowerCase();
      const assignedById = item.assignedBy?._id || item.assignedBy?.id || item.assignedBy?.userId || item.assigner?._id || item.assigner?.id || item.assigner?.userId || item.assignedById || item.assignerId || item.meta?.assignedBy?._id || item.meta?.assignedBy?.id || item.meta?.assignedById || item.meta?.assigner?._id || item.meta?.assigner?.id || item.meta?.assignerId;
      if (targetName && assignedByName === targetName) return true;
      if (targetId && String(assignedById) === String(targetId)) return true;

      const itemUserName = (item.userName || item.username || '').toString().trim().toLowerCase();
      if (targetName && itemUserName === targetName) return true;

      return false;
    }).length;
  }

  fetchFollowUpReminders(status?: 'Today' | 'Upcoming' | 'Pending' | 'Completed', isLoadMore = false) {
    const targetStatus = status || this.activeReminderTab;
    if (isLoadMore) {
      if (this.remindersLoadingMore || !this.remindersHasMore[targetStatus]) return;
      this.remindersLoadingMore = true;
      this.remindersPage[targetStatus] = (this.remindersPage[targetStatus] || 1) + 1;
    } else {
      this.isRemindersLoading = true;
      this.remindersPage[targetStatus] = 1;
      this.remindersHasMore[targetStatus] = true;
    }

    const isOutbound = (this.project || '').toUpperCase() === 'OUTBOUND';
    const payload: any = {
      project: this.project,
      page: this.remindersPage[targetStatus],
      limit: this.remindersLimit
    };

    if (targetStatus !== 'Today') {
      payload.status = targetStatus;
    }

    if (isOutbound) {
      payload.leadType = 'Outbound';
    }

    this.crmService.getFollowUpReminders(payload).subscribe({
      next: (res: any) => {
        if (isLoadMore) {
          this.remindersLoadingMore = false;
        } else {
          this.isRemindersLoading = false;
        }

        const resData = res?.data;
        let list: any[] = [];
        let totalCount = 0;

        if (Array.isArray(resData?.followUpReminders)) {
          list = resData.followUpReminders;
          totalCount = resData.totalCount ?? list.length;
        } else if (Array.isArray(resData?.reminders)) {
          list = resData.reminders;
          totalCount = resData.totalCount ?? list.length;
        } else if (Array.isArray(resData?.followUpRemindersData)) {
          list = resData.followUpRemindersData;
          totalCount = resData.totalCount ?? list.length;
        } else if (Array.isArray(resData?.data)) {
          list = resData.data;
          totalCount = resData.totalCount ?? list.length;
        } else if (Array.isArray(resData)) {
          list = resData;
          totalCount = res?.totalCount ?? res?.count ?? list.length;
        } else if (Array.isArray(res?.followUpReminders)) {
          list = res.followUpReminders;
          totalCount = res.totalCount ?? list.length;
        } else if (Array.isArray(res?.reminders)) {
          list = res.reminders;
          totalCount = res.totalCount ?? list.length;
        } else if (Array.isArray(res)) {
          list = res;
          totalCount = list.length;
        }

        if (isLoadMore) {
          this.remindersData[targetStatus] = this.sortRemindersByDate([
            ...(this.remindersData[targetStatus] || []),
            ...list
          ]);
        } else {
          this.remindersData[targetStatus] = this.sortRemindersByDate(list);
        }

        this.reminderCounts[targetStatus] = totalCount || this.remindersData[targetStatus].length;
        if (list.length < this.remindersLimit || (this.remindersData[targetStatus].length >= totalCount && totalCount > 0)) {
          this.remindersHasMore[targetStatus] = false;
        }
      },
      error: (err: any) => {
        if (isLoadMore) {
          this.remindersLoadingMore = false;
        } else {
          this.isRemindersLoading = false;
        }
        console.error(`Error fetching follow-up reminders (${targetStatus}):`, err);
      }
    });
  }

  fetchAllReminderCounts() {
    const isOutbound = (this.project || '').toUpperCase() === 'OUTBOUND';
    this.remindersTabs.forEach(tab => {
      if (tab !== this.activeReminderTab) {
        const payload: any = {
          project: this.project,
          page: 1,
          limit: this.remindersLimit
        };
        if (tab !== 'Today') {
          payload.status = tab;
        }
        if (isOutbound) payload.leadType = 'Outbound';

        this.crmService.getFollowUpReminders(payload).subscribe({
          next: (res: any) => {
            const resData = res?.data;
            let list: any[] = [];
            let total = 0;

            if (Array.isArray(resData?.followUpReminders)) {
              list = resData.followUpReminders;
              total = resData.totalCount ?? list.length;
            } else if (Array.isArray(resData?.reminders)) {
              list = resData.reminders;
              total = resData.totalCount ?? list.length;
            } else if (Array.isArray(resData?.followUpRemindersData)) {
              list = resData.followUpRemindersData;
              total = resData.totalCount ?? list.length;
            } else if (Array.isArray(resData?.data)) {
              list = resData.data;
              total = resData.totalCount ?? list.length;
            } else if (Array.isArray(resData)) {
              list = resData;
              total = res?.totalCount ?? res?.count ?? resData.length;
            } else if (Array.isArray(res?.followUpReminders)) {
              list = res.followUpReminders;
              total = res.totalCount ?? list.length;
            } else if (Array.isArray(res?.reminders)) {
              list = res.reminders;
              total = res.totalCount ?? list.length;
            } else if (Array.isArray(res)) {
              list = res;
              total = list.length;
            } else if (resData?.totalCount !== undefined) {
              total = resData.totalCount;
            } else if (res?.totalCount !== undefined) {
              total = res.totalCount;
            }

            this.reminderCounts[tab] = total || list.length;
            if (list.length > 0 && (!this.remindersData[tab] || this.remindersData[tab].length === 0)) {
              this.remindersData[tab] = this.sortRemindersByDate(list);
              if (list.length < this.remindersLimit || (list.length >= total && total > 0)) {
                this.remindersHasMore[tab] = false;
              }
            }
          },
          error: () => {}
        });
      }
    });
  }

  onReminderTabChange(tab: 'Today' | 'Upcoming' | 'Pending' | 'Completed') {
    this.activeReminderTab = tab;
    this.fetchFollowUpReminders(tab);
  }

  sortRemindersByDate(reminders: any[]): any[] {
    if (!Array.isArray(reminders)) return [];
    return [...reminders].sort((a, b) => {
      const dateA = new Date(a.dueAt || a.reminderDate || a.date || a.createdAt || 0).getTime() || 0;
      const dateB = new Date(b.dueAt || b.reminderDate || b.date || b.createdAt || 0).getTime() || 0;
      return dateA - dateB;
    });
  }

  hasReminderTime(dueAt?: string | Date | null): boolean {
    if (!dueAt) return false;
    const str = String(dueAt).trim();
    if (!str.includes('T') && !str.includes(':')) {
      return false;
    }
    if (str.includes('T00:00:00') || str.includes(' 00:00:00') || str.endsWith('T00:00:00.000Z') || str.endsWith('T00:00:00Z')) {
      return false;
    }
    return true;
  }

  getReminderLeadName(item: any): string {
    if (!item) return 'Lead';
    const lead = item.leadId || item.lead;
    if (typeof lead === 'object' && lead !== null) {
      return lead.name || lead.customerName || lead.companyName || lead.contactName || item.name || item.companyName || 'Lead';
    }
    return item.name || item.companyName || item.customerName || item.leadName || 'Lead';
  }

  getReminderLeadPhone(item: any): string {
    if (!item) return '';
    const lead = item.leadId || item.lead;
    if (typeof lead === 'object' && lead !== null) {
      return lead.phone || lead.phoneNumber || lead.mobile || '';
    }
    return item.phone || item.phoneNumber || item.mobile || '';
  }

  getReminderLeadProject(item: any): string {
    if (!item) return '';
    const lead = item.leadId || item.lead;
    if (typeof lead === 'object' && lead !== null) {
      return lead.project || item.project || '';
    }
    return item.project || '';
  }

  getReminderStatusBadge(status: string): { bg: string; color: string; border: string; icon: string } {
    const s = (status || '').toLowerCase();
    if (s === 'completed') {
      return { bg: '#dcfce7', color: '#15803d', border: '#bbf7d0', icon: 'fas fa-check-circle' };
    }
    if (s === 'upcoming') {
      return { bg: '#e0e7ff', color: '#4338ca', border: '#c7d2fe', icon: 'fas fa-calendar-alt' };
    }
    if (s === 'today') {
      return { bg: '#e0f2fe', color: '#0369a1', border: '#bae6fd', icon: 'fas fa-calendar-day' };
    }
    if (s === 'incomplete') {
      return { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca', icon: 'fas fa-exclamation-circle' };
    }
    return { bg: '#fef3c7', color: '#b45309', border: '#fde68a', icon: 'fas fa-clock' };
  }

  updatingReminderId: string | null = null;

  hasUpcomingTaskScheduled(item: any): boolean {
    if (!item) return false;
    const targetStatus = (
      item?.targetStatus ||
      item?.status ||
      item?.meta?.targetStatus ||
      item?.meta?.status ||
      ''
    ).toString().trim().toLowerCase();

    const isClosed = targetStatus === 'closed' || targetStatus === 'closed won';
    return isClosed || item?.meta?.hasNextTaskScheduled === true || item?.hasNextTaskScheduled === true;
  }

  markReminderAsDone(item: any, event?: Event) {
    if (event) event.stopPropagation();
    if (!item) return;

    // Feature: only allow users to mark a reminder as "Completed" when they already have an upcoming task scheduled or targetStatus is Closed
    const hasNextTask = this.hasUpcomingTaskScheduled(item);

    if (!hasNextTask) {
      const nextStatus = item?.targetStatus || item?.status || 'Next Status';
      alertModalNextTaskReminder(nextStatus).then(() => {
        this.openLeadFromReminder(item);
      });
      return;
    }

    const reminderId = item?._id || item?.id;
    if (!reminderId || this.updatingReminderId === reminderId) return;

    this.updatingReminderId = reminderId;
    const body: any = {
      status: 'Completed'
    };
    if (item.project) body.project = item.project;
    if (item.leadType) body.leadType = item.leadType;
    if (item.projectTab) body.projectTab = item.projectTab;

    this.crmService.updateFollowUpReminder(reminderId, body).subscribe({
      next: (res: any) => {
        this.updatingReminderId = null;
        alertModalWithoutConfirm('success', 'Reminder marked as Completed');

        // Immediate UI Update
        const oldStatus = item.status || this.activeReminderTab;
        item.status = 'Completed';

        if (this.activeReminderTab !== 'Completed') {
          if (this.remindersData[this.activeReminderTab]) {
            this.remindersData[this.activeReminderTab] = this.remindersData[this.activeReminderTab].filter(
              (r: any) => (r._id || r.id) !== reminderId
            );
          }
          this.reminderCounts[this.activeReminderTab] = Math.max(0, (this.reminderCounts[this.activeReminderTab] || 1) - 1);
        }

        if (oldStatus && oldStatus !== this.activeReminderTab && oldStatus !== 'Completed' && this.reminderCounts[oldStatus] !== undefined) {
          this.reminderCounts[oldStatus] = Math.max(0, (this.reminderCounts[oldStatus] || 1) - 1);
        }
        this.reminderCounts['Completed'] = (this.reminderCounts['Completed'] || 0) + 1;

        if (this.remindersData['Completed']) {
          const exists = this.remindersData['Completed'].some((r: any) => (r._id || r.id) === reminderId);
          if (!exists) {
            this.remindersData['Completed'] = [item, ...this.remindersData['Completed']];
          }
        }
      },
      error: (err: any) => {
        this.updatingReminderId = null;
        console.error('Failed to update follow-up reminder:', err);
        const errMsg = err?.error?.message || err?.message || 'Failed to mark reminder as Done';
        alertModalWithoutConfirm('error', errMsg);
      }
    });
  }

  openLeadFromReminder(item: any) {
    if (!item) return;
    const rawLead = item.leadId || item.lead;
    const isLeadObj = typeof rawLead === 'object' && rawLead !== null;
    const leadId = isLeadObj ? (rawLead._id || rawLead.id) : (typeof rawLead === 'string' ? rawLead : (item.lead_id || item._id));
    const proj = (isLeadObj && rawLead.project) ? rawLead.project : (item.project || this.project);
    const source = (isLeadObj && (rawLead.source || rawLead.projectTab)) ? (rawLead.source || rawLead.projectTab) : (item.projectTab || item.source || item.sourceLead || 'Email');

    const initialLeadData: any = {
      _id: leadId,
      id: leadId,
      leadName: (isLeadObj && (rawLead.leadName || rawLead.name || rawLead.customerName || rawLead.contactName)) || item.name || item.leadName || item.customerName || item.contactName || '',
      name: (isLeadObj && (rawLead.name || rawLead.leadName || rawLead.customerName || rawLead.contactName)) || item.name || item.leadName || item.customerName || item.contactName || '',
      companyName: (isLeadObj && rawLead.companyName) || item.companyName || '',
      phoneNo: (isLeadObj && (rawLead.phoneNo || rawLead.phone || rawLead.phoneNumber || rawLead.mobile)) || item.phone || item.phoneNumber || item.mobile || '',
      email: (isLeadObj && rawLead.email) || item.email || '',
      designation: (isLeadObj && rawLead.designation) || item.designation || '',
      status: (isLeadObj && rawLead.status) || item.targetStatus || item.status || '',
      customerValue: (isLeadObj && (rawLead.customerValue || rawLead.badge)) || item.customerValue || item.badge || '',
      project: proj,
      source: source,
      projectTab: source,
      ...(isLeadObj ? rawLead : {})
    };

    if (leadId) {
      const currentSectionProject = this.project || 'ALL';
      const moduleRoute = this.getModuleRouteByProject(currentSectionProject);

      this.showCanvasUI = true;
      this.router.navigate([`/lead-track/${moduleRoute}`], { queryParams: { view: 'kanban' } });
      this.loadAllColumns();
      this.openDetailsPanel(initialLeadData);
    }
  }

  updateRemindersHasNextTaskScheduled(leadId: string) {
    if (!leadId) return;
    const targetLeadId = String(leadId);
    Object.keys(this.remindersData).forEach(tabKey => {
      if (Array.isArray(this.remindersData[tabKey])) {
        this.remindersData[tabKey].forEach((rem: any) => {
          const remLeadId = String(rem?.leadId?._id || rem?.leadId || rem?.lead?._id || rem?.lead || '');
          if (remLeadId === targetLeadId) {
            if (!rem.meta) rem.meta = {};
            rem.meta.hasNextTaskScheduled = true;
            rem.hasNextTaskScheduled = true;
          }
        });
      }
    });
  }

  isFollowUpTaskUpdated(task?: any): boolean {
    const t = task || this.activeFollowUpTask;
    if (!t) return false;
    const taskId = String(t._id || t.id || '');

    if (taskId && this.updatedTaskIds.has(taskId)) {
      return true;
    }

    if (t.note && typeof t.note === 'string' && t.note.trim().length > 0) {
      return true;
    }

    const rawDispositions = this.dispositions || this.singleLead?.dispositionStatus || [];
    if (Array.isArray(rawDispositions) && rawDispositions.length > 0) {
      const hasUpdateEvent = rawDispositions.some((d: any) => {
        const ev = String(d?.event || '').trim().toLowerCase();
        const dTaskId = String(d?.meta?.taskId || d?.taskId || d?._id || '');
        return (ev.includes('task updated') || ev.includes('task note updated') || ev.includes('follow-up updated')) && (!taskId || dTaskId === taskId);
      });
      if (hasUpdateEvent) return true;
    }

    return false;
  }

  get activeFollowUpTask(): any {
    const tasks = this.followUpTasks || this.singleLead?.followUpTasks || this.selectedLead?.followUpTasks || (this as any).selectedLeadForDetails?.followUpTasks || [];
    if (Array.isArray(tasks) && tasks.length > 0) {
      const active = tasks.find((t: any) => {
        if (!t) return false;
        const isDone = t.isCompleted === true || t.isCompleted === 'true' || String(t.status || '').toLowerCase() === 'completed' || String(t.status || '').toLowerCase() === 'done';
        return !isDone;
      });
      if (active) return active;
    }

    const rawDispositions = this.dispositions || this.singleLead?.dispositionStatus || this.selectedLead?.dispositionStatus || [];
    if (Array.isArray(rawDispositions) && rawDispositions.length > 0) {
      const createdTasks = new Map<string, any>();
      const completedTaskIds = new Set<string>();

      rawDispositions.forEach((d: any) => {
        if (!d) return;
        const ev = String(d.event || '').trim().toLowerCase();
        const taskId = String(d.meta?.taskId || d.taskId || d._id || '');
        if (ev.includes('completed')) {
          if (taskId) completedTaskIds.add(taskId);
        } else if (ev.includes('created') || ev.includes('updated')) {
          if (taskId && !createdTasks.has(taskId)) {
            createdTasks.set(taskId, d);
          }
        }
      });

      for (const [tId, d] of createdTasks.entries()) {
        if (!completedTaskIds.has(tId)) {
          return {
            _id: tId,
            id: tId,
            isCompleted: false,
            assignee: d.meta?.assignee || d.assignee,
            taskDescription: d.meta?.taskDescription || d.note || ''
          };
        }
      }
    }

    return null;
  }

  get hasActiveFollowUpTask(): boolean {
    return !!this.activeFollowUpTask;
  }

  get canShowAssistantTaskActionBox(): boolean {
    const task = this.activeFollowUpTask;
    if (!task || task.isCompleted) return false;

    const loggedInId = String(this.userId || localStorage.getItem("guestID") || localStorage.getItem("userId") || localStorage.getItem("_id") || "").trim();
    const loggedInName = String(this.loggedInUserName || localStorage.getItem("username") || localStorage.getItem("userName") || localStorage.getItem("name") || "").trim().toLowerCase();

    if (!loggedInId && !loggedInName) return false;

    const assigneeObj = task.assignee || task.assignedTo || task.followUpAssignedTo;
    if (!assigneeObj) return false;

    const checkMatch = (item: any): boolean => {
      if (!item) return false;
      if (typeof item === 'string') {
        const trimmed = item.trim().toLowerCase();
        if (loggedInId && trimmed === loggedInId.toLowerCase()) return true;
        if (loggedInName && trimmed === loggedInName) return true;
        return false;
      }
      if (typeof item === 'object') {
        const id = String(item.userId || item._id || item.id || '').trim();
        const name = String(item.userName || item.username || item.name || '').trim().toLowerCase();

        if (id && loggedInId && id.toLowerCase() === loggedInId.toLowerCase()) return true;
        if (name && loggedInName && name === loggedInName) return true;
      }
      return false;
    };

    if (Array.isArray(assigneeObj)) {
      return assigneeObj.some(item => checkMatch(item));
    }

    return checkMatch(assigneeObj);
  }

  get formattedFollowUpTaskLogs(): any[] {
    const rawDispositions = this.dispositions || this.singleLead?.dispositionStatus || [];

    if (rawDispositions.length > 0) {
      const logs: any[] = [];
      rawDispositions.forEach((item: any, index: number) => {
        if (!item) return;
        const eventName = (item.event || '').trim().toLowerCase();
        if (
          eventName === 'follow up task created' ||
          eventName === 'follow-up task created' ||
          eventName === 'followup task created' ||
          eventName === 'follow up task updated' ||
          eventName === 'follow-up task updated' ||
          eventName === 'followup task updated' ||
          eventName === 'follow up task completed' ||
          eventName === 'follow-up task completed' ||
          eventName === 'followup task completed'
        ) {
          const meta = item.meta || {};
          const timestamp = item.createdAt || item.updatedAt || meta.createdAt || meta.updatedAt;
          const seqNo = index + 1;

          if (eventName.includes('completed')) {
            logs.push({
              id: item._id,
              seqNo: seqNo,
              taskId: meta.taskId || item._id,
              logType: 'completed',
              timestamp: timestamp,
              assigneeName: meta.assignee?.userName || meta.assignee?.name || item.assignee?.userName || '-',
              note: meta.note || item.note || ''
            });
          } else if (eventName.includes('updated')) {
            logs.push({
              id: item._id,
              seqNo: seqNo,
              taskId: meta.taskId || item._id,
              logType: 'updated',
              timestamp: timestamp,
              assigneeName: meta.assignee?.userName || meta.assignee?.name || item.assignee?.userName || '-',
              note: meta.note || item.note || ''
            });
          } else {
            // Created
            logs.push({
              id: item._id,
              seqNo: seqNo,
              taskId: meta.taskId || item._id,
              logType: 'created',
              timestamp: timestamp,
              assignedByName: meta.assignedBy?.userName || meta.assignedBy?.name || item.assignedBy?.userName || 'Sales',
              assigneeName: meta.assignee?.userName || meta.assignee?.name || item.assignee?.userName || '-',
              note: meta.taskDescription || item.taskDescription || meta.note || item.note || ''
            });
          }
        }
      });

      if (logs.length > 0) {
        return logs.sort((a: any, b: any) => {
          const timeA = new Date(a.timestamp).getTime() || 0;
          const timeB = new Date(b.timestamp).getTime() || 0;
          return timeB - timeA;
        });
      }
    }

    // Fallback: If no disposition task events exist, use followUpTasks array
    if (!this.followUpTasks || !this.followUpTasks.length) return [];
    const fallbackLogs: any[] = [];
    let currentSeq = 1;

    this.followUpTasks.forEach((task: any) => {
      const assigneeName = task.assignee?.userName || task.assignee?.name || '-';
      const assigneeProfileImg = task.assignee?.profileImg || this.salesUsers?.find((u: any) => u.userName === assigneeName)?.profileImg || '';
      const assignedByName = task.assignedBy?.userName || task.assignedBy?.name || 'Sales';
      const assignedByProfileImg = task.assignedBy?.profileImg || this.salesUsers?.find((u: any) => u.userName === assignedByName)?.profileImg || '';

      // 1. Task Created entry
      fallbackLogs.push({
        id: (task._id || task.id) + '_created',
        seqNo: currentSeq++,
        taskId: task._id || task.id,
        isFollowUpTaskEvent: true,
        logType: 'created',
        timestamp: task.createdAt || task.updatedAt,
        assignedByName: assignedByName,
        assignedByProfileImg: assignedByProfileImg,
        assigneeName: assigneeName,
        assigneeProfileImg: assigneeProfileImg,
        note: task.taskDescription || ''
      });

      // 2. Task Updated entry if updated and not completed
      if (task.note && task.note.trim() && !task.isCompleted) {
        fallbackLogs.push({
          id: (task._id || task.id) + '_updated',
          seqNo: currentSeq++,
          taskId: task._id || task.id,
          isFollowUpTaskEvent: true,
          logType: 'updated',
          timestamp: task.updatedAt || task.createdAt,
          assigneeName: assigneeName,
          assigneeProfileImg: assigneeProfileImg,
          note: task.note
        });
      }

      // 3. Task Completed entry
      if (task.isCompleted) {
        fallbackLogs.push({
          id: (task._id || task.id) + '_completed',
          seqNo: currentSeq++,
          taskId: task._id || task.id,
          isFollowUpTaskEvent: true,
          logType: 'completed',
          timestamp: task.updatedAt || task.createdAt,
          assigneeName: assigneeName,
          assigneeProfileImg: assigneeProfileImg,
          note: task.note || ''
        });
      }
    });

    return fallbackLogs.sort((a: any, b: any) => {
      const timeA = new Date(a.timestamp).getTime() || 0;
      const timeB = new Date(b.timestamp).getTime() || 0;
      return timeB - timeA;
    });
  }

  get formattedDispositions(): any[] {
    const rawDispositions = this.dispositions || this.singleLead?.dispositionStatus || [];
    if (!rawDispositions || !rawDispositions.length) return [];

    // 1. Sort raw dispositions chronologically (oldest first: ascending by createdAt / timestamp / updatedAt)
    const sortedChronological = [...rawDispositions].sort((a: any, b: any) => {
      const timeA = new Date(a.createdAt || a.timestamp || a.updatedAt || a.meta?.createdAt || a.meta?.updatedAt || 0).getTime() || 0;
      const timeB = new Date(b.createdAt || b.timestamp || b.updatedAt || b.meta?.createdAt || b.meta?.updatedAt || 0).getTime() || 0;
      return timeA - timeB;
    });

    const mapped = sortedChronological.map((item: any, index: number) => {
      const eventName = (item.event || '').trim().toLowerCase();
      const meta = item.meta || {};
      const timestamp = item.createdAt || item.updatedAt || item.timestamp || meta.createdAt || meta.updatedAt;
      const seqNo = index + 1;

      // 1. Follow Up Task Events
      if (
        eventName === DISPOSITION_EVENT.FOLLOW_UP_TASK_CREATED.toLowerCase() ||
        eventName === DISPOSITION_EVENT.FOLLOW_UP_TASK_UPDATED.toLowerCase() ||
        eventName === DISPOSITION_EVENT.FOLLOW_UP_TASK_COMPLETED.toLowerCase() ||
        eventName.includes('follow up task') ||
        eventName.includes('follow-up task') ||
        eventName.includes('followup task')
      ) {
        const taskId = meta.taskId || item.taskId || item._id;
        const matchedTask = (this.followUpTasks || []).find((t: any) => (t._id || t.id) === taskId);
        const assigneeName = meta.assignee?.userName || meta.assignee?.name || item.assignee?.userName || matchedTask?.assignee?.userName || '-';
        const assigneeProfileImg = meta.assignee?.profileImg || item.assignee?.profileImg || matchedTask?.assignee?.profileImg || (this.salesUsers?.find((u: any) => u.userName === assigneeName || u.name === assigneeName)?.profileImg) || '';
        const assignedByName = meta.assignedBy?.userName || meta.assignedBy?.name || item.assignedBy?.userName || matchedTask?.assignedBy?.userName || 'Sales';
        const assignedByProfileImg = meta.assignedBy?.profileImg || item.assignedBy?.profileImg || matchedTask?.assignedBy?.profileImg || (this.salesUsers?.find((u: any) => u.userName === assignedByName || u.name === assignedByName)?.profileImg) || '';

        if (eventName.includes('completed')) {
          return {
            ...item,
            isFollowUpTaskEvent: true,
            id: item._id,
            seqNo: seqNo,
            taskId: taskId,
            logType: 'completed',
            timestamp: timestamp,
            createdAt: item.createdAt || timestamp,
            updatedAt: item.updatedAt || timestamp,
            assigneeName: assigneeName,
            assigneeProfileImg: assigneeProfileImg,
            note: meta.note || item.note || ''
          };
        } else if (eventName.includes('updated')) {
          return {
            ...item,
            isFollowUpTaskEvent: true,
            id: item._id,
            seqNo: seqNo,
            taskId: taskId,
            logType: 'updated',
            timestamp: timestamp,
            createdAt: item.createdAt || timestamp,
            updatedAt: item.updatedAt || timestamp,
            assigneeName: assigneeName,
            assigneeProfileImg: assigneeProfileImg,
            note: meta.note || item.note || ''
          };
        } else {
          return {
            ...item,
            isFollowUpTaskEvent: true,
            id: item._id,
            seqNo: seqNo,
            taskId: taskId,
            logType: 'created',
            timestamp: timestamp,
            createdAt: item.createdAt || timestamp,
            updatedAt: item.updatedAt || timestamp,
            assignedByName: assignedByName,
            assignedByProfileImg: assignedByProfileImg,
            assigneeName: assigneeName,
            assigneeProfileImg: assigneeProfileImg,
            note: meta.taskDescription || item.taskDescription || meta.note || item.note || ''
          };
        }
      }

      // 2. Lead Reassigned Event
      if (
        eventName === DISPOSITION_EVENT.LEAD_REASSIGNED.toLowerCase() ||
        eventName === 'lead reassigned' ||
        eventName === 'lead_reassigned'
      ) {
        const reassignedByName = meta.reassignedBy?.userName || meta.reassignedBy?.name || item.reassignedBy?.userName || item.userName || 'System';
        const reassignedByProfileImg = meta.reassignedBy?.profileImg || item.reassignedBy?.profileImg || (this.salesUsers?.find((u: any) => u.userName === reassignedByName || u.name === reassignedByName)?.profileImg) || '';
        const reassignedToName = meta.reassignedTo?.userName || meta.reassignedTo?.name || item.reassignedTo?.userName || item.followUpAssignedTo?.userName || '-';
        const reassignedToProfileImg = meta.reassignedTo?.profileImg || item.reassignedTo?.profileImg || (this.salesUsers?.find((u: any) => u.userName === reassignedToName || u.name === reassignedToName)?.profileImg) || '';
        const previousAssigneeName = meta.previousAssignee?.userName || meta.previousAssignee?.name || item.previousAssignee?.userName || '';
        const previousAssigneeProfileImg = meta.previousAssignee?.profileImg || item.previousAssignee?.profileImg || (this.salesUsers?.find((u: any) => u.userName === previousAssigneeName || u.name === previousAssigneeName)?.profileImg) || '';

        return {
          ...item,
          isLeadReassignedEvent: true,
          id: item._id,
          seqNo: seqNo,
          timestamp: timestamp,
          createdAt: item.createdAt || timestamp,
          updatedAt: item.updatedAt || timestamp,
          reassignedByName: reassignedByName,
          reassignedByProfileImg: reassignedByProfileImg,
          reassignedToName: reassignedToName,
          reassignedToProfileImg: reassignedToProfileImg,
          previousAssigneeName: previousAssigneeName,
          previousAssigneeProfileImg: previousAssigneeProfileImg,
          note: ''
        };
      }

      // 3. Lead Follow Up Assigned Event
      if (
        eventName === DISPOSITION_EVENT.LEAD_FOLLOW_UP_ASSIGNED.toLowerCase() ||
        eventName === 'lead follow up assigned' ||
        eventName === 'lead follow-up assigned' ||
        eventName === 'lead followup assigned'
      ) {
        const assignedByName = meta.assignedBy?.userName || meta.assignedBy?.name || item.assignedBy?.userName || item.userName || 'System';
        const assignedByProfileImg = meta.assignedBy?.profileImg || item.assignedBy?.profileImg || (this.salesUsers?.find((u: any) => u.userName === assignedByName || u.name === assignedByName)?.profileImg) || '';
        const assignedToName = meta.assignedTo?.userName || meta.assignedTo?.name || item.assignedTo?.userName || meta.assignee?.userName || item.userName || '-';
        const assignedToProfileImg = meta.assignedTo?.profileImg || item.assignedTo?.profileImg || (this.salesUsers?.find((u: any) => u.userName === assignedToName || u.name === assignedToName)?.profileImg) || '';

        return {
          ...item,
          isLeadFollowUpAssignedEvent: true,
          id: item._id,
          seqNo: seqNo,
          timestamp: timestamp,
          createdAt: item.createdAt || timestamp,
          updatedAt: item.updatedAt || timestamp,
          assignedByName: assignedByName,
          assignedByProfileImg: assignedByProfileImg,
          assignedToName: assignedToName,
          assignedToProfileImg: assignedToProfileImg,
          note: meta.note || item.note || ''
        };
      }

      // 4. Lead Assigned Event
      if (
        eventName === DISPOSITION_EVENT.LEAD_ASSIGNED.toLowerCase() ||
        eventName === 'lead assigned' ||
        eventName === 'lead_assigned'
      ) {
        const assignedByName = meta.assignedBy?.userName || meta.assignedBy?.name || item.assignedBy?.userName || item.userName || 'System';
        const assignedByProfileImg = meta.assignedBy?.profileImg || item.assignedBy?.profileImg || (this.salesUsers?.find((u: any) => u.userName === assignedByName || u.name === assignedByName)?.profileImg) || '';
        const assignedToName = meta.assignedTo?.userName || meta.assignedTo?.name || item.assignedTo?.userName || meta.assignee?.userName || item.userName || '-';
        const assignedToProfileImg = meta.assignedTo?.profileImg || item.assignedTo?.profileImg || (this.salesUsers?.find((u: any) => u.userName === assignedToName || u.name === assignedToName)?.profileImg) || '';

        return {
          ...item,
          isLeadAssignedEvent: true,
          id: item._id,
          seqNo: seqNo,
          timestamp: timestamp,
          createdAt: item.createdAt || timestamp,
          updatedAt: item.updatedAt || timestamp,
          assignedByName: assignedByName,
          assignedByProfileImg: assignedByProfileImg,
          assignedToName: assignedToName,
          assignedToProfileImg: assignedToProfileImg,
          note: ''
        };
      }

      // 5. Closing Pool Update Event
      if (
        eventName === DISPOSITION_EVENT.CLOSING_POOL_UPDATE.toLowerCase() ||
        eventName === 'closing pool update' ||
        eventName === 'closing_pool_update' ||
        item.hasClosingPotential !== undefined ||
        meta.hasClosingPotential !== undefined
      ) {
        return {
          ...item,
          isClosingPoolEvent: true,
          id: item._id,
          seqNo: seqNo,
          timestamp: timestamp,
          createdAt: item.createdAt || timestamp,
          updatedAt: item.updatedAt || timestamp,
          hasClosingPotential: item.hasClosingPotential !== undefined ? item.hasClosingPotential : meta.hasClosingPotential
        };
      }

      // 6. Status Update Event
      if (
        eventName === DISPOSITION_EVENT.STATUS_UPDATE.toLowerCase() ||
        eventName === 'status update' ||
        eventName === 'status_update' ||
        (item.type === 'System' && item.status && item.prevStatus && item.prevStatus !== item.status)
      ) {
        return {
          ...item,
          isStatusUpdateEvent: true,
          id: item._id,
          seqNo: seqNo,
          timestamp: timestamp,
          createdAt: item.createdAt || timestamp,
          updatedAt: item.updatedAt || timestamp,
          prevStatus: item.prevStatus || meta.prevStatus || '-',
          status: item.status || meta.status || '-'
        };
      }

      // 7. User Note / General Disposition
      return {
        ...item,
        isUserNote: true,
        id: item._id,
        seqNo: seqNo,
        timestamp: timestamp,
        createdAt: item.createdAt || timestamp,
        updatedAt: item.updatedAt || timestamp,
        userName: item.userName || meta.userName || '-',
        status: item.status || meta.status || '',
        note: item.note || meta.note || ''
      };
    });

    return mapped.sort((a: any, b: any) => {
      const timeA = new Date(a.createdAt || a.timestamp || a.updatedAt || 0).getTime() || 0;
      const timeB = new Date(b.createdAt || b.timestamp || b.updatedAt || 0).getTime() || 0;
      return timeB - timeA;
    });
  }

  updateAssistantTaskNote() {
    const task = this.activeFollowUpTask;
    if (!task || !task._id) return;

    if (!this.assistantTaskNote?.trim()) return;

    this.isUpdatingTaskNote = true;
    const targetLead = this.selectedLead || this.selectedLeadForDetails || this.singleLead;
    const leadObj = targetLead || {};
    const targetProject = task?.project || this.getActualLeadProject(leadObj) || this.project;
    const redirectUrl = this.getKanbanRedirectUrlByProject(targetProject);

    const body: any = {
      note: this.assistantTaskNote.trim(),
      redirectUrl: redirectUrl
    };

    this.crmService.updateFollowUpTask(task._id, body).subscribe({
      next: (res: any) => {
        this.isUpdatingTaskNote = false;
        const taskId = String(task._id || task.id || '');
        if (taskId) {
          this.updatedTaskIds.add(taskId);
        }
        task.note = this.assistantTaskNote.trim();
        this.assistantTaskNote = '';
        alertModalWithoutConfirm('success', 'Follow-up task updated successfully');
        const targetLeadId = this.selectedLead?._id || this.selectedLeadForDetails?._id || this.singleLead?._id;
        if (targetLeadId) {
          this.getSingleLeadById(targetLeadId);
        }
      },
      error: (err: any) => {
        this.isUpdatingTaskNote = false;
        console.error('Error updating follow up task note:', err);
        alertModalWithoutConfirm('error', 'Failed to update follow-up task note');
      }
    });
  }

  markAssistantTaskDone() {
    const task = this.activeFollowUpTask;
    if (!task || !task._id) return;
    if (!this.assistantTaskNote?.trim()) return;
    if (!this.assistantTaskReminderDueAt) {
      alertModalWithoutConfirm('warning', 'Please select a reminder date before marking the task as Done');
      return;
    }

    this.isCompletingTask = true;
    const targetLead = this.selectedLead || this.selectedLeadForDetails || this.singleLead;
    const leadObj = targetLead || {};
    const targetProject = task?.project || this.getActualLeadProject(leadObj) || this.project;
    const redirectUrl = this.getKanbanRedirectUrlByProject(targetProject);
    const isOutbound = this.isOutboundLead(leadObj);
    const rawTab = this.getLeadProjectTab ? this.getLeadProjectTab(leadObj) : (leadObj?.projectTab || leadObj?.source || leadObj?.sourceLead);
    let projectTab = rawTab;
    const upperProj = (targetProject || '').toUpperCase();
    if (upperProj === 'IIC-LAB' || upperProj === 'IIC' || upperProj === 'V-ESTATE') {
      projectTab = rawTab || 'IVR';
    }

    const taskBody: any = {
      isCompleted: true,
      note: this.assistantTaskNote ? this.assistantTaskNote.trim() : '',
      redirectUrl: redirectUrl
    };

    const reminderPayload: any = {
      targetStatus: leadObj?.status || this.selectedOptionStatus || this.selectedLeadStatus || 'Follow-up Task',
      leadId: leadObj?._id,
      dueAt: new Date(this.assistantTaskReminderDueAt).toISOString()
    };

    if (isOutbound) {
      reminderPayload.leadType = 'Outbound';
    } else {
      reminderPayload.project = targetProject;
      if (projectTab) {
        reminderPayload.projectTab = projectTab;
      }
      if (leadObj?.leadType) {
        reminderPayload.leadType = leadObj.leadType;
      } else if (leadObj?.sourceLead || leadObj?.source) {
        reminderPayload.leadType = leadObj.sourceLead || leadObj.source;
      } else {
        reminderPayload.leadType = 'Inbound';
      }
    }

    forkJoin({
      taskUpdate: this.crmService.updateFollowUpTask(task._id, taskBody),
      reminder: this.crmService.createFollowUpReminder(reminderPayload)
    }).subscribe({
      next: (res: any) => {
        this.isCompletingTask = false;
        task.isCompleted = true;
        if (Array.isArray(this.followUpTasks)) {
          const matched = this.followUpTasks.find((t: any) => (t._id || t.id) === (task._id || task.id));
          if (matched) {
            matched.isCompleted = true;
            matched.note = taskBody.note;
          }
        }
        this.assistantTaskNote = '';
        this.assistantTaskReminderDueAt = '';
        alertModalWithoutConfirm('success', 'Follow-up task marked as done');
        const targetLeadId = this.selectedLead?._id || this.selectedLeadForDetails?._id || this.singleLead?._id;
        if (targetLeadId) {
          this.getSingleLeadById(targetLeadId);
          this.updateRemindersHasNextTaskScheduled(targetLeadId);
        }
        this.fetchFollowUpReminders();
        this.fetchAllReminderCounts();
        if (this.showCanvasUI) {
          this.loadAllColumns();
        }
      },
      error: (err: any) => {
        this.isCompletingTask = false;
        console.error('Error completing follow up task:', err);
        alertModalWithoutConfirm('error', 'Failed to mark follow-up task as Done');
      }
    });
  }



  projectIcons: { [key: string]: string } = {
    'V-RETAIL': '../../assets/vretail-icon-9.png',
    'V-ESTATE': '../../assets/vestate-icon.png',
    'IIC-LAB': '../../assets/iic-leads-icon.png'
  };

  get canvasTitle(): string {
    const proj = (this.project || '').toUpperCase();
    if (proj === 'IIC' || proj === 'IIC-LAB') {
      return 'IIC Leads';
    } else if (proj === 'V-RETAIL') {
      return 'V-Retail Leads';
    } else if (proj === 'V-ESTATE') {
      return 'V-Estate Leads';
    } else if (proj === 'OUTBOUND') {
      return 'Outbound Leads';
    }
    return 'All Leads';
  }

  followUpLeadsCanvas: any[] = [];
  canvasTabs = [
    { id: 0, label: "Follow-up Task", icon: "/assets/leads/follow-up-task.svg", status: 'Follow-up Task', isFollowUpTask: true },
    { id: 1, label: "Not started", icon: "/assets/leads/assigned-lead-icon.png", status: 'Not started', notStarted: true },
    { id: 2, label: "Contacted", icon: "/assets/leads/circle-phone.png", status: 'Contacted', isProgress: true },
    { id: 3, label: "Email introduction", icon: "/assets/leads/email-tab.png", status: 'Email introduction', isProgress: true },
    { id: 4, label: "Meetings in Video call", icon: "/assets/leads/video-meeting.png", status: 'Meetings in Video call', isProgress: true },
    { id: 5, label: "Meetings in person", icon: "/assets/leads/meeting-icon.png", status: 'Meetings in person', isProgress: true },
    { id: 6, label: "Presentation", icon: "/assets/leads/presentation-icon.png", status: 'Presentation', isProgress: true },
    { id: 7, label: "Cost sheet", icon: "/assets/leads/cost-sheet-icon.png", status: 'Cost sheet', isProgress: true },
    { id: 8, label: "Closing Pool", icon: "/assets/leads/unicorn.svg", status: 'Closing Pool', isClosingPool: true },
    { id: 9, label: "Closed", icon: "/assets/leads/closed.png", status: 'Closed', isSuccess: true },
    { id: 10, label: "Lost", icon: "/assets/leads/lost-icon.png", status: 'Lost', isDanger: true },
    { id: 11, label: "Unqualified", icon: "/assets/leads/unqualified.png", status: 'Unqualified', isUnqualified: true }
  ];

  neverEnterPredicate = () => false;






  isUpdatingClosingPool = false;
  toggleClosingPool(leadParam?: any) {
    if (this.isUpdatingClosingPool) return;
    const targetLead = leadParam || this.selectedLead || this.selectedLeadForDetails || this.singleLead;
    if (!targetLead || !targetLead._id) return;

    const currentVal = !!(targetLead.hasClosingPotential === true || targetLead.hasClosingPotential === 'true' || targetLead.status === 'Closing Pool' || targetLead.stageStatus === 'Closing Pool');
    const newVal = !currentVal;

    this.isUpdatingClosingPool = true;

    const leadObj = targetLead || this.singleLead || this.selectedLeadForDetails || this.selectedLead;
    const isOutboundLead =
      this.project === 'OUTBOUND' ||
      (leadObj && (
        String(leadObj.leadType).toLowerCase() === 'outbound' ||
        String(leadObj.project).toLowerCase() === 'outbound' ||
        String(leadObj.source).toLowerCase() === 'outbound' ||
        String(leadObj.sourceLead).toLowerCase() === 'outbound' ||
        String(leadObj.projectTab).toLowerCase() === 'outbound'
      ));

    const targetProject = this.getActualLeadProject(leadObj);
    const redirectUrl = this.getKanbanRedirectUrlByProject(targetProject);

    const payload: any = {
      leadId: targetLead._id,
      hasClosingPotential: newVal,
      redirectUrl: redirectUrl
    };

    if (isOutboundLead) {
      payload.leadType = 'Outbound';
    } else {
      if (targetProject && targetProject !== 'ALL') {
        payload.project = targetProject;
      }
      const rawTab = leadObj?.source || leadObj?.sourceLead || leadObj?.projectTab;
      const resolvedProjectTab = this.getLeadProjectTab(leadObj);
      const projectTab = rawTab || resolvedProjectTab || (targetProject === 'IIC-LAB' || targetProject === 'V-ESTATE' || targetProject === 'IIC' ? 'IVR' : undefined);
      if (projectTab) {
        payload.projectTab = projectTab;
      }
    }

    this.crmService.updateSalesLead(payload).subscribe({
      next: () => {
        this.isUpdatingClosingPool = false;
        targetLead.hasClosingPotential = newVal;
        if (this.selectedLead) this.selectedLead.hasClosingPotential = newVal;
        if (this.selectedLeadForDetails) this.selectedLeadForDetails.hasClosingPotential = newVal;
        if (this.singleLead) this.singleLead.hasClosingPotential = newVal;

        // Update Closing Pool column immediately
        const closingPoolCol = this.emptyColumns[6];
        if (closingPoolCol) {
          if (newVal) {
            const exists = closingPoolCol.items.some((item: any) => item._id === targetLead._id);
            if (!exists) {
              closingPoolCol.items = [targetLead, ...closingPoolCol.items];
              this.totalStatusCount['Closing Pool'] = (this.totalStatusCount['Closing Pool'] || 0) + 1;
            }
          } else {
            const prevLen = closingPoolCol.items.length;
            closingPoolCol.items = closingPoolCol.items.filter((item: any) => item._id !== targetLead._id);
            if (closingPoolCol.items.length < prevLen) {
              this.totalStatusCount['Closing Pool'] = Math.max(0, (this.totalStatusCount['Closing Pool'] || 0) - 1);
            }
          }
        }

        this.syncLeadInAllColumns({ ...targetLead, hasClosingPotential: newVal });
        this.getAllLeadsBreakdown();

        const newDisposition: Disposition = {
          _id: 'temp_' + Date.now(),
          leadId: targetLead._id,
          type: 'System',
          event: 'Closing Pool Update',
          hasClosingPotential: newVal,
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString()
        };
        this.dispositions = [newDisposition, ...this.dispositions];

        alertModalWithoutConfirm('success', newVal ? 'Added to Closing Pool!' : 'Removed from Closing Pool!');
      },
      error: (err: any) => {
        this.isUpdatingClosingPool = false;
        console.error('Error updating closing pool status:', err);
        alertModalWithoutConfirm('error', 'Failed to update Closing Pool');
      }
    });
  }

  syncLeadInAllColumns(updatedLead: any) {
    if (!updatedLead) return;
    const leadId = String(updatedLead._id || updatedLead.id || updatedLead.leadId || '');
    if (!leadId) return;

    const matchLead = (l: any) => {
      if (!l) return false;
      const id = String(l._id || l.id || l.leadId || '');
      return id === leadId;
    };

    if (this.assignedLeadsCanvas) {
      this.assignedLeadsCanvas = this.assignedLeadsCanvas.map((l: any) =>
        matchLead(l) ? { ...l, ...updatedLead } : l
      );
    }
    if (this.followUpLeadsCanvas) {
      this.followUpLeadsCanvas = this.followUpLeadsCanvas.map((l: any) =>
        matchLead(l) ? { ...l, ...updatedLead } : l
      );
    }
    if (this.emptyColumns) {
      this.emptyColumns = this.emptyColumns.map((col: any) => ({
        ...col,
        items: (col.items || []).map((item: any) =>
          matchLead(item) ? { ...item, ...updatedLead } : item
        )
      }));
    }
    if (this.leads) {
      this.leads = this.leads.map((l: any) =>
        matchLead(l) ? { ...l, ...updatedLead } : l
      );
    }
    if (this.singleLead && matchLead(this.singleLead)) {
      this.singleLead = { ...this.singleLead, ...updatedLead };
    }
    if (this.selectedLead && matchLead(this.selectedLead)) {
      this.selectedLead = { ...this.selectedLead, ...updatedLead };
    }
    if (this.selectedLeadForDetails && matchLead(this.selectedLeadForDetails)) {
      this.selectedLeadForDetails = { ...this.selectedLeadForDetails, ...updatedLead };
    }
    this.changeDetector.markForCheck();
  }

  openFullscreen(block: FileBlock) {
    let type: 'image' | 'video' | 'document' = 'document';

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(block.extension || '')) {
      type = 'image';
    } else if (['mp4', 'mov', 'webm', 'mkv'].includes(block.extension || '')) {
      type = 'video';
    }

    this.fullscreenPreview = {
      url: block.url!,
      type,
      fileName: block.fileName
    };
  }

  closeFullscreen() {
    this.fullscreenPreview = null;
  }

  downloadFile(preview: any) {
    const link = document.createElement('a');
    link.href = preview.url;
    link.download = preview.fileName;
    link.click();
  }
  getPlaceholderForBlock(block: any, index: number) {
    const isLast = index === this.editorBlocks.length - 1;
    const isEmpty = !block.value || block.value.trim() === "";
    return isLast && isEmpty ? "Ask or find anything from your workspace…" : "";
  }
  openLeadDetails(lead) {
    this.selectedLeadForDetails = lead;
  }
  activeDetailsLeadId: string | null = null;
  isLoadingLeadDetails: boolean = false;

  closeDetailsPanel() {
    this.activeDetailsLeadId = null;
    this.isLoadingLeadDetails = false;
    const panel = document.querySelector('.details-panel');
    const backdrop = document.querySelector('.details-panel-backdrop');

    if (panel) panel.classList.remove('slide-in');
    if (panel) panel.classList.add('slide-out');

    if (backdrop) backdrop.classList.remove('fade-in');
    if (backdrop) backdrop.classList.add('fade-out');

    setTimeout(() => {
      this.showDetailsPanel = false;
    }, 300);
  }

  selectLeadInCanvas(lead: any) {
    if (!lead) return;
    console.log("SELECTED LEAD:", lead._id);
    this.selectedLeadForDetails = lead;
    this.selectedLead = lead;
    console.log("ACTIVE LEAD ID:", lead._id);

    this.editorBlocks = [];
    if (this.activeDetailsLeadId === String(lead._id || lead.id)) {
      return;
    }
    if (!this.showDetailsPanel || this.singleLead?._id !== lead._id) {
      this.getSingleLeadById(lead._id, lead.project, lead);
    }
  }

  goBackToLeads() {
    this.showCanvasUI = false;
    this.isCanvasLoading = false;
    this.selectedLeadForDetails = null;
    this.showDetailsPanel = false;
    this.startDate = null;
    this.endDate = null;
    this.selectedDateRange = null;
    this.getAdminOverView();
    this.getAllLeadsBreakdown();
    this.getLeadsListingDetail();

    const moduleRoute = this.getModuleRouteByProject(this.project);
    this.router.navigate([`/lead-track/${moduleRoute}`], { queryParams: {} });
  }

  activeTabIndex = 0;
  scrollToColumn(index: number) {
    const container = document.querySelector('.canvas-scroll-container') as HTMLElement;
    if (!container) return;
    const isMobile = window.innerWidth <= 768;
    const columnWidth = isMobile ? (300 + 12) : 432;
    container.scrollTo({ left: index * columnWidth, behavior: 'smooth' });
    this.activeTabIndex = index;
  }

  onCanvasScroll(event: any) {
    const scrollLeft = event.target.scrollLeft;
    const isMobile = window.innerWidth <= 768;
    const columnWidth = isMobile ? (300 + 12) : 432;
    this.activeTabIndex = Math.round(scrollLeft / columnWidth);
  }

  getLatestFollowUpForLead(leadId: string, lead?: any) {
    const leadObj = lead || this.singleLead || this.selectedLeadForDetails || (this.assignedLeadsCanvas && this.assignedLeadsCanvas.find((l: any) => l._id === leadId)) || (this.leads && this.leads.find(l => l._id === leadId));
    const targetProject = this.getActualLeadProject(leadObj);
    const isOutboundLead = this.isOutboundLead(leadObj);

    const body: any = {
      leadId,
      status: 'Incomplete'
    };

    if (isOutboundLead) {
      body.leadType = 'Outbound';
    } else {
      body.project = targetProject;
      const rawTab = leadObj?.source || leadObj?.sourceLead || leadObj?.projectTab;
      const projectTab = rawTab || ((targetProject === 'IIC-LAB' || targetProject === 'V-ESTATE' || targetProject === 'IIC') ? 'IVR' : undefined);
      if (projectTab) {
        body.projectTab = projectTab;
      }
    }

    this.crmService.getFollowUp(body).subscribe({
      next: (res: any) => {
        if (this.activeDetailsLeadId && this.activeDetailsLeadId !== String(leadId)) {
          return;
        }
        const followUps = res?.data?.followUpMeetingsData || [];
        if (!followUps.length) {
          if (this.followUpForm) this.followUpForm.reset();
          return;
        }
        const leadFollowUps = followUps.filter((f: any) => f.leadId === leadId);
        if (!leadFollowUps.length) {
          if (this.followUpForm) this.followUpForm.reset();
          return;
        }
        const latest = leadFollowUps.sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
        if (latest && this.followUpForm) {
          this.followUpForm.patchValue({
            date: latest.date ? latest.date.substring(0, 10) : '',
            time: latest.time || '',
            type: latest.type || ''
          });
        }
      },
      error: () => { }
    });
  }

  openDetailsPanel(lead: any) {
    if (!lead) return;
    console.log("Open", lead);
    this.closeAllDropdowns();
    this.selectedLead = lead;
    this.selectedLeadForDetails = lead;
    this.showDetailsPanel = true;
    const currentId = String(lead._id || lead.id || '');
    this.activeDetailsLeadId = currentId;
    this.isLoadingLeadDetails = true;

    // Immediately populate the form from the known Kanban card data so no field is empty
    this.patchLeadForm(lead);

    this.getSingleLeadById(lead._id, lead.project, lead);
    this.getLatestFollowUpForLead(lead._id, lead);

    setTimeout(() => {
      const panel = document.querySelector('.details-panel');
      const backdrop = document.querySelector('.details-panel-backdrop');

      if (panel) {
        panel.classList.remove('pre-slide');
        panel.classList.add('slide-in');
      }

      if (backdrop) {
        backdrop.classList.remove('pre-fade');
        backdrop.classList.add('fade-in');
      }
    });
  }

  selectedLeadStatus: string = "";
  selectedAssigneeName = "";
  getSingleLeadById(id: string | number, targetProject?: string | any, leadParam?: any) {
    const leadObj = leadParam || (typeof targetProject === 'object' ? targetProject : null) || this.selectedLead || this.selectedLeadForDetails || (this.leads && this.leads.find(l => l._id === id)) || (this.assignedLeadsCanvas && this.assignedLeadsCanvas.find((l: any) => l._id === id));

    const rawProj = (typeof targetProject === 'string' ? targetProject : leadObj?.project || (this.selectedLeadForDetails?.project) || (this.selectedLead?.project) || this.project || '').toUpperCase();

    const isOutboundLead =
      this.project === 'OUTBOUND' ||
      rawProj === 'OUTBOUND' ||
      (leadObj && (
        String(leadObj.leadType).toLowerCase() === 'outbound' ||
        String(leadObj.project).toLowerCase() === 'outbound' ||
        String(leadObj.source).toLowerCase() === 'outbound' ||
        String(leadObj.sourceLead).toLowerCase() === 'outbound' ||
        String(leadObj.projectTab).toLowerCase() === 'outbound'
      ));

    const resolvedProject = this.getActualLeadProject(leadObj);
    let project = isOutboundLead ? 'OUTBOUND' : (typeof targetProject === 'string' && targetProject !== 'ALL' ? targetProject : (resolvedProject || leadObj?.project || this.project));
    if (project === 'ALL') {
      project = resolvedProject || '';
    }
    const rawTab = this.getLeadProjectTab ? this.getLeadProjectTab(leadObj) : undefined;
    const uppercaseProject = (project || '').toUpperCase();
    let tabToPass = isOutboundLead ? undefined : rawTab;
    if (!isOutboundLead && (uppercaseProject === 'IIC-LAB' || uppercaseProject === 'IIC' || uppercaseProject === 'V-ESTATE')) {
      tabToPass = rawTab || 'IVR';
    }

    const requestLeadId = String(id);
    this.crmService.getSingleLead(id, isOutboundLead ? undefined : project, tabToPass, isOutboundLead ? 'Outbound' : undefined).subscribe({
      next: (res: any) => {
        if (this.activeDetailsLeadId && this.activeDetailsLeadId !== requestLeadId) {
          console.log(`[getSingleLeadById] Discarding response for ${requestLeadId} because active is ${this.activeDetailsLeadId}`);
          return;
        }
        this.isLoadingLeadDetails = false;
        const apiLead = res?.data;
        if (!apiLead) return;

        const apiHasClosing = apiLead.hasClosingPotential !== undefined
          ? (apiLead.hasClosingPotential === true || apiLead.hasClosingPotential === 'true')
          : (leadObj?.hasClosingPotential === true || leadObj?.hasClosingPotential === 'true');

        const mergedLead = {
          ...leadObj,
          ...apiLead,
          hasClosingPotential: apiHasClosing,
          contactName: apiLead.contactName || apiLead.leadName || apiLead.name || leadObj?.contactName || leadObj?.leadName || leadObj?.name || '',
          name: apiLead.name || apiLead.contactName || apiLead.leadName || leadObj?.name || leadObj?.contactName || leadObj?.leadName || '',
          leadName: apiLead.leadName || apiLead.contactName || apiLead.name || leadObj?.leadName || leadObj?.contactName || leadObj?.leadName || '',
          companyName: apiLead.companyName || leadObj?.companyName || '',
          phoneNo: apiLead.phoneNo || apiLead.callerId || apiLead.phone || apiLead.phoneNumber || apiLead.mobile || apiLead.mobileNo || leadObj?.phoneNo || leadObj?.callerId || leadObj?.phone || leadObj?.phoneNumber || leadObj?.mobile || '',
          email: apiLead.email || (Array.isArray(apiLead.emails) && apiLead.emails[0]) || leadObj?.email || (Array.isArray(leadObj?.emails) && leadObj?.emails[0]) || '',
          designation: apiLead.designation || leadObj?.designation || '',
          status: apiLead.status || leadObj?.status || '',
          customerValue: apiLead.customerValue || leadObj?.customerValue || leadObj?.badge || '',
          dispositionStatus: apiLead.dispositionStatus || leadObj?.dispositionStatus || [],
          followUpTasks: apiLead.followUpTasks || leadObj?.followUpTasks || [],
          media: apiLead.media || leadObj?.media || [],
          additionalNotes: apiLead.additionalNotes || leadObj?.additionalNotes || ''
        };

        this.singleLead = mergedLead;
        this.selectedLead = mergedLead;
        this.selectedLeadForDetails = mergedLead;
        this.dispositions = mergedLead.dispositionStatus || [];
        this.followUpTasks = mergedLead.followUpTasks || [];

        this.patchLeadForm(mergedLead);
        this.selectedLeadStatus = mergedLead.status || "";
        this.showLostReason = this.selectedLeadStatus === "Lost";
        this.selectedAssigneeName = mergedLead.followUpAssignedTo?.userName || "";
        this.logoPreview = mergedLead.companyProfileImg || null;
        this.loadEditorBlocks(mergedLead.additionalNotes || "");
        this.loadMediaBlocks(mergedLead.media || []);
        const val = mergedLead.customerValue;
        this.selectedBadge = [
          this.badgeOptions.find(b => b.value === val)
        ];
        this.statusnote = mergedLead.description || "";
        this.initialBadgeValue = val || "";
        this.initialNotesValue = mergedLead.description || "";
        this.showDetailsPanel = true;
      },
      error: (err) => {
        console.error("Error fetching lead:", err);
        // If query with tabToPass failed, retry once without tabToPass
        if (tabToPass && (!this.activeDetailsLeadId || this.activeDetailsLeadId === requestLeadId)) {
          this.crmService.getSingleLead(id, isOutboundLead ? undefined : project, undefined, isOutboundLead ? 'Outbound' : undefined).subscribe({
            next: (fallbackRes: any) => {
              if (this.activeDetailsLeadId && this.activeDetailsLeadId !== requestLeadId) return;
              this.isLoadingLeadDetails = false;
              const apiLead = fallbackRes?.data;
              if (!apiLead) return;
              const fallbackHasClosing = apiLead.hasClosingPotential !== undefined
                ? (apiLead.hasClosingPotential === true || apiLead.hasClosingPotential === 'true')
                : (leadObj?.hasClosingPotential === true || leadObj?.hasClosingPotential === 'true');
              const mergedLead = {
                ...leadObj,
                ...apiLead,
                hasClosingPotential: fallbackHasClosing,
                contactName: apiLead.contactName || apiLead.leadName || apiLead.name || leadObj?.contactName || leadObj?.leadName || leadObj?.name || '',
                name: apiLead.name || apiLead.contactName || apiLead.leadName || leadObj?.name || leadObj?.contactName || leadObj?.leadName || '',
                leadName: apiLead.leadName || apiLead.contactName || apiLead.name || leadObj?.leadName || leadObj?.contactName || leadObj?.name || '',
                companyName: apiLead.companyName || leadObj?.companyName || '',
                phoneNo: apiLead.phoneNo || apiLead.callerId || apiLead.phone || apiLead.phoneNumber || apiLead.mobile || apiLead.mobileNo || leadObj?.phoneNo || leadObj?.callerId || leadObj?.phone || leadObj?.phoneNumber || leadObj?.mobile || '',
                email: apiLead.email || (Array.isArray(apiLead.emails) && apiLead.emails[0]) || leadObj?.email || (Array.isArray(leadObj?.emails) && leadObj?.emails[0]) || '',
                designation: apiLead.designation || leadObj?.designation || '',
                status: apiLead.status || leadObj?.status || '',
                customerValue: apiLead.customerValue || leadObj?.customerValue || leadObj?.badge || '',
                dispositionStatus: apiLead.dispositionStatus || leadObj?.dispositionStatus || [],
                followUpTasks: apiLead.followUpTasks || leadObj?.followUpTasks || [],
                media: apiLead.media || leadObj?.media || [],
                additionalNotes: apiLead.additionalNotes || leadObj?.additionalNotes || ''
              };
              this.singleLead = mergedLead;
              this.selectedLead = mergedLead;
              this.selectedLeadForDetails = mergedLead;
              this.dispositions = mergedLead.dispositionStatus || [];
              this.followUpTasks = mergedLead.followUpTasks || [];
              this.patchLeadForm(mergedLead);
            },
            error: (fallbackErr) => {
              console.error("Fallback fetch lead failed:", fallbackErr);
              if (this.activeDetailsLeadId && this.activeDetailsLeadId === requestLeadId) {
                this.isLoadingLeadDetails = false;
              }
            }
          });
        } else {
          if (this.activeDetailsLeadId && this.activeDetailsLeadId === requestLeadId) {
            this.isLoadingLeadDetails = false;
          }
        }
      }
    });
  }

  getLeadPhone(lead: any): string {
    if (!lead) return '-';
    const phone =
      lead.phoneNo ||
      lead.callerId ||
      lead.phone ||
      lead.phoneNumber ||
      lead.mobile ||
      lead.mobileNo ||
      lead.calledNo ||
      lead.contactNo ||
      lead.contactNumber ||
      lead.tel ||
      (Array.isArray(lead.phoneNumbers) && lead.phoneNumbers[0]) ||
      (Array.isArray(lead.customerInformation) && (lead.customerInformation[0]?.phoneNo || lead.customerInformation[0]?.phoneNumber || lead.customerInformation[0]?.phone || lead.customerInformation[0]?.mobile)) ||
      '';
    return phone ? String(phone) : '-';
  }

  patchLeadForm(lead: any) {
    if (!lead) return;

    let email = lead.email || "";
    if (!email) {
      if (Array.isArray(lead.emails) && lead.emails.length > 0 && lead.emails[0]) {
        email = lead.emails[0];
      } else if (typeof lead.emails === 'string' && lead.emails) {
        email = lead.emails;
      } else if (lead.senderEmail) {
        email = lead.senderEmail;
      } else if (lead.fromEmail) {
        email = lead.fromEmail;
      } else if (lead.from) {
        email = lead.from;
      } else if (lead.sourceEmail) {
        email = lead.sourceEmail;
      } else if (lead.mail) {
        email = lead.mail;
      } else if (lead.emailId) {
        email = lead.emailId;
      } else if (Array.isArray(lead.customerInformation) && lead.customerInformation[0]?.email) {
        email = lead.customerInformation[0].email;
      }
    }

    const rawBadge = (lead.customerValue || lead.badge || '').toLowerCase();
    const badgeObj = this.badgeOptions.find(b => b.value === rawBadge) || null;
    this.selectedBadgeDetails = badgeObj;
    this.badgeDropdownOpen = false;

    const notesVal = lead.adminNotes || lead.description || '';
    const resolvedPhone =
      lead.phoneNo ||
      lead.callerId ||
      lead.phone ||
      lead.phoneNumber ||
      lead.mobile ||
      lead.mobileNo ||
      lead.calledNo ||
      lead.contactNo ||
      lead.contactNumber ||
      lead.tel ||
      (Array.isArray(lead.phoneNumbers) && lead.phoneNumbers[0]) ||
      (Array.isArray(lead.customerInformation) && (lead.customerInformation[0]?.phoneNo || lead.customerInformation[0]?.phoneNumber || lead.customerInformation[0]?.phone || lead.customerInformation[0]?.mobile)) ||
      "";

    const resolvedName =
      lead.contactName ||
      lead.leadName ||
      lead.name ||
      lead.fullName ||
      lead.customerName ||
      lead.personName ||
      (Array.isArray(lead.customerInformation) && (lead.customerInformation[0]?.contactName || lead.customerInformation[0]?.name || lead.customerInformation[0]?.fullName)) ||
      "";

    const resolvedCompany =
      lead.companyName ||
      lead.company ||
      lead.organization ||
      lead.orgName ||
      (Array.isArray(lead.customerInformation) && (lead.customerInformation[0]?.companyName || lead.customerInformation[0]?.company)) ||
      "";

    const resolvedDesignation =
      lead.designation ||
      lead.title ||
      lead.jobTitle ||
      lead.role ||
      (Array.isArray(lead.customerInformation) && lead.customerInformation[0]?.designation) ||
      "";

    this.leadForm.patchValue({
      companyName: resolvedCompany,
      leadName: resolvedName,
      name: resolvedName,
      contactName: resolvedName,
      phoneNo: resolvedPhone,
      email: email,
      designation: resolvedDesignation,
      followUpDate: lead.followUpDate ? (typeof lead.followUpDate === 'string' ? lead.followUpDate.substring(0, 10) : new Date(lead.followUpDate).toISOString().substring(0, 10)) : "",
      followUpAssignee: lead.followUpAssignedTo?.userId || lead.followUpAssignee || "",
      status: lead.status || "",
      leadId: lead._id || lead.id,
      adminNotes: notesVal,
      customerValue: rawBadge,
      lossReason: lead.lossReason || "",
    }, { emitEvent: false });

    this.initialLeadSnapshot = this.leadForm.getRawValue();
    this.leadForm.markAsPristine();
    this.leadForm.markAsUntouched();
  }

  getLeadEmail(lead: any): string {
    if (!lead) return '-';
    if (lead.email) return lead.email;
    if (Array.isArray(lead.emails) && lead.emails.length > 0 && lead.emails[0]) {
      return lead.emails[0];
    }
    if (typeof lead.emails === 'string' && lead.emails) {
      return lead.emails;
    }
    if (lead.senderEmail) return lead.senderEmail;
    if (lead.fromEmail) return lead.fromEmail;
    if (lead.from) return lead.from;
    if (lead.sourceEmail) return lead.sourceEmail;
    if (lead.mail) return lead.mail;
    if (lead.emailId) return lead.emailId;
    return '-';
  }


  expandedTextBlocks: { [key: number]: boolean } = {};

  loadEditorBlocks(text: string) {
    this.expandedTextBlocks = {};
    this.editorBlocks = [
      {
        id: crypto.randomUUID(),
        type: "text",
        value: text
      }
    ];
  }

  expandTextBlock(index: number) {
    const textarea = this.editorTextareas?.toArray()[index]?.nativeElement;
    if (!textarea) return;
    this.expandedTextBlocks[index] = !this.expandedTextBlocks[index];
    requestAnimationFrame(() => {
      if (this.expandedTextBlocks[index]) {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
      } else {
        textarea.style.height = '80px';
      }
    });
  }

  onTextBlockInput(event: Event, index: number) {
    const textarea = event.target as HTMLTextAreaElement;

    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';

    this.editorBlocks[index].value = textarea.value;

  }



  loadMediaBlocks(media: any[]) {
    media.forEach(file => {
      const extension = file.extension.replace(".", "").toLowerCase();

      let url = "";
      let thumbnail = "";

      // IMAGE 
      if (["jpg", "jpeg", "png", "webp", "gif"].includes(extension)) {
        url = file.compressedImgUrl || file.fileUrl;
        thumbnail = url;
      }

      // VIDEO 
      else if (["mp4", "mov", "webm", "mkv"].includes(extension)) {
        url = file.fileUrl;
        thumbnail = file.thumbnailUrl;
      }


      else {
        url = file.fileUrl;
        thumbnail = "/assets/leads/document-files-icon.png";
      }

      const fileBlock: FileBlock = {
        id: crypto.randomUUID(),
        type: "file",

        fileName: file.fileName || "unknown",
        fileSize: file.size || 0,
        rawFile: null as any,
        uploading: false,
        progress: 100,
        parts: [],

        url: url,
        thumbnailUrl: thumbnail,
        extension: extension,
        uploadedAt: file.updatedAt,
        uploadId: file._id,
        mediaId: file._id
      };

      this.editorBlocks.push(fileBlock);
    });
  }



  populateFollowUpForm(followUp: any) {
    if (!this.followUpForm) return;

    const formattedDate = followUp.followUpDate
      ? new Date(followUp.followUpDate).toISOString().slice(0, 10)
      : '';

    this.followUpForm.patchValue({
      date: formattedDate,
      time: followUp.followUpTime || '',
      type: followUp.followUpType || ''
    });

    this.followUpForm.markAsPristine();
    this.followUpForm.markAsUntouched();

    console.log('Patched form value:', this.followUpForm.value);
  }


  showCreateOutboundModal = false;
  isSubmittingOutbound = false;
  outboundErrorMessage = '';

  userAlertModal = {
    show: false,
    title: 'Notice',
    message: '',
    type: 'warning'
  };

  showAlert(message: string, title: string = 'Notice', type: string = 'warning') {
    this.userAlertModal = {
      show: true,
      title,
      message,
      type
    };
  }

  closeAlertModal() {
    this.userAlertModal.show = false;
  }

  isEditOutboundMode = false;
  editingOutboundLeadId: string | null = null;

  outboundForm = {
    name: '',
    email: '',
    companyName: '',
    designation: '',
    phoneNo: '',
    source: 'Outbound',
    message: ''
  };

  isOutboundLead(lead: any): boolean {
    if (!lead) return false;
    const type = String(lead?.leadType || '').toLowerCase();
    const proj = String(lead?.project || '').toLowerCase();
    const src = String(lead?.source || lead?.sourceLead || '').toLowerCase();
    const tab = String(lead?.projectTab || '').toLowerCase();
    return (
      this.project === 'OUTBOUND' ||
      type === 'outbound' ||
      proj === 'outbound' ||
      src === 'outbound' ||
      tab === 'outbound'
    );
  }

  getLeadProjectBadge(leadObj: any): string {
    if (!leadObj) return this.project || 'ALL';
    if (this.isOutboundLead(leadObj)) {
      return 'OUTBOUND';
    }
    const proj = leadObj.project || this.project || '';
    if (!proj || proj === 'ALL') {
      return 'ALL';
    }
    return proj;
  }

  openCreateOutboundLeadModal() {
    this.outboundErrorMessage = '';
    this.isEditOutboundMode = false;
    this.editingOutboundLeadId = null;
    this.outboundForm = {
      name: '',
      email: '',
      companyName: '',
      designation: '',
      phoneNo: '',
      source: 'Outbound',
      message: ''
    };
    this.showCreateOutboundModal = true;
  }

  openEditOutboundLeadModal(lead: any) {
    if (!lead) return;
    if (!this.isLeadUnassigned(lead)) {
      alertModalWithoutConfirm("warning", "Assigned Outbound leads cannot be edited.");
      return;
    }
    this.outboundErrorMessage = '';
    this.isEditOutboundMode = true;
    this.editingOutboundLeadId = lead._id || lead.id || null;
    this.outboundForm = {
      name: lead.name || '',
      email: lead.email || '',
      companyName: lead.companyName || '',
      designation: lead.designation || '',
      phoneNo: lead.phoneNo || lead.phone || '',
      source: lead.source || lead.sourceLead || 'Outbound',
      message: lead.message || ''
    };
    this.showCreateOutboundModal = true;
  }

  closeCreateOutboundLeadModal() {
    this.showCreateOutboundModal = false;
    this.isEditOutboundMode = false;
    this.editingOutboundLeadId = null;
    this.outboundErrorMessage = '';
  }

  submitCreateOutboundLead() {
    if (!this.outboundForm.name?.trim()) {
      this.outboundErrorMessage = 'Please enter lead name';
      return;
    }
    this.outboundErrorMessage = '';
    this.isSubmittingOutbound = true;
    const payload = {
      name: this.outboundForm.name.trim(),
      email: this.outboundForm.email?.trim() || '',
      companyName: this.outboundForm.companyName?.trim() || '',
      designation: this.outboundForm.designation?.trim() || '',
      phoneNo: this.outboundForm.phoneNo?.trim() || '',
      source: 'Outbound',
      message: this.outboundForm.message?.trim() || ''
    };

    if (this.isEditOutboundMode && this.editingOutboundLeadId) {
      this.crmService.updateOutboundLead(this.editingOutboundLeadId, payload).subscribe({
        next: (res: any) => {
          this.isSubmittingOutbound = false;
          this.closeCreateOutboundLeadModal();
          this.getLeadsListingDetail();
        },
        error: (err: any) => {
          this.isSubmittingOutbound = false;
          console.error('Error updating outbound lead:', err);
          this.outboundErrorMessage = err?.error?.message || 'Failed to update outbound lead. Please try again.';
        }
      });
    } else {
      this.crmService.createOutboundLead(payload).subscribe({
        next: (res: any) => {
          this.isSubmittingOutbound = false;
          this.closeCreateOutboundLeadModal();
          this.getLeadsListingDetail();
        },
        error: (err: any) => {
          this.isSubmittingOutbound = false;
          console.error('Error creating outbound lead:', err);
          this.outboundErrorMessage = err?.error?.message || 'Failed to create outbound lead. Please try again.';
        }
      });
    }
  }
}
